/**
 * Preview-only overlay generated from MANUS_LOCAL_AEO_ACTION_STRATEGY_2026-10-28.md.
 * British English applies only to this local AEO preview. Do not hand-edit supplied facts, FAQ pairs or graphs.
 */
export type LocalAeoStrategyFaq = { q: string; a: string };
export type LocalAeoStrategySpec = {
  section: string; h1: string; title: string; description: string; answerFirst: string; factsHtml: string; faqHtml: string;
  moduleHtml: string | null; moduleBeforeHeading: string | null; dateModified: string;
  faqs: LocalAeoStrategyFaq[]; schemaGraph: Record<string, unknown>;
};

export const LOCAL_AEO_STRATEGY_SPECS: Readonly<Record<string, LocalAeoStrategySpec>> = Object.freeze({
  "/local/": {
    "section": "C-B1",
    "h1": "Local business marketing for UK independents",
    "title": "Local business marketing for UK independents",
    "description": "Local business marketing guides for UK owners: dentists, cafés, groomers, garages and salons. Google Business Profile, reviews, AI search. London-led.",
    "answerFirst": "For a UK independent business, local marketing comes down to facts customers and AI tools can check: a complete Google Business Profile, the same name, address and phone number everywhere, one clear page per service, reviews you reply to and an easy way to book. Google says local results depend mainly on relevance, distance and popularity.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Google says local results are mainly based on relevance, distance and popularity, and that complete, accurate Business Profile information helps with relevance. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n<li>Google's guidelines ask for the real-world business name, and service-area businesses that do not serve customers at their address should hide it. Source: <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Guidelines for representing your business on Google</a>; <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n<li>Google says there are no additional requirements or special optimisations to appear in AI Overviews or AI Mode; a page must be indexed and eligible to show a snippet. Source: <a href=\"https://developers.google.com/search/docs/appearance/ai-features\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Search Central: AI features and your website</a>.</li>\n<li>OpenAI says sites that allow its OAI-SearchBot crawler can appear in ChatGPT search answers, and that this is separate from GPTBot, which relates to model training. Source: <a href=\"https://developers.openai.com/api/docs/bots\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">OpenAI: Overview of OpenAI crawlers</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned under the Digital Markets, Competition and Consumers Act 2024; the CMA's guidance explains what businesses must do. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>; <a href=\"https://www.legislation.gov.uk/ukpga/2024/13/contents\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Digital Markets, Competition and Consumers Act 2024</a>.</li>\n<li>Bing Places and Apple Business let a business claim its listing for Bing, Apple Maps and Siri, separately from Google. Source: <a href=\"https://www.bing.com/forbusiness/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Bing Places for Business</a>; <a href=\"https://business.apple.com/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Apple Business (formerly Apple Business Connect)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>What are the best ways for a UK independent business to improve local marketing and appear in Google and AI search?</h3>\n<p>For a UK independent business, local marketing comes down to facts customers and AI tools can check: a complete Google Business Profile, the same name, address and phone number everywhere, one clear page per service, reviews you reply to and an easy way to book. Google says local results depend mainly on relevance, distance and popularity.</p>\n<h3>Do you have offices in the places you cover?</h3>\n<p>No. Integrated.Social is based in London and works remotely with businesses elsewhere in the UK. Our guides describe the businesses we take on, not where we have premises, and we never create a business listing for a place where we have no address.</p>\n<h3>How is AI search different from ordinary local SEO?</h3>\n<p>Ordinary local SEO aims at the map pack and the blue links. AI assistants write an answer and may name a handful of businesses, so the details they can verify about you carry more weight: clear service descriptions, matching contact details across directories and direct answers to customer questions. Most of the groundwork overlaps.</p>\n<h3>Why do regulated sectors get separate guidance?</h3>\n<p>Dental practices, solicitors and some beauty treatments have their own advertising rules, set by bodies such as the General Dental Council and the Solicitors Regulation Authority, and by legislation for sunbeds. Marketing that ignores them puts the business at risk, so each guide explains the rules and leaves sign-off with the qualified person at your business.</p>\n<h3>Once I have my score, what are the next steps?</h3>\n<p>You receive a score out of 100 for how clearly your website is set up to be found and understood by Google and AI assistants, plus the main gaps to fix first. If you want help with the fixes, you can book a 30-minute call. There is no obligation and no card needed.</p>\n<h3>My industry isn't listed. Can you still help?</h3>\n<p>Possibly. The same groundwork applies to most businesses that customers find locally: an accurate Google Business Profile, clear service pages and genuine reviews. Start with the Free AI Visibility Score for your website, then book a call if you want to talk through whether we are the right fit for your business.</p>\n<h3>What does the Local Presence Plan cost, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleHtml": "<section id=\"uk-local-visibility-evidence-standard\" aria-labelledby=\"uk-local-visibility-evidence-standard-h\">\n<h2 id=\"uk-local-visibility-evidence-standard-h\">The UK Local Visibility Evidence Standard</h2>\n<p>A local business is easier for customers and search systems to understand when its business facts, service pages, reviews, directory details and booking route agree. The standard below helps you check those public signals yourself. It does not guarantee a map-pack position, an AI mention or enquiries.</p>\n<h3>What this standard checks</h3>\n<p>Five areas of public information a customer can see before they book. Each check says what to look at and why it matters, and links the public source that sets the rule where there is one. Free to use, no sign-up.</p>\n<h3>What it cannot prove</h3>\n<p>It does not measure rankings, AI answers, paid-media results or customer satisfaction, and it is not legal, clinical or compliance advice.</p>\n\n<h3>Check 1: Can a customer verify the business facts?</h3>\n<ul>\n<li>Your business name on Google matches your signage and website, with no added keywords. Source: <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\">Google Business Profile Help: Guidelines for representing your business on Google</a>.</li>\n<li>Your address shows only if customers visit you; if you travel to them, a service area is set instead. Source: <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n<li>Opening hours, including holiday hours, match on Google, your website and the directories you use.</li>\n<li>One main phone number appears everywhere, and someone answers it during the hours you list.</li>\n<li>Your main Google category says what you are, not everything you do.</li>\n<li>When not to use this check: if you are moving or rebranding, finish the change first, then check.</li>\n</ul>\n\n<h3>Check 2: Can a customer understand the service and whether it suits them?</h3>\n<ul>\n<li>Each main service has its own page or clear section saying who it is for, and who it is not for.</li>\n<li>Prices, or the factors that set a price, are visible, and you say whether VAT is included.</li>\n<li>Booking steps, lead times and cancellation terms are written down.</li>\n<li>Any licence or registration your sector needs is named and linked to the official register or council page.</li>\n<li>Any claim about results can be backed by evidence you hold. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/03.html\" target=\"_blank\" rel=\"noopener\">CAP Code section 3: Misleading advertising</a>.</li>\n</ul>\n\n<h3>Check 3: Can a customer see independent proof and review context?</h3>\n<ul>\n<li>You ask every customer for a review in the same way, and you never reward positive reviews. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n<li>You reply to reviews, good and bad, without sharing private details. Source: <a href=\"https://support.google.com/business/answer/3474122\" target=\"_blank\" rel=\"noopener\">Google Business Profile Help: Tips to get more reviews</a>.</li>\n<li>Any rating or testimonial on your website says where it came from.</li>\n<li>You do not hide, select or edit reviews in a way that misleads. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2024/13/contents\" target=\"_blank\" rel=\"noopener\">Digital Markets, Competition and Consumers Act 2024</a>.</li>\n</ul>\n\n<h3>Check 4: Do public listings and the website agree?</h3>\n<ul>\n<li>Name, address and phone number match on Google, Bing Places, Apple Business and every directory you use. Sources: <a href=\"https://www.bing.com/forbusiness/\" target=\"_blank\" rel=\"noopener\">Bing Places for Business</a>; <a href=\"https://business.apple.com/\" target=\"_blank\" rel=\"noopener\">Apple Business (formerly Apple Business Connect)</a>.</li>\n<li>If you have more than one location, each has its own page, and each page matches its listing.</li>\n<li>Structured data on your website describes only what the page shows.</li>\n<li>Old listings with a previous address or number are corrected or closed.</li>\n</ul>\n\n<h3>Check 5: Is the route to an enquiry or booking clear?</h3>\n<ul>\n<li>The booking or website link on your Google profile opens the right page.</li>\n<li>On a phone, a customer can see how to book without scrolling past the first screen.</li>\n<li>Your enquiry form asks only what you need to reply.</li>\n<li>You say how soon you reply, and you keep to it.</li>\n<li>Your service pages are indexed and can show a snippet in search. Source: <a href=\"https://developers.google.com/search/docs/appearance/ai-features\" target=\"_blank\" rel=\"noopener\">Google Search Central: AI features and your website</a>.</li>\n</ul>\n\n<h2>Sector notes</h2>\n<h3>Dental practices</h3>\n<p>Check that your NHS status on the <a href=\"https://www.nhs.uk/service-search/find-a-dentist\" target=\"_blank\" rel=\"noopener\">NHS website's Find a dentist service</a> matches your website and Google profile, that only clinicians on a <a href=\"https://www.gdc-uk.org/registration/your-registration/specialist-lists\" target=\"_blank\" rel=\"noopener\">GDC specialist list</a> are called specialists, and that advertising follows the <a href=\"https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising\" target=\"_blank\" rel=\"noopener\">GDC's guidance on advertising</a>. See our <a href=\"/local/dentist-marketing/\">dental marketing guide</a>.</p>\n<h3>Cafés and restaurants</h3>\n<p>Check that your <a href=\"https://ratings.food.gov.uk/\" target=\"_blank\" rel=\"noopener\">food hygiene rating</a> is easy to find, that customers can see how to ask about the <a href=\"https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses\" target=\"_blank\" rel=\"noopener\">14 allergens</a>, and that outdoor seating is covered by a <a href=\"https://www.gov.uk/government/publications/pavement-licences-guidance\" target=\"_blank\" rel=\"noopener\">pavement licence</a>. More in the <a href=\"/local/cafe-marketing/#ops-card-cafe\">30-day café operations card</a>.</p>\n<h3>Dog grooming businesses</h3>\n<p>Check that you state which breeds and coats you groom, how long each groom takes, the <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\">area you cover</a>, and, for vans driving in London, the <a href=\"https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when\" target=\"_blank\" rel=\"noopener\">ULEZ</a>. More in the <a href=\"/local/dog-grooming-marketing/#ops-card-dog-grooming\">30-day dog grooming operations card</a>.</p>\n<h3>Garages and MOT workshops</h3>\n<p>Check that your MOT classes and price are stated (the <a href=\"https://www.gov.uk/getting-an-mot/mot-test-fees\" target=\"_blank\" rel=\"noopener\">maximum car MOT fee</a> is set by government), that reminders go only to customers who agreed, and that drivers know about <a href=\"https://www.gov.uk/get-stop-view-mot-reminder\" target=\"_blank\" rel=\"noopener\">free GOV.UK MOT reminders</a>. More in the <a href=\"/local/garage-marketing/#ops-card-garage\">30-day garage operations card</a>.</p>\n\n<h2>Use the public checklist</h2>\n<p>Print this standard or save it as a PDF from your browser, then work through one check a week. There is no form and no sign-up.</p>\n<p><button type=\"button\" data-event=\"evidence_standard_print\" onclick=\"window.print()\">Print the checklist</button></p>\n<p>Check the public signals on your own site. <a href=\"#score\">Check my AI visibility</a></p>\n\n<h2>What this standard does not promise</h2>\n<p>Meeting every check makes your public information more accurate and easier to verify. It does not promise a position in Google Maps, a mention or citation in an AI answer, or any number of enquiries, because those depend on many factors outside your control, including what Google calls relevance, distance and popularity (<a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\">Google Business Profile Help: Tips to improve your local ranking on Google</a>).</p>\n<p>See what the Local Presence Plan includes. <a href=\"/services/local-presence-plan/\">Local Presence Plan</a></p>\n\n<h2>Sources and revision history</h2>\n<p>Version 1.0, published [PLACEHOLDER: publish date]. Reviewed by [PLACEHOLDER: reviewer name and role]. All sources checked on 28 October 2026. Report a correction through our <a href=\"/editorial-policy/\">editorial policy</a>.</p>\n</section>",
    "moduleBeforeHeading": "What local marketing covers for a small business",
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": "https://integrated.social/local/#webpage",
          "url": "https://integrated.social/local/",
          "name": "Local business marketing for UK independents",
          "headline": "Local business marketing for UK independents",
          "inLanguage": "en-GB",
          "isPartOf": {
            "@id": "https://integrated.social/#website"
          },
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/#service"
          },
          "mainEntity": {
            "@type": "ItemList",
            "@id": "https://integrated.social/local/#pages",
            "numberOfItems": 28,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "url": "https://integrated.social/local/dentist-marketing/",
                "name": "Dental marketing for NHS, mixed and private practices"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "url": "https://integrated.social/local/teeth-whitening-marketing/",
                "name": "Teeth whitening marketing for dental practices"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "url": "https://integrated.social/local/cafe-marketing/",
                "name": "Café marketing for independent coffee shops"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "url": "https://integrated.social/local/dog-grooming-marketing/",
                "name": "Dog grooming marketing for salons and mobile groomers"
              },
              {
                "@type": "ListItem",
                "position": 5,
                "url": "https://integrated.social/local/garage-marketing/",
                "name": "Garage marketing for MOT, servicing and tyres"
              },
              {
                "@type": "ListItem",
                "position": 6,
                "url": "https://integrated.social/local/beauty-salon-marketing/",
                "name": "Beauty salon marketing for hair, nails and lashes"
              },
              {
                "@type": "ListItem",
                "position": 7,
                "url": "https://integrated.social/local/restaurant-marketing/",
                "name": "Restaurant marketing for independent restaurants"
              },
              {
                "@type": "ListItem",
                "position": 8,
                "url": "https://integrated.social/local/solicitor-marketing/",
                "name": "Solicitor marketing for high-street law firms"
              },
              {
                "@type": "ListItem",
                "position": 9,
                "url": "https://integrated.social/local/gym-marketing/",
                "name": "Gym marketing for independent gyms and studios"
              },
              {
                "@type": "ListItem",
                "position": 10,
                "url": "https://integrated.social/local/pilates-studio-marketing/",
                "name": "Pilates studio marketing for reformer and mat"
              },
              {
                "@type": "ListItem",
                "position": 11,
                "url": "https://integrated.social/local/tanning-salon-marketing/",
                "name": "Tanning salon marketing for sunbeds and spray tans"
              },
              {
                "@type": "ListItem",
                "position": 12,
                "url": "https://integrated.social/local/yoga-studio-marketing/",
                "name": "Yoga studio marketing for classes and courses"
              },
              {
                "@type": "ListItem",
                "position": 13,
                "url": "https://integrated.social/local/florist-marketing/",
                "name": "Florist marketing for independent flower shops"
              },
              {
                "@type": "ListItem",
                "position": 14,
                "url": "https://integrated.social/local/dry-cleaner-marketing/",
                "name": "Dry cleaner marketing for wedding dress care"
              },
              {
                "@type": "ListItem",
                "position": 15,
                "url": "https://integrated.social/local/dog-walker-marketing/",
                "name": "Dog walker marketing for local walking businesses"
              },
              {
                "@type": "ListItem",
                "position": 16,
                "url": "https://integrated.social/local/cat-sitting-marketing/",
                "name": "Cat sitting marketing for home-visit cat sitters"
              },
              {
                "@type": "ListItem",
                "position": 17,
                "url": "https://integrated.social/local/tutor-marketing/",
                "name": "Tutor marketing for private and small-group tutors"
              },
              {
                "@type": "ListItem",
                "position": 18,
                "url": "https://integrated.social/local/driving-instructor-marketing/",
                "name": "Driving instructor marketing for ADIs"
              },
              {
                "@type": "ListItem",
                "position": 19,
                "url": "https://integrated.social/local/window-cleaner-marketing/",
                "name": "Window cleaner marketing for local rounds"
              },
              {
                "@type": "ListItem",
                "position": 20,
                "url": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/",
                "name": "Café marketing in Richmond, Putney, Wimbledon and Lambeth"
              },
              {
                "@type": "ListItem",
                "position": 21,
                "url": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/",
                "name": "Dental practice marketing in SW London"
              },
              {
                "@type": "ListItem",
                "position": 22,
                "url": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/",
                "name": "Gym marketing in Putney, Wimbledon and Kingston"
              },
              {
                "@type": "ListItem",
                "position": 23,
                "url": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/",
                "name": "Solicitor marketing in SW London and Surrey"
              },
              {
                "@type": "ListItem",
                "position": 24,
                "url": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/",
                "name": "Pilates studio marketing in Putney, Richmond and Wimbledon"
              },
              {
                "@type": "ListItem",
                "position": 25,
                "url": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/",
                "name": "Restaurant marketing in Richmond, Kingston and Guildford"
              },
              {
                "@type": "ListItem",
                "position": 26,
                "url": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/",
                "name": "Salon marketing in SW London and Surrey"
              },
              {
                "@type": "ListItem",
                "position": 27,
                "url": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/",
                "name": "Tanning salon marketing in SW London and Surrey"
              },
              {
                "@type": "ListItem",
                "position": 28,
                "url": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/",
                "name": "MOT and garage marketing in SW London and Surrey"
              }
            ]
          },
          "dateModified": "2026-10-28",
          "description": "Local business marketing guides for UK owners: dentists, cafés, groomers, garages and salons. Google Business Profile, reviews, AI search. London-led.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Guidelines for representing your business on Google",
              "url": "https://support.google.com/business/answer/3038177"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Manage your service areas for service-area and hybrid businesses",
              "url": "https://support.google.com/business/answer/9157481"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Search Central: AI features and your website",
              "url": "https://developers.google.com/search/docs/appearance/ai-features"
            },
            {
              "@type": "CreativeWork",
              "name": "OpenAI: Overview of OpenAI crawlers",
              "url": "https://developers.openai.com/api/docs/bots"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            },
            {
              "@type": "CreativeWork",
              "name": "Digital Markets, Competition and Consumers Act 2024",
              "url": "https://www.legislation.gov.uk/ukpga/2024/13/contents"
            },
            {
              "@type": "CreativeWork",
              "name": "Bing Places for Business",
              "url": "https://www.bing.com/forbusiness/"
            },
            {
              "@type": "CreativeWork",
              "name": "Apple Business (formerly Apple Business Connect)",
              "url": "https://business.apple.com/"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 3: Misleading advertising",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/03.html"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to get more reviews",
              "url": "https://support.google.com/business/answer/3474122"
            },
            {
              "@type": "CreativeWork",
              "name": "NHS website: Find a dentist",
              "url": "https://www.nhs.uk/service-search/find-a-dentist"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Specialist lists",
              "url": "https://www.gdc-uk.org/registration/your-registration/specialist-lists"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Guidance on advertising",
              "url": "https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Food hygiene ratings search",
              "url": "https://ratings.food.gov.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Allergen guidance for food businesses",
              "url": "https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Pavement licences guidance",
              "url": "https://www.gov.uk/government/publications/pavement-licences-guidance"
            },
            {
              "@type": "CreativeWork",
              "name": "Transport for London: ULEZ, where and when",
              "url": "https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Getting an MOT, MOT costs",
              "url": "https://www.gov.uk/getting-an-mot/mot-test-fees"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Get, view or stop MOT reminders",
              "url": "https://www.gov.uk/get-stop-view-mot-reminder"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/#service",
          "name": "Local business marketing for UK independents",
          "description": "Local Presence Plan for local businesses across the UK.",
          "serviceType": "Local search, Google Business Profile and AI search marketing for UK small businesses",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent local businesses in the UK"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/",
          "offers": {
            "@id": "https://integrated.social/local/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly retainer billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What are the best ways for a UK independent business to improve local marketing and appear in Google and AI search?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For a UK independent business, local marketing comes down to facts customers and AI tools can check: a complete Google Business Profile, the same name, address and phone number everywhere, one clear page per service, reviews you reply to and an easy way to book. Google says local results depend mainly on relevance, distance and popularity."
              }
            },
            {
              "@type": "Question",
              "name": "Do you have offices in the places you cover?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Integrated.Social is based in London and works remotely with businesses elsewhere in the UK. Our guides describe the businesses we take on, not where we have premises, and we never create a business listing for a place where we have no address."
              }
            },
            {
              "@type": "Question",
              "name": "How is AI search different from ordinary local SEO?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ordinary local SEO aims at the map pack and the blue links. AI assistants write an answer and may name a handful of businesses, so the details they can verify about you carry more weight: clear service descriptions, matching contact details across directories and direct answers to customer questions. Most of the groundwork overlaps."
              }
            },
            {
              "@type": "Question",
              "name": "Why do regulated sectors get separate guidance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Dental practices, solicitors and some beauty treatments have their own advertising rules, set by bodies such as the General Dental Council and the Solicitors Regulation Authority, and by legislation for sunbeds. Marketing that ignores them puts the business at risk, so each guide explains the rules and leaves sign-off with the qualified person at your business."
              }
            },
            {
              "@type": "Question",
              "name": "Once I have my score, what are the next steps?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You receive a score out of 100 for how clearly your website is set up to be found and understood by Google and AI assistants, plus the main gaps to fix first. If you want help with the fixes, you can book a 30-minute call. There is no obligation and no card needed."
              }
            },
            {
              "@type": "Question",
              "name": "My industry isn't listed. Can you still help?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Possibly. The same groundwork applies to most businesses that customers find locally: an accurate Google Business Profile, clear service pages and genuine reviews. Start with the Free AI Visibility Score for your website, then book a call if you want to talk through whether we are the right fit for your business."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What are the best ways for a UK independent business to improve local marketing and appear in Google and AI search?",
        "a": "For a UK independent business, local marketing comes down to facts customers and AI tools can check: a complete Google Business Profile, the same name, address and phone number everywhere, one clear page per service, reviews you reply to and an easy way to book. Google says local results depend mainly on relevance, distance and popularity."
      },
      {
        "q": "Do you have offices in the places you cover?",
        "a": "No. Integrated.Social is based in London and works remotely with businesses elsewhere in the UK. Our guides describe the businesses we take on, not where we have premises, and we never create a business listing for a place where we have no address."
      },
      {
        "q": "How is AI search different from ordinary local SEO?",
        "a": "Ordinary local SEO aims at the map pack and the blue links. AI assistants write an answer and may name a handful of businesses, so the details they can verify about you carry more weight: clear service descriptions, matching contact details across directories and direct answers to customer questions. Most of the groundwork overlaps."
      },
      {
        "q": "Why do regulated sectors get separate guidance?",
        "a": "Dental practices, solicitors and some beauty treatments have their own advertising rules, set by bodies such as the General Dental Council and the Solicitors Regulation Authority, and by legislation for sunbeds. Marketing that ignores them puts the business at risk, so each guide explains the rules and leaves sign-off with the qualified person at your business."
      },
      {
        "q": "Once I have my score, what are the next steps?",
        "a": "You receive a score out of 100 for how clearly your website is set up to be found and understood by Google and AI assistants, plus the main gaps to fix first. If you want help with the fixes, you can book a 30-minute call. There is no obligation and no card needed."
      },
      {
        "q": "My industry isn't listed. Can you still help?",
        "a": "Possibly. The same groundwork applies to most businesses that customers find locally: an accurate Google Business Profile, clear service pages and genuine reviews. Start with the Free AI Visibility Score for your website, then book a call if you want to talk through whether we are the right fit for your business."
      },
      {
        "q": "What does the Local Presence Plan cost, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/beauty-salon-marketing/": {
    "section": "C-C1",
    "h1": "Beauty salon marketing for hair, nails and lashes",
    "title": "Beauty salon marketing for hair, nails and lashes",
    "description": "Beauty salon marketing for UK hair, nail, lash and brow salons: online bookings, deposits, Google Business Profile, reviews and before-and-after rules.",
    "answerFirst": "Beauty salon marketing starts with the details clients check before they book: the treatments you offer, who performs them, a price guide, availability and recent reviews. We keep those details accurate on your Google Business Profile, booking system, website and the AI tools clients ask, treatment by treatment, from colour to lashes.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Skin piercing, tattooing, electrolysis and semi-permanent make-up need registration with the local council outside London; London boroughs license special treatments separately. Source: <a href=\"https://www.gov.uk/find-licences/skin-piercing-and-tattooing\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Skin piercing and tattooing licence</a>.</li>\n<li>It is illegal in England to give Botox or dermal fillers for cosmetic purposes to anyone under 18. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2021/19/contents\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Botulinum Toxin and Cosmetic Fillers (Children) Act 2021</a>.</li>\n<li>The CAP Code requires evidence for objective claims about beauty and health-related treatments. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK beauty salon market itself online?</h3>\n<p>Beauty salon marketing starts with the details clients check before they book: the treatments you offer, who performs them, a price guide, availability and recent reviews. We keep those details accurate on your Google Business Profile, booking system, website and the AI tools clients ask, treatment by treatment, from colour to lashes.</p>\n<h3>Will a deposit put clients off booking online?</h3>\n<p>Some clients hesitate, so explain the policy before checkout rather than at payment. Whether to take deposits is your call. We make sure the policy reads the same on your booking system, website and Google Business Profile, so clients and AI tools never see conflicting rules about cancellations or no-shows.</p>\n<h3>Our salon is in London. Does licensing affect our marketing?</h3>\n<p>It can. Many London boroughs require a special treatment licence under the London Local Authorities Act 1991 for services such as massage, manicure or electrolysis. List only the treatments your licence covers and say who carries them out. We check your service list against what you tell us is licensed.</p>\n<h3>How should we use before-and-after photos?</h3>\n<p>Use genuine, unedited photos of your own work, with the client's written permission, and avoid filters that change the result. The CAP Code treats misleading images as misleading advertising. We help you caption each photo plainly with the treatment and the stylist, which also gives search engines and AI tools more to describe.</p>\n<h3>Do you write patch-test and aftercare pages?</h3>\n<p>Yes. Colour, lash and brow clients ask the same questions before and after every appointment. Clear written pages on allergy alert tests and aftercare save your team time on the phone, set expectations honestly and give AI tools accurate material instead of guesses. Your senior stylist checks the wording before it goes live.</p>\n<h3>We offer lots of treatments. Should all of them go on our Google profile?</h3>\n<p>List the treatments you most want to be known for, using the names clients actually search, such as gel nails, brow lamination or balayage. You can add more, but a long, untidy list makes it harder to see what you're good at. We set up your Google services and match them on your website, so clients see one clear picture.</p>\n<h3>What does the Local Presence Plan cost for a beauty salon, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/beauty-salon-marketing/#webpage",
          "url": "https://integrated.social/local/beauty-salon-marketing/",
          "name": "Beauty salon marketing for hair, nails and lashes",
          "headline": "Beauty salon marketing for hair, nails and lashes",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/beauty-salon-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/beauty-salon-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/beauty-salon-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Beauty salon marketing for UK hair, nail, lash and brow salons: online bookings, deposits, Google Business Profile, reviews and before-and-after rules.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Skin piercing and tattooing licence",
              "url": "https://www.gov.uk/find-licences/skin-piercing-and-tattooing"
            },
            {
              "@type": "CreativeWork",
              "name": "Botulinum Toxin and Cosmetic Fillers (Children) Act 2021",
              "url": "https://www.legislation.gov.uk/ukpga/2021/19/contents"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/beauty-salon-marketing/#service",
          "name": "Beauty salon marketing for hair, nails and lashes",
          "description": "Local Presence Plan for Beauty Salons: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for hair, nail, lash and tanning salons",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Hair, nail, lash, brow and tanning salons"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/beauty-salon-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/beauty-salon-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/beauty-salon-marketing/#offer",
          "name": "Local Presence Plan for Beauty Salons",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/beauty-salon-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Beauty salon marketing for hair, nails and lashes",
              "item": "https://integrated.social/local/beauty-salon-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/beauty-salon-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK beauty salon market itself online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Beauty salon marketing starts with the details clients check before they book: the treatments you offer, who performs them, a price guide, availability and recent reviews. We keep those details accurate on your Google Business Profile, booking system, website and the AI tools clients ask, treatment by treatment, from colour to lashes."
              }
            },
            {
              "@type": "Question",
              "name": "Will a deposit put clients off booking online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Some clients hesitate, so explain the policy before checkout rather than at payment. Whether to take deposits is your call. We make sure the policy reads the same on your booking system, website and Google Business Profile, so clients and AI tools never see conflicting rules about cancellations or no-shows."
              }
            },
            {
              "@type": "Question",
              "name": "Our salon is in London. Does licensing affect our marketing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It can. Many London boroughs require a special treatment licence under the London Local Authorities Act 1991 for services such as massage, manicure or electrolysis. List only the treatments your licence covers and say who carries them out. We check your service list against what you tell us is licensed."
              }
            },
            {
              "@type": "Question",
              "name": "How should we use before-and-after photos?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Use genuine, unedited photos of your own work, with the client's written permission, and avoid filters that change the result. The CAP Code treats misleading images as misleading advertising. We help you caption each photo plainly with the treatment and the stylist, which also gives search engines and AI tools more to describe."
              }
            },
            {
              "@type": "Question",
              "name": "Do you write patch-test and aftercare pages?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Colour, lash and brow clients ask the same questions before and after every appointment. Clear written pages on allergy alert tests and aftercare save your team time on the phone, set expectations honestly and give AI tools accurate material instead of guesses. Your senior stylist checks the wording before it goes live."
              }
            },
            {
              "@type": "Question",
              "name": "We offer lots of treatments. Should all of them go on our Google profile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "List the treatments you most want to be known for, using the names clients actually search, such as gel nails, brow lamination or balayage. You can add more, but a long, untidy list makes it harder to see what you're good at. We set up your Google services and match them on your website, so clients see one clear picture."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a beauty salon, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK beauty salon market itself online?",
        "a": "Beauty salon marketing starts with the details clients check before they book: the treatments you offer, who performs them, a price guide, availability and recent reviews. We keep those details accurate on your Google Business Profile, booking system, website and the AI tools clients ask, treatment by treatment, from colour to lashes."
      },
      {
        "q": "Will a deposit put clients off booking online?",
        "a": "Some clients hesitate, so explain the policy before checkout rather than at payment. Whether to take deposits is your call. We make sure the policy reads the same on your booking system, website and Google Business Profile, so clients and AI tools never see conflicting rules about cancellations or no-shows."
      },
      {
        "q": "Our salon is in London. Does licensing affect our marketing?",
        "a": "It can. Many London boroughs require a special treatment licence under the London Local Authorities Act 1991 for services such as massage, manicure or electrolysis. List only the treatments your licence covers and say who carries them out. We check your service list against what you tell us is licensed."
      },
      {
        "q": "How should we use before-and-after photos?",
        "a": "Use genuine, unedited photos of your own work, with the client's written permission, and avoid filters that change the result. The CAP Code treats misleading images as misleading advertising. We help you caption each photo plainly with the treatment and the stylist, which also gives search engines and AI tools more to describe."
      },
      {
        "q": "Do you write patch-test and aftercare pages?",
        "a": "Yes. Colour, lash and brow clients ask the same questions before and after every appointment. Clear written pages on allergy alert tests and aftercare save your team time on the phone, set expectations honestly and give AI tools accurate material instead of guesses. Your senior stylist checks the wording before it goes live."
      },
      {
        "q": "We offer lots of treatments. Should all of them go on our Google profile?",
        "a": "List the treatments you most want to be known for, using the names clients actually search, such as gel nails, brow lamination or balayage. You can add more, but a long, untidy list makes it harder to see what you're good at. We set up your Google services and match them on your website, so clients see one clear picture."
      },
      {
        "q": "What does the Local Presence Plan cost for a beauty salon, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/cafe-marketing/": {
    "section": "C-C2",
    "h1": "Café marketing for independent coffee shops",
    "title": "Café marketing for independent coffee shops",
    "description": "Cafe marketing for independent UK coffee shops: Google Business Profile, reviews, menus, food hygiene rating and delivery apps, without the jargon.",
    "answerFirst": "The marketing ideas that work for a UK independent café are mostly about being accurate where people decide: a complete Google Business Profile with menu, hours and photos, your food hygiene rating, clear allergen information, prompt replies to reviews, and posts that fill your quiet slots, such as weekday mornings or early evenings.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Food hygiene ratings are published online for businesses in England, Wales and Northern Ireland; displaying the sticker is a legal requirement in Wales and Northern Ireland and encouraged in England. Source: <a href=\"https://www.gov.uk/government/publications/food-hygiene-rating-scheme-fhrs-guidance-for-businesses/food-hygiene-rating-scheme-fhrs-guidance-for-businesses\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Food Hygiene Rating Scheme guidance for businesses</a>; <a href=\"https://ratings.food.gov.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Food hygiene ratings search</a>.</li>\n<li>Food businesses must give customers information about any of the 14 listed allergens used as ingredients. Source: <a href=\"https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Allergen guidance for food businesses</a>.</li>\n<li>Food prepacked for direct sale must carry a full ingredients list with allergens emphasised. Source: <a href=\"https://www.gov.uk/government/publications/prepacked-for-direct-sale-ppds-allergen-labelling-changes-for-schools-colleges-and-nurseries\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Prepacked for direct sale (PPDS) allergen labelling</a>.</li>\n<li>Tables and chairs on the pavement need a pavement licence from the council; fees are capped at £500 for a first application and £350 for a renewal. Source: <a href=\"https://www.gov.uk/government/publications/pavement-licences-guidance\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Pavement licences guidance</a>.</li>\n<li>Google says local results depend mainly on relevance, distance and popularity. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>What marketing ideas work for an independent café in the UK?</h3>\n<p>The marketing ideas that work for a UK independent café are mostly about being accurate where people decide: a complete Google Business Profile with menu, hours and photos, your food hygiene rating, clear allergen information, prompt replies to reviews, and posts that fill your quiet slots, such as weekday mornings or early evenings.</p>\n<h3>Will this bring more people in on quiet weekdays?</h3>\n<p>We cannot promise footfall. We can target the searches that match your quiet slots, such as early commuter coffee or a laptop-friendly mid-morning, and make sure your hours and details are right everywhere. Then we compare what changes against your own till figures, not against vanity numbers.</p>\n<h3>Do I need a new website?</h3>\n<p>Not necessarily. For many cafés the biggest gaps are inconsistent opening hours, an out-of-date menu and missing dietary or accessibility details across Google and other listings. The Free AI Visibility Score shows how clearly your current website is set up before you spend anything.</p>\n<h3>Should I focus on delivery apps or my own takeaway?</h3>\n<p>That depends on your margins, your kitchen and how well your food travels, which is your decision. We show how customers search for each option near you and make sure whichever you choose is described accurately, so you are not paying commission on orders you would have taken anyway.</p>\n<h3>Does my food hygiene rating matter online?</h3>\n<p>Customers can look it up on the Food Standards Agency website, and some apps show it beside your listing. In England displaying the sticker is voluntary; in Wales and Northern Ireland it is required. We make sure your listings link to the correct premises so the right rating appears.</p>\n<h3>My opening hours change with the seasons. Can you keep them right?</h3>\n<p>Yes, and it matters, because wrong hours can send people away from an open door. We set your regular, holiday and special hours on your Google Business Profile and match them on your website and the main directories. Your weekly report flags anything that looks out of date. Just tell us when your hours change.</p>\n<h3>What does the Local Presence Plan cost for a café, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleHtml": "<section id=\"ops-card-cafe\" aria-labelledby=\"ops-card-cafe-h\" data-event=\"ops_card_view\" data-card=\"cafe\">\n<h2 id=\"ops-card-cafe-h\">30-day café visibility and booking operations card</h2>\n<table>\n<tbody>\n<tr><th scope=\"row\">This month's goal</th><td>Make your hours, menu and allergen information right everywhere customers check, then fill one quiet slot.</td></tr>\n<tr><th scope=\"row\">Who owns it</th><td>You or your manager.</td></tr>\n<tr><th scope=\"row\">Public fact to verify</th><td>Opening hours (including holiday hours), the current menu with prices, and your food hygiene rating.</td></tr>\n<tr><th scope=\"row\">Customer question to answer</th><td>Are you open now, and is there something I can eat?</td></tr>\n<tr><th scope=\"row\">Booking friction to remove</th><td>A menu that exists only as an image or PDF, or an order or booking link that opens the wrong page.</td></tr>\n<tr><th scope=\"row\">Official sources to check</th><td><a href=\"https://ratings.food.gov.uk/\" target=\"_blank\" rel=\"noopener\">FSA: Food hygiene ratings search</a>; <a href=\"https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses\" target=\"_blank\" rel=\"noopener\">FSA: Allergen guidance for food businesses</a>; <a href=\"https://www.gov.uk/government/publications/pavement-licences-guidance\" target=\"_blank\" rel=\"noopener\">GOV.UK: Pavement licences guidance</a>; <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\">Google Business Profile Help: Guidelines for representing your business on Google</a></td></tr>\n<tr><th scope=\"row\">What not to claim</th><td>Allergen-free guarantees, a hygiene rating you do not hold, or \"best coffee in town\" without evidence.</td></tr>\n<tr><th scope=\"row\">Review date</th><td>Set a date 30 days from today and run the card again.</td></tr>\n</tbody>\n</table>\n<h3>Five public checks</h3>\n<ul>\n<li>Your Google categories and hours match your door sign, including holiday hours.</li>\n<li>Your menu is on your website as text, with prices and the date it was last updated.</li>\n<li>Your website says how customers can ask about allergens, and staff know the answer.</li>\n<li>Your food hygiene rating is linked from your website.</li>\n<li>If you use delivery apps, their hours and menu match yours; if you do not, say so.</li>\n<li>Any outdoor seating you mention is covered by your pavement licence.</li>\n</ul>\n<h3>Booking-route check</h3>\n<p>Tap your Google profile on a phone: the menu, the order or booking link and the phone number should each work in one tap. If you do not take bookings, say \"walk-ins only\" on Google and your website.</p>\n<h3>Review and response check</h3>\n<p>Reply to new reviews within a time you set and keep to it, and add new photos this month. Never offer a free drink or discount for a review; the CMA's fake reviews guidance treats concealed incentives as banned.</p>\n<h3>What this card cannot promise</h3>\n<p>The card helps your public information stay accurate. It does not promise more bookings, a place in Google Maps or a mention in an AI answer. For the checks every local business should run, see the <a href=\"/local/#uk-local-visibility-evidence-standard\">UK Local Visibility Evidence Standard</a>.</p>\n</section>",
    "moduleBeforeHeading": "Plans, prices and next step",
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/cafe-marketing/#webpage",
          "url": "https://integrated.social/local/cafe-marketing/",
          "name": "Café marketing for independent coffee shops",
          "headline": "Café marketing for independent coffee shops",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/cafe-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/cafe-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/cafe-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Cafe marketing for independent UK coffee shops: Google Business Profile, reviews, menus, food hygiene rating and delivery apps, without the jargon.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "FSA: Food Hygiene Rating Scheme guidance for businesses",
              "url": "https://www.gov.uk/government/publications/food-hygiene-rating-scheme-fhrs-guidance-for-businesses/food-hygiene-rating-scheme-fhrs-guidance-for-businesses"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Food hygiene ratings search",
              "url": "https://ratings.food.gov.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Allergen guidance for food businesses",
              "url": "https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Prepacked for direct sale (PPDS) allergen labelling",
              "url": "https://www.gov.uk/government/publications/prepacked-for-direct-sale-ppds-allergen-labelling-changes-for-schools-colleges-and-nurseries"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Pavement licences guidance",
              "url": "https://www.gov.uk/government/publications/pavement-licences-guidance"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Guidelines for representing your business on Google",
              "url": "https://support.google.com/business/answer/3038177"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/cafe-marketing/#service",
          "name": "Café marketing for independent coffee shops",
          "description": "Local Presence Plan for Cafés: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for independent cafés and coffee shops",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent cafés and coffee shops"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/cafe-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/cafe-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/cafe-marketing/#offer",
          "name": "Local Presence Plan for Cafés",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/cafe-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Café marketing for independent coffee shops",
              "item": "https://integrated.social/local/cafe-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/cafe-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What marketing ideas work for an independent café in the UK?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The marketing ideas that work for a UK independent café are mostly about being accurate where people decide: a complete Google Business Profile with menu, hours and photos, your food hygiene rating, clear allergen information, prompt replies to reviews, and posts that fill your quiet slots, such as weekday mornings or early evenings."
              }
            },
            {
              "@type": "Question",
              "name": "Will this bring more people in on quiet weekdays?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We cannot promise footfall. We can target the searches that match your quiet slots, such as early commuter coffee or a laptop-friendly mid-morning, and make sure your hours and details are right everywhere. Then we compare what changes against your own till figures, not against vanity numbers."
              }
            },
            {
              "@type": "Question",
              "name": "Do I need a new website?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Not necessarily. For many cafés the biggest gaps are inconsistent opening hours, an out-of-date menu and missing dietary or accessibility details across Google and other listings. The Free AI Visibility Score shows how clearly your current website is set up before you spend anything."
              }
            },
            {
              "@type": "Question",
              "name": "Should I focus on delivery apps or my own takeaway?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "That depends on your margins, your kitchen and how well your food travels, which is your decision. We show how customers search for each option near you and make sure whichever you choose is described accurately, so you are not paying commission on orders you would have taken anyway."
              }
            },
            {
              "@type": "Question",
              "name": "Does my food hygiene rating matter online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Customers can look it up on the Food Standards Agency website, and some apps show it beside your listing. In England displaying the sticker is voluntary; in Wales and Northern Ireland it is required. We make sure your listings link to the correct premises so the right rating appears."
              }
            },
            {
              "@type": "Question",
              "name": "My opening hours change with the seasons. Can you keep them right?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, and it matters, because wrong hours can send people away from an open door. We set your regular, holiday and special hours on your Google Business Profile and match them on your website and the main directories. Your weekly report flags anything that looks out of date. Just tell us when your hours change."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a café, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What marketing ideas work for an independent café in the UK?",
        "a": "The marketing ideas that work for a UK independent café are mostly about being accurate where people decide: a complete Google Business Profile with menu, hours and photos, your food hygiene rating, clear allergen information, prompt replies to reviews, and posts that fill your quiet slots, such as weekday mornings or early evenings."
      },
      {
        "q": "Will this bring more people in on quiet weekdays?",
        "a": "We cannot promise footfall. We can target the searches that match your quiet slots, such as early commuter coffee or a laptop-friendly mid-morning, and make sure your hours and details are right everywhere. Then we compare what changes against your own till figures, not against vanity numbers."
      },
      {
        "q": "Do I need a new website?",
        "a": "Not necessarily. For many cafés the biggest gaps are inconsistent opening hours, an out-of-date menu and missing dietary or accessibility details across Google and other listings. The Free AI Visibility Score shows how clearly your current website is set up before you spend anything."
      },
      {
        "q": "Should I focus on delivery apps or my own takeaway?",
        "a": "That depends on your margins, your kitchen and how well your food travels, which is your decision. We show how customers search for each option near you and make sure whichever you choose is described accurately, so you are not paying commission on orders you would have taken anyway."
      },
      {
        "q": "Does my food hygiene rating matter online?",
        "a": "Customers can look it up on the Food Standards Agency website, and some apps show it beside your listing. In England displaying the sticker is voluntary; in Wales and Northern Ireland it is required. We make sure your listings link to the correct premises so the right rating appears."
      },
      {
        "q": "My opening hours change with the seasons. Can you keep them right?",
        "a": "Yes, and it matters, because wrong hours can send people away from an open door. We set your regular, holiday and special hours on your Google Business Profile and match them on your website and the main directories. Your weekly report flags anything that looks out of date. Just tell us when your hours change."
      },
      {
        "q": "What does the Local Presence Plan cost for a café, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/cat-sitting-marketing/": {
    "section": "C-C3",
    "h1": "Cat sitting marketing for home-visit cat sitters",
    "title": "Cat sitting marketing for home-visit cat sitters",
    "description": "Cat sitting marketing for UK home-visit cat sitters: Google Business Profile service areas, bookings, reviews, meet-and-greets and repeat holiday clients.",
    "answerFirst": "Cat sitting marketing comes down to trust: owners are handing over their house keys and their cat. We explain your home visits clearly, from visit length to medication and key handling, and keep your service area, availability and booking process consistent across Google, your website and AI tools, so the right local owners book.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Defra's statutory guidance says cat sitters who look after cats in the owner's own home do not need a cat boarding licence. Source: <a href=\"https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities/cat-boarding-licensing-statutory-guidance-for-local-authorities\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Defra: Cat boarding licensing statutory guidance</a>.</li>\n<li>Owners in England must have their cat microchipped by the time it is 20 weeks old. Source: <a href=\"https://www.gov.uk/get-your-dog-cat-microchipped\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Get your dog or cat microchipped</a>.</li>\n<li>Service-area businesses can show the areas they serve on Google instead of a street address. Source: <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK cat sitter market their business?</h3>\n<p>Cat sitting marketing comes down to trust: owners are handing over their house keys and their cat. We explain your home visits clearly, from visit length to medication and key handling, and keep your service area, availability and booking process consistent across Google, your website and AI tools, so the right local owners book.</p>\n<h3>Is a council licence required for cat sitting?</h3>\n<p>For visits in the cat's own home, GOV.UK guidance says no licence is needed in England. Boarding cats away from their home as a business does need a council licence. We check your services against the current guidance before anything is published, and we never write fully licensed for home visits.</p>\n<h3>How should we explain key handling?</h3>\n<p>Write it down: how keys are collected, how they are labelled without the owner's address, where they are kept and when they are returned. A clear key policy answers the first worry most owners have, and it gives search engines and AI tools something concrete to describe.</p>\n<h3>Can we advertise giving medication?</h3>\n<p>Only describe what you are genuinely trained and willing to do, such as tablets or insulin injections, and how you agree instructions with the owner and their vet. Avoid health claims. Clear limits protect cats and reassure owners that you will not take on care you cannot provide.</p>\n<h3>Why offer a meet-and-greet first?</h3>\n<p>It lets you meet the cat, learn feeding and litter routines, check where medication is kept and collect keys in person. Owners feel more confident, and you avoid surprises on the first visit. We make the meet-and-greet step visible on your site and booking page so owners expect it.</p>\n<h3>I visit cats in their own homes. What should my Google profile show instead of an address?</h3>\n<p>If owners don't come to you, set your Google Business Profile up as a service-area business. That means hiding your home address and showing the towns or postcodes you cover. Only list areas you genuinely travel to. We set this up for you and keep your business name and phone number the same across your website and the main directories.</p>\n<h3>What does the Local Presence Plan cost for a cat sitter, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/cat-sitting-marketing/#webpage",
          "url": "https://integrated.social/local/cat-sitting-marketing/",
          "name": "Cat sitting marketing for home-visit cat sitters",
          "headline": "Cat sitting marketing for home-visit cat sitters",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/cat-sitting-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/cat-sitting-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/cat-sitting-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Cat sitting marketing for UK home-visit cat sitters: Google Business Profile service areas, bookings, reviews, meet-and-greets and repeat holiday clients.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Defra: Cat boarding licensing statutory guidance",
              "url": "https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities/cat-boarding-licensing-statutory-guidance-for-local-authorities"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Get your dog or cat microchipped",
              "url": "https://www.gov.uk/get-your-dog-cat-microchipped"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Manage your service areas for service-area and hybrid businesses",
              "url": "https://support.google.com/business/answer/9157481"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/cat-sitting-marketing/#service",
          "name": "Cat sitting marketing for home-visit cat sitters",
          "description": "Local Presence Plan for Cat Sitters: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for cat sitters",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Cat sitters and home-visit pet carers"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/cat-sitting-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/cat-sitting-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/cat-sitting-marketing/#offer",
          "name": "Local Presence Plan for Cat Sitters",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/cat-sitting-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Cat sitting marketing for home-visit cat sitters",
              "item": "https://integrated.social/local/cat-sitting-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/cat-sitting-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK cat sitter market their business?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Cat sitting marketing comes down to trust: owners are handing over their house keys and their cat. We explain your home visits clearly, from visit length to medication and key handling, and keep your service area, availability and booking process consistent across Google, your website and AI tools, so the right local owners book."
              }
            },
            {
              "@type": "Question",
              "name": "Is a council licence required for cat sitting?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For visits in the cat's own home, GOV.UK guidance says no licence is needed in England. Boarding cats away from their home as a business does need a council licence. We check your services against the current guidance before anything is published, and we never write fully licensed for home visits."
              }
            },
            {
              "@type": "Question",
              "name": "How should we explain key handling?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Write it down: how keys are collected, how they are labelled without the owner's address, where they are kept and when they are returned. A clear key policy answers the first worry most owners have, and it gives search engines and AI tools something concrete to describe."
              }
            },
            {
              "@type": "Question",
              "name": "Can we advertise giving medication?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only describe what you are genuinely trained and willing to do, such as tablets or insulin injections, and how you agree instructions with the owner and their vet. Avoid health claims. Clear limits protect cats and reassure owners that you will not take on care you cannot provide."
              }
            },
            {
              "@type": "Question",
              "name": "Why offer a meet-and-greet first?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It lets you meet the cat, learn feeding and litter routines, check where medication is kept and collect keys in person. Owners feel more confident, and you avoid surprises on the first visit. We make the meet-and-greet step visible on your site and booking page so owners expect it."
              }
            },
            {
              "@type": "Question",
              "name": "I visit cats in their own homes. What should my Google profile show instead of an address?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "If owners don't come to you, set your Google Business Profile up as a service-area business. That means hiding your home address and showing the towns or postcodes you cover. Only list areas you genuinely travel to. We set this up for you and keep your business name and phone number the same across your website and the main directories."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a cat sitter, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK cat sitter market their business?",
        "a": "Cat sitting marketing comes down to trust: owners are handing over their house keys and their cat. We explain your home visits clearly, from visit length to medication and key handling, and keep your service area, availability and booking process consistent across Google, your website and AI tools, so the right local owners book."
      },
      {
        "q": "Is a council licence required for cat sitting?",
        "a": "For visits in the cat's own home, GOV.UK guidance says no licence is needed in England. Boarding cats away from their home as a business does need a council licence. We check your services against the current guidance before anything is published, and we never write fully licensed for home visits."
      },
      {
        "q": "How should we explain key handling?",
        "a": "Write it down: how keys are collected, how they are labelled without the owner's address, where they are kept and when they are returned. A clear key policy answers the first worry most owners have, and it gives search engines and AI tools something concrete to describe."
      },
      {
        "q": "Can we advertise giving medication?",
        "a": "Only describe what you are genuinely trained and willing to do, such as tablets or insulin injections, and how you agree instructions with the owner and their vet. Avoid health claims. Clear limits protect cats and reassure owners that you will not take on care you cannot provide."
      },
      {
        "q": "Why offer a meet-and-greet first?",
        "a": "It lets you meet the cat, learn feeding and litter routines, check where medication is kept and collect keys in person. Owners feel more confident, and you avoid surprises on the first visit. We make the meet-and-greet step visible on your site and booking page so owners expect it."
      },
      {
        "q": "I visit cats in their own homes. What should my Google profile show instead of an address?",
        "a": "If owners don't come to you, set your Google Business Profile up as a service-area business. That means hiding your home address and showing the towns or postcodes you cover. Only list areas you genuinely travel to. We set this up for you and keep your business name and phone number the same across your website and the main directories."
      },
      {
        "q": "What does the Local Presence Plan cost for a cat sitter, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/dentist-marketing/": {
    "section": "C-C4",
    "h1": "Dental marketing for NHS, mixed and private practices",
    "title": "Dental marketing for NHS, mixed and private practices",
    "description": "Dental marketing for UK practices: patient recall, treatment pages, Google Business Profile and AI search, with GDC and ASA guidance in mind. London-led.",
    "answerFirst": "To attract more private and mixed-practice patients, a UK dental practice needs four things patients and AI tools can check: an accurate NHS website and Google Business Profile listing, one page per treatment with fees and steps, reviews you reply to, and advertising that follows GDC guidance and the CAP Code, approved by a registrant at the practice.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>NHS dental charges in England are set nationally: Band 1 is £27.90, Band 2 is £76.60 and Band 3 is £332.10 (NHS website, checked 28 October 2026). Source: <a href=\"https://www.nhs.uk/nhs-services/dentists/how-much-nhs-dental-treatment-costs/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">NHS website: How much NHS dental treatment costs</a>.</li>\n<li>Patients use the NHS website's Find a dentist service to see which practices say they are taking on NHS patients. Source: <a href=\"https://www.nhs.uk/service-search/find-a-dentist\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">NHS website: Find a dentist</a>.</li>\n<li>The GDC's guidance on advertising applies to all dental advertising, and only dentists on a GDC specialist list may call themselves specialists. Source: <a href=\"https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">General Dental Council: Guidance on advertising</a>; <a href=\"https://www.gdc-uk.org/registration/your-registration/specialist-lists\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">General Dental Council: Specialist lists</a>.</li>\n<li>Dental service providers in England must be registered with the Care Quality Commission. Source: <a href=\"https://www.cqc.org.uk/guidance-regulation/dentists\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Care Quality Commission: Dentists, information for providers</a>.</li>\n<li>The CAP Code requires evidence for objective health and treatment claims. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>What marketing approaches help a UK dental practice attract more private and mixed-practice patients?</h3>\n<p>To attract more private and mixed-practice patients, a UK dental practice needs four things patients and AI tools can check: an accurate NHS website and Google Business Profile listing, one page per treatment with fees and steps, reviews you reply to, and advertising that follows GDC guidance and the CAP Code, approved by a registrant at the practice.</p>\n<h3>Can you help us get fewer unsuitable enquiries?</h3>\n<p>Yes, that is often the first job. If your practice cannot take new NHS patients, or you want to grow implant and clear aligner work, your pages and listings should say so plainly. Clear information filters enquiries before they reach reception, so your team spends its time on patients you can actually treat.</p>\n<h3>Who checks our copy for GDC and ASA compliance?</h3>\n<p>We flag claims, titles, testimonials and imagery that may breach GDC guidance or the CAP Code, but we are not your compliance adviser. A registered professional at your practice approves clinical and advertising content before publication. Agents draft; a named senior person signs. For clinical claims, your registrant approves.</p>\n<h3>How do you track whether enquiries become patients?</h3>\n<p>We agree one route from enquiry to consultation booked to treatment accepted, using your practice management software where it allows. The aim is to see which patient questions lead to real appointments and treatment plans, rather than reporting clicks or impressions that never reach your diary.</p>\n<h3>Will our practice appear in ChatGPT or Google AI answers?</h3>\n<p>Nobody can honestly guarantee that. We make your practice facts and treatment explainers accurate, consistent and easy for search engines and AI tools to read, then report what changes. Start with the Free AI Visibility Score, which shows how clearly your website is set up today.</p>\n<h3>What does the weekly report tell a practice manager?</h3>\n<p>It lists the three most important fixes for that week, in plain words. For a practice, that might be a treatment missing from your Google profile, opening hours that don't match your website, or an old photo that needs replacing. Each fix is short and specific. It's a to-do list, not a sales pitch or a page of charts.</p>\n<h3>What does the Local Presence Plan cost for a dental practice, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleHtml": null,
    "moduleBeforeHeading": "What this does not cover",
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/dentist-marketing/#webpage",
          "url": "https://integrated.social/local/dentist-marketing/",
          "name": "Dental marketing for NHS, mixed and private practices",
          "headline": "Dental marketing for NHS, mixed and private practices",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/dentist-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/dentist-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/dentist-marketing/#faq"
          },
          "hasPart": [
            {
              "@id": "https://integrated.social/local/teeth-whitening-marketing/#webpage"
            }
          ],
          "dateModified": "2026-10-28",
          "description": "Dental marketing for UK practices: patient recall, treatment pages, Google Business Profile and AI search, with GDC and ASA guidance in mind. London-led.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "NHS website: How much NHS dental treatment costs",
              "url": "https://www.nhs.uk/nhs-services/dentists/how-much-nhs-dental-treatment-costs/"
            },
            {
              "@type": "CreativeWork",
              "name": "NHS website: Find a dentist",
              "url": "https://www.nhs.uk/service-search/find-a-dentist"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Guidance on advertising",
              "url": "https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Specialist lists",
              "url": "https://www.gdc-uk.org/registration/your-registration/specialist-lists"
            },
            {
              "@type": "CreativeWork",
              "name": "Care Quality Commission: Dentists, information for providers",
              "url": "https://www.cqc.org.uk/guidance-regulation/dentists"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Standards and guidance",
              "url": "https://www.gdc-uk.org/standards-guidance/standards-and-guidance"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/dentist-marketing/#service",
          "name": "Dental marketing for NHS, mixed and private practices",
          "description": "Local Presence Plan for Dental Practices: available across the UK, including Greater London.",
          "serviceType": "Marketing and AI search visibility for dental practices",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "NHS, mixed and private dental practices"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/dentist-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/dentist-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/dentist-marketing/#offer",
          "name": "Local Presence Plan for Dental Practices",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/dentist-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Dental marketing for NHS, mixed and private practices",
              "item": "https://integrated.social/local/dentist-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/dentist-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What marketing approaches help a UK dental practice attract more private and mixed-practice patients?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "To attract more private and mixed-practice patients, a UK dental practice needs four things patients and AI tools can check: an accurate NHS website and Google Business Profile listing, one page per treatment with fees and steps, reviews you reply to, and advertising that follows GDC guidance and the CAP Code, approved by a registrant at the practice."
              }
            },
            {
              "@type": "Question",
              "name": "Can you help us get fewer unsuitable enquiries?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, that is often the first job. If your practice cannot take new NHS patients, or you want to grow implant and clear aligner work, your pages and listings should say so plainly. Clear information filters enquiries before they reach reception, so your team spends its time on patients you can actually treat."
              }
            },
            {
              "@type": "Question",
              "name": "Who checks our copy for GDC and ASA compliance?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We flag claims, titles, testimonials and imagery that may breach GDC guidance or the CAP Code, but we are not your compliance adviser. A registered professional at your practice approves clinical and advertising content before publication. Agents draft; a named senior person signs. For clinical claims, your registrant approves."
              }
            },
            {
              "@type": "Question",
              "name": "How do you track whether enquiries become patients?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We agree one route from enquiry to consultation booked to treatment accepted, using your practice management software where it allows. The aim is to see which patient questions lead to real appointments and treatment plans, rather than reporting clicks or impressions that never reach your diary."
              }
            },
            {
              "@type": "Question",
              "name": "Will our practice appear in ChatGPT or Google AI answers?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Nobody can honestly guarantee that. We make your practice facts and treatment explainers accurate, consistent and easy for search engines and AI tools to read, then report what changes. Start with the Free AI Visibility Score, which shows how clearly your website is set up today."
              }
            },
            {
              "@type": "Question",
              "name": "What does the weekly report tell a practice manager?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It lists the three most important fixes for that week, in plain words. For a practice, that might be a treatment missing from your Google profile, opening hours that don't match your website, or an old photo that needs replacing. Each fix is short and specific. It's a to-do list, not a sales pitch or a page of charts."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a dental practice, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What marketing approaches help a UK dental practice attract more private and mixed-practice patients?",
        "a": "To attract more private and mixed-practice patients, a UK dental practice needs four things patients and AI tools can check: an accurate NHS website and Google Business Profile listing, one page per treatment with fees and steps, reviews you reply to, and advertising that follows GDC guidance and the CAP Code, approved by a registrant at the practice."
      },
      {
        "q": "Can you help us get fewer unsuitable enquiries?",
        "a": "Yes, that is often the first job. If your practice cannot take new NHS patients, or you want to grow implant and clear aligner work, your pages and listings should say so plainly. Clear information filters enquiries before they reach reception, so your team spends its time on patients you can actually treat."
      },
      {
        "q": "Who checks our copy for GDC and ASA compliance?",
        "a": "We flag claims, titles, testimonials and imagery that may breach GDC guidance or the CAP Code, but we are not your compliance adviser. A registered professional at your practice approves clinical and advertising content before publication. Agents draft; a named senior person signs. For clinical claims, your registrant approves."
      },
      {
        "q": "How do you track whether enquiries become patients?",
        "a": "We agree one route from enquiry to consultation booked to treatment accepted, using your practice management software where it allows. The aim is to see which patient questions lead to real appointments and treatment plans, rather than reporting clicks or impressions that never reach your diary."
      },
      {
        "q": "Will our practice appear in ChatGPT or Google AI answers?",
        "a": "Nobody can honestly guarantee that. We make your practice facts and treatment explainers accurate, consistent and easy for search engines and AI tools to read, then report what changes. Start with the Free AI Visibility Score, which shows how clearly your website is set up today."
      },
      {
        "q": "What does the weekly report tell a practice manager?",
        "a": "It lists the three most important fixes for that week, in plain words. For a practice, that might be a treatment missing from your Google profile, opening hours that don't match your website, or an old photo that needs replacing. Each fix is short and specific. It's a to-do list, not a sales pitch or a page of charts."
      },
      {
        "q": "What does the Local Presence Plan cost for a dental practice, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/dog-grooming-marketing/": {
    "section": "C-C5",
    "h1": "Dog grooming marketing for salons and mobile groomers",
    "title": "Dog grooming marketing for salons and mobile groomers",
    "description": "Dog grooming marketing for UK salons, home studios and mobile vans: Google Business Profile, rebooking, reviews and breed-led service pages.",
    "answerFirst": "Marketing a UK dog grooming business works best when owners can see, before they call, which breeds and coats you handle, where you work, how long each groom takes and when they can rebook. Set this out on your Google Business Profile and service pages, ask happy clients for reviews, and send rebooking reminders by coat type.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Dog grooming on its own is not one of the activities licensed under England's animal activities regulations, which cover activities such as dog boarding and day care. Source: <a href=\"https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Defra: Animal activities licensing guidance for local authorities</a>.</li>\n<li>Dogs in England must be microchipped by the time they are 8 weeks old. Source: <a href=\"https://www.gov.uk/get-your-dog-cat-microchipped\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Get your dog or cat microchipped</a>.</li>\n<li>Mobile grooming vans driving in London must meet ULEZ standards or pay the daily charge. Source: <a href=\"https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Transport for London: ULEZ, where and when</a>.</li>\n<li>Service-area businesses can show the areas they serve on Google instead of a street address. Source: <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK dog grooming business market itself?</h3>\n<p>Marketing a UK dog grooming business works best when owners can see, before they call, which breeds and coats you handle, where you work, how long each groom takes and when they can rebook. Set this out on your Google Business Profile and service pages, ask happy clients for reviews, and send rebooking reminders by coat type.</p>\n<h3>Can you help me attract specific breeds?</h3>\n<p>Yes. Owners often search by breed and coat type, such as cockapoo, cavapoo or border terrier hand stripping. We make sure your pages and listings describe the work you do well, in owners' own words, so the right enquiries find you and unsuitable ones are filtered out before they reach your calendar.</p>\n<h3>I'm a mobile groomer. How do you handle my service area?</h3>\n<p>We define it honestly by the postcodes and neighbourhoods you actually reach, taking traffic, parking and, in London, the ULEZ into account. An accurate service area matters more than covering every borough, because a booking you cannot reach on time costs you more than it earns.</p>\n<h3>Will this guarantee a full diary?</h3>\n<p>No. We do not promise bookings, rankings or appearances in AI answers. We make your information accurate and easy for search engines and AI tools to read, connect enquiries to your booking calendar and report what changes. The Free AI Visibility Score is the starting point.</p>\n<h3>Should my website mention grooming awards?</h3>\n<p>Yes, if your team has earned them. Give each award or training course its exact title and the year it was completed, and say which groomer holds it. Owners trying a new groomer also want to know how nervous or elderly dogs are handled, so pair the list with a plain description of your approach. Leave out anything nobody in the team actually holds.</p>\n<h3>Can you help me promote puppy introduction appointments?</h3>\n<p>Yes, within the plan. Your Google profile can list puppy introduction visits as a service, and your website can say from what age you see puppies and what a first visit involves. Your weekly report may suggest where that information is missing. We keep it factual and friendly, with no pressure lines or limited-time offers.</p>\n<h3>What does the Local Presence Plan cost for a dog groomer, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleHtml": "<section id=\"ops-card-dog-grooming\" aria-labelledby=\"ops-card-dog-grooming-h\" data-event=\"ops_card_view\" data-card=\"dog_grooming\">\n<h2 id=\"ops-card-dog-grooming-h\">30-day dog grooming visibility and booking operations card</h2>\n<table>\n<tbody>\n<tr><th scope=\"row\">This month's goal</th><td>Make it clear which dogs you groom, where you work and how rebooking works.</td></tr>\n<tr><th scope=\"row\">Who owns it</th><td>You, as the groomer or salon owner.</td></tr>\n<tr><th scope=\"row\">Public fact to verify</th><td>Services by breed and coat type, how long each groom takes, your service area and your cancellation policy.</td></tr>\n<tr><th scope=\"row\">Customer question to answer</th><td>Will you groom my dog, can you reach me, and when can I rebook?</td></tr>\n<tr><th scope=\"row\">Booking friction to remove</th><td>No visible rebooking route after a groom, or a service area that does not match where the van actually goes.</td></tr>\n<tr><th scope=\"row\">Official sources to check</th><td><a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>; <a href=\"https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when\" target=\"_blank\" rel=\"noopener\">Transport for London: ULEZ, where and when</a>; <a href=\"https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities\" target=\"_blank\" rel=\"noopener\">Defra: Animal activities licensing guidance for local authorities</a>; <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\">CMA: Fake reviews guidance (CMA208)</a></td></tr>\n<tr><th scope=\"row\">What not to claim</th><td>\"Stress-free for every dog\", any welfare or veterinary advice, or qualifications and insurance you do not hold.</td></tr>\n<tr><th scope=\"row\">Review date</th><td>Set a date 30 days from today and run the card again.</td></tr>\n</tbody>\n</table>\n<h3>Five public checks</h3>\n<ul>\n<li>Your Google profile lists the services you offer, by coat type where it helps owners choose.</li>\n<li>Each service shows how long it usually takes.</li>\n<li>Your service area on Google matches the postcodes you really cover; for a van in London, you have checked the ULEZ.</li>\n<li>Your cancellation and late-arrival terms are written on your website.</li>\n<li>Insurance or qualification wording appears only as you have supplied it, and you can show proof.</li>\n</ul>\n<h3>Booking-route check</h3>\n<p>After each groom, the owner knows how to book the next one: a link, a card or a reminder they agreed to receive. Check the link opens your booking page with the right service.</p>\n<h3>Review and response check</h3>\n<p>Ask every client for a review in the same way, at the same point after the groom. Reply without sharing details about the dog or the owner.</p>\n<h3>What this card cannot promise</h3>\n<p>The card helps your public information stay accurate. It does not promise more bookings, a place in Google Maps or a mention in an AI answer. For the checks every local business should run, see the <a href=\"/local/#uk-local-visibility-evidence-standard\">UK Local Visibility Evidence Standard</a>.</p>\n</section>",
    "moduleBeforeHeading": "Plans, prices and next step",
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/dog-grooming-marketing/#webpage",
          "url": "https://integrated.social/local/dog-grooming-marketing/",
          "name": "Dog grooming marketing for salons and mobile groomers",
          "headline": "Dog grooming marketing for salons and mobile groomers",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/dog-grooming-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/dog-grooming-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/dog-grooming-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Dog grooming marketing for UK salons, home studios and mobile vans: Google Business Profile, rebooking, reviews and breed-led service pages.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Defra: Animal activities licensing guidance for local authorities",
              "url": "https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Get your dog or cat microchipped",
              "url": "https://www.gov.uk/get-your-dog-cat-microchipped"
            },
            {
              "@type": "CreativeWork",
              "name": "Transport for London: ULEZ, where and when",
              "url": "https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Manage your service areas for service-area and hybrid businesses",
              "url": "https://support.google.com/business/answer/9157481"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/dog-grooming-marketing/#service",
          "name": "Dog grooming marketing for salons and mobile groomers",
          "description": "Local Presence Plan for Dog Groomers: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for dog grooming salons, home studios and mobile groomers",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Dog grooming salons, home studios and mobile groomers"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/dog-grooming-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/dog-grooming-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/dog-grooming-marketing/#offer",
          "name": "Local Presence Plan for Dog Groomers",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/dog-grooming-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Dog grooming marketing for salons and mobile groomers",
              "item": "https://integrated.social/local/dog-grooming-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/dog-grooming-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK dog grooming business market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Marketing a UK dog grooming business works best when owners can see, before they call, which breeds and coats you handle, where you work, how long each groom takes and when they can rebook. Set this out on your Google Business Profile and service pages, ask happy clients for reviews, and send rebooking reminders by coat type."
              }
            },
            {
              "@type": "Question",
              "name": "Can you help me attract specific breeds?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Owners often search by breed and coat type, such as cockapoo, cavapoo or border terrier hand stripping. We make sure your pages and listings describe the work you do well, in owners' own words, so the right enquiries find you and unsuitable ones are filtered out before they reach your calendar."
              }
            },
            {
              "@type": "Question",
              "name": "I'm a mobile groomer. How do you handle my service area?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We define it honestly by the postcodes and neighbourhoods you actually reach, taking traffic, parking and, in London, the ULEZ into account. An accurate service area matters more than covering every borough, because a booking you cannot reach on time costs you more than it earns."
              }
            },
            {
              "@type": "Question",
              "name": "Will this guarantee a full diary?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. We do not promise bookings, rankings or appearances in AI answers. We make your information accurate and easy for search engines and AI tools to read, connect enquiries to your booking calendar and report what changes. The Free AI Visibility Score is the starting point."
              }
            },
            {
              "@type": "Question",
              "name": "Should my website mention grooming awards?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, if your team has earned them. Give each award or training course its exact title and the year it was completed, and say which groomer holds it. Owners trying a new groomer also want to know how nervous or elderly dogs are handled, so pair the list with a plain description of your approach. Leave out anything nobody in the team actually holds."
              }
            },
            {
              "@type": "Question",
              "name": "Can you help me promote puppy introduction appointments?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, within the plan. Your Google profile can list puppy introduction visits as a service, and your website can say from what age you see puppies and what a first visit involves. Your weekly report may suggest where that information is missing. We keep it factual and friendly, with no pressure lines or limited-time offers."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a dog groomer, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK dog grooming business market itself?",
        "a": "Marketing a UK dog grooming business works best when owners can see, before they call, which breeds and coats you handle, where you work, how long each groom takes and when they can rebook. Set this out on your Google Business Profile and service pages, ask happy clients for reviews, and send rebooking reminders by coat type."
      },
      {
        "q": "Can you help me attract specific breeds?",
        "a": "Yes. Owners often search by breed and coat type, such as cockapoo, cavapoo or border terrier hand stripping. We make sure your pages and listings describe the work you do well, in owners' own words, so the right enquiries find you and unsuitable ones are filtered out before they reach your calendar."
      },
      {
        "q": "I'm a mobile groomer. How do you handle my service area?",
        "a": "We define it honestly by the postcodes and neighbourhoods you actually reach, taking traffic, parking and, in London, the ULEZ into account. An accurate service area matters more than covering every borough, because a booking you cannot reach on time costs you more than it earns."
      },
      {
        "q": "Will this guarantee a full diary?",
        "a": "No. We do not promise bookings, rankings or appearances in AI answers. We make your information accurate and easy for search engines and AI tools to read, connect enquiries to your booking calendar and report what changes. The Free AI Visibility Score is the starting point."
      },
      {
        "q": "Should my website mention grooming awards?",
        "a": "Yes, if your team has earned them. Give each award or training course its exact title and the year it was completed, and say which groomer holds it. Owners trying a new groomer also want to know how nervous or elderly dogs are handled, so pair the list with a plain description of your approach. Leave out anything nobody in the team actually holds."
      },
      {
        "q": "Can you help me promote puppy introduction appointments?",
        "a": "Yes, within the plan. Your Google profile can list puppy introduction visits as a service, and your website can say from what age you see puppies and what a first visit involves. Your weekly report may suggest where that information is missing. We keep it factual and friendly, with no pressure lines or limited-time offers."
      },
      {
        "q": "What does the Local Presence Plan cost for a dog groomer, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/dog-walker-marketing/": {
    "section": "C-C6",
    "h1": "Dog walker marketing for local walking businesses",
    "title": "Dog walker marketing for local walking businesses",
    "description": "Dog walker marketing for UK walking businesses: Google Business Profile service areas, group and solo walks, reviews, insurance and council dog limits.",
    "answerFirst": "Dog walker marketing for local walking businesses has to answer three worries owners have: who has my keys, how many dogs are walked together, and where they go. We present your walks, areas, group sizes and booking process consistently across Google, your website and AI tools, so nearby owners can choose you with confidence.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Councils can use Public Spaces Protection Orders to set rules for dogs in public spaces, including limits on how many dogs one person may walk. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2014/12/part/4/chapter/2\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Anti-social Behaviour, Crime and Policing Act 2014, Part 4 Chapter 2 (Public Spaces Protection Orders)</a>.</li>\n<li>Commercial dog walkers need a licence from The Royal Parks to work in the parks where its licence scheme applies. Source: <a href=\"https://www.royalparks.org.uk/commercial-licences/dog-walking-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Royal Parks: Commercial dog walking licences</a>.</li>\n<li>Dog walking is not a licensable animal activity in England, but dog day care is. Source: <a href=\"https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities/dog-day-care-licensing-statutory-guidance-for-local-authorities\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Defra: Dog day care licensing statutory guidance</a>.</li>\n<li>Dogs in England must be microchipped by the time they are 8 weeks old. Source: <a href=\"https://www.gov.uk/get-your-dog-cat-microchipped\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Get your dog or cat microchipped</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK dog walker market their business?</h3>\n<p>Dog walker marketing for local walking businesses has to answer three worries owners have: who has my keys, how many dogs are walked together, and where they go. We present your walks, areas, group sizes and booking process consistently across Google, your website and AI tools, so nearby owners can choose you with confidence.</p>\n<h3>How many dogs can we say we walk at once?</h3>\n<p>Only the number you actually walk, within your council's Public Spaces Protection Order. Group sizes are set locally, often through a council PSPO (see legislation.gov.uk), and rules differ between neighbouring councils. We check the PSPO for the areas you cover before any group size goes on your website or profile.</p>\n<h3>What do owners expect to read about pick-ups and home access?</h3>\n<p>Owners leaving a dog at home want to know who comes in, when, and what happens at the door. Say whether you collect from the house or a neighbour, how you secure gates and leads, how alarms are handled and when the dog is returned. Practical detail like this answers the question before they ask.</p>\n<h3>Can we mention insurance on our site?</h3>\n<p>Only if you hold a current policy, and describe it exactly as written. Avoid broad phrases that suggest cover you do not have. We would rather leave insurance out than overstate it, because owners check, and an inaccurate claim does more harm than saying nothing.</p>\n<h3>Do I need a licence to walk dogs commercially?</h3>\n<p>Dog walking is not one of the animal activities licensed in England, but dog day care and boarding are. Some councils set limits on how many dogs one person can walk, and The Royal Parks requires a commercial dog walking licence in the parks its scheme covers. Check the rules for every area you list on your profile.</p>\n<h3>What does the Local Presence Plan cost for a dog walker, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/dog-walker-marketing/#webpage",
          "url": "https://integrated.social/local/dog-walker-marketing/",
          "name": "Dog walker marketing for local walking businesses",
          "headline": "Dog walker marketing for local walking businesses",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/dog-walker-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/dog-walker-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/dog-walker-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Dog walker marketing for UK walking businesses: Google Business Profile service areas, group and solo walks, reviews, insurance and council dog limits.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Anti-social Behaviour, Crime and Policing Act 2014, Part 4 Chapter 2 (Public Spaces Protection Orders)",
              "url": "https://www.legislation.gov.uk/ukpga/2014/12/part/4/chapter/2"
            },
            {
              "@type": "CreativeWork",
              "name": "The Royal Parks: Commercial dog walking licences",
              "url": "https://www.royalparks.org.uk/commercial-licences/dog-walking-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Defra: Dog day care licensing statutory guidance",
              "url": "https://www.gov.uk/government/publications/animal-activities-licensing-guidance-for-local-authorities/dog-day-care-licensing-statutory-guidance-for-local-authorities"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Get your dog or cat microchipped",
              "url": "https://www.gov.uk/get-your-dog-cat-microchipped"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/dog-walker-marketing/#service",
          "name": "Dog walker marketing for local walking businesses",
          "description": "Local Presence Plan for Dog Walkers: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for dog walkers",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Dog walkers"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/dog-walker-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/dog-walker-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/dog-walker-marketing/#offer",
          "name": "Local Presence Plan for Dog Walkers",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/dog-walker-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Dog walker marketing for local walking businesses",
              "item": "https://integrated.social/local/dog-walker-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/dog-walker-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK dog walker market their business?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Dog walker marketing for local walking businesses has to answer three worries owners have: who has my keys, how many dogs are walked together, and where they go. We present your walks, areas, group sizes and booking process consistently across Google, your website and AI tools, so nearby owners can choose you with confidence."
              }
            },
            {
              "@type": "Question",
              "name": "How many dogs can we say we walk at once?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only the number you actually walk, within your council's Public Spaces Protection Order. Group sizes are set locally, often through a council PSPO (see legislation.gov.uk), and rules differ between neighbouring councils. We check the PSPO for the areas you cover before any group size goes on your website or profile."
              }
            },
            {
              "@type": "Question",
              "name": "What do owners expect to read about pick-ups and home access?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Owners leaving a dog at home want to know who comes in, when, and what happens at the door. Say whether you collect from the house or a neighbour, how you secure gates and leads, how alarms are handled and when the dog is returned. Practical detail like this answers the question before they ask."
              }
            },
            {
              "@type": "Question",
              "name": "Can we mention insurance on our site?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only if you hold a current policy, and describe it exactly as written. Avoid broad phrases that suggest cover you do not have. We would rather leave insurance out than overstate it, because owners check, and an inaccurate claim does more harm than saying nothing."
              }
            },
            {
              "@type": "Question",
              "name": "Do I need a licence to walk dogs commercially?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Dog walking is not one of the animal activities licensed in England, but dog day care and boarding are. Some councils set limits on how many dogs one person can walk, and The Royal Parks requires a commercial dog walking licence in the parks its scheme covers. Check the rules for every area you list on your profile."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a dog walker, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK dog walker market their business?",
        "a": "Dog walker marketing for local walking businesses has to answer three worries owners have: who has my keys, how many dogs are walked together, and where they go. We present your walks, areas, group sizes and booking process consistently across Google, your website and AI tools, so nearby owners can choose you with confidence."
      },
      {
        "q": "How many dogs can we say we walk at once?",
        "a": "Only the number you actually walk, within your council's Public Spaces Protection Order. Group sizes are set locally, often through a council PSPO (see legislation.gov.uk), and rules differ between neighbouring councils. We check the PSPO for the areas you cover before any group size goes on your website or profile."
      },
      {
        "q": "What do owners expect to read about pick-ups and home access?",
        "a": "Owners leaving a dog at home want to know who comes in, when, and what happens at the door. Say whether you collect from the house or a neighbour, how you secure gates and leads, how alarms are handled and when the dog is returned. Practical detail like this answers the question before they ask."
      },
      {
        "q": "Can we mention insurance on our site?",
        "a": "Only if you hold a current policy, and describe it exactly as written. Avoid broad phrases that suggest cover you do not have. We would rather leave insurance out than overstate it, because owners check, and an inaccurate claim does more harm than saying nothing."
      },
      {
        "q": "Do I need a licence to walk dogs commercially?",
        "a": "Dog walking is not one of the animal activities licensed in England, but dog day care and boarding are. Some councils set limits on how many dogs one person can walk, and The Royal Parks requires a commercial dog walking licence in the parks its scheme covers. Check the rules for every area you list on your profile."
      },
      {
        "q": "What does the Local Presence Plan cost for a dog walker, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/driving-instructor-marketing/": {
    "section": "C-C7",
    "h1": "Driving instructor marketing for ADIs",
    "title": "Driving instructor marketing for ADIs",
    "description": "Driving instructor marketing for UK ADIs and small driving schools: pupil enquiries, test-area pages, Google Business Profile, reviews and badge rules.",
    "answerFirst": "Driving instructor marketing for ADIs is about helping learners and parents confirm the basics quickly: your pick-up areas, manual or automatic lessons, the test centres you know and your ADI badge. We keep these details consistent across Google, your website and AI tools, so local pupils can find you and see clearly what you offer.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Only an approved driving instructor (ADI), or a trainee with a DVSA trainee licence, can charge for driving lessons. Source: <a href=\"https://www.gov.uk/become-a-driving-instructor\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Check if you can become a driving instructor</a>.</li>\n<li>Anyone can report a person giving paid lessons without being an ADI. Source: <a href=\"https://www.gov.uk/report-an-illegal-driving-instructor\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Report an illegal driving instructor</a>.</li>\n<li>DVSA publishes driving test data, including pass rates by test centre. Source: <a href=\"https://www.gov.uk/government/collections/driving-tests-and-instructors-statistics\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Driving test, theory test and driving instructor data</a>.</li>\n<li>Service-area businesses can show the areas they serve on Google instead of a street address. Source: <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK driving instructor market their lessons?</h3>\n<p>Driving instructor marketing for ADIs is about helping learners and parents confirm the basics quickly: your pick-up areas, manual or automatic lessons, the test centres you know and your ADI badge. We keep these details consistent across Google, your website and AI tools, so local pupils can find you and see clearly what you offer.</p>\n<h3>Can we publish our pass rate?</h3>\n<p>We do not publish pass rates or pass-first-time claims on your pages. Results depend on each pupil, and outcome claims can mislead. Instead we describe how you teach, the formats you offer, the test centres you know and real pupil comments shared with consent, which answer what learners actually ask.</p>\n<h3>Who is allowed to charge for driving lessons?</h3>\n<p>GOV.UK says only approved driving instructors on the ADI register, or trainee instructors holding a trainee licence, can charge for lessons in a car. ADIs display a green badge and trainees a pink one (see GOV.UK). We make sure your badge status is described accurately and point learners to the official checks.</p>\n<h3>How should a trainee driving instructor advertise?</h3>\n<p>As a trainee driving instructor, never as a PDI, and through the training establishment named on the licence. GOV.UK trainee licence rules say trainees can only give instruction from that establishment. We keep wording accurate so marketing never suggests a trainee is a fully approved instructor.</p>\n<h3>Do we need separate pages for each town we cover?</h3>\n<p>No. One clear section listing pick-up postcodes, schools and test centres serves learners better than many thin town pages. We keep that list in step with your Google profile service area, so search engines and AI tools see one consistent answer about where you teach.</p>\n<h3>Can I mention test centre pass rates in my marketing?</h3>\n<p>You can point learners to DVSA's published pass rates by test centre, but do not present a centre's overall rate as your own result. If you quote your own pupils' pass rate, keep the records behind it, say which period it covers and update it, because the CAP Code requires evidence for objective claims.</p>\n<h3>What does the Local Presence Plan cost for a driving instructor, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/driving-instructor-marketing/#webpage",
          "url": "https://integrated.social/local/driving-instructor-marketing/",
          "name": "Driving instructor marketing for ADIs",
          "headline": "Driving instructor marketing for ADIs",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/driving-instructor-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/driving-instructor-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/driving-instructor-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Driving instructor marketing for UK ADIs and small driving schools: pupil enquiries, test-area pages, Google Business Profile, reviews and badge rules.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Check if you can become a driving instructor",
              "url": "https://www.gov.uk/become-a-driving-instructor"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Report an illegal driving instructor",
              "url": "https://www.gov.uk/report-an-illegal-driving-instructor"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Driving test, theory test and driving instructor data",
              "url": "https://www.gov.uk/government/collections/driving-tests-and-instructors-statistics"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Manage your service areas for service-area and hybrid businesses",
              "url": "https://support.google.com/business/answer/9157481"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/driving-instructor-marketing/#service",
          "name": "Driving instructor marketing for ADIs",
          "description": "Local Presence Plan for Driving Instructors: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for driving instructors",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Driving instructors"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/driving-instructor-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/driving-instructor-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/driving-instructor-marketing/#offer",
          "name": "Local Presence Plan for Driving Instructors",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/driving-instructor-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Driving instructor marketing for ADIs",
              "item": "https://integrated.social/local/driving-instructor-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/driving-instructor-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK driving instructor market their lessons?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Driving instructor marketing for ADIs is about helping learners and parents confirm the basics quickly: your pick-up areas, manual or automatic lessons, the test centres you know and your ADI badge. We keep these details consistent across Google, your website and AI tools, so local pupils can find you and see clearly what you offer."
              }
            },
            {
              "@type": "Question",
              "name": "Can we publish our pass rate?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We do not publish pass rates or pass-first-time claims on your pages. Results depend on each pupil, and outcome claims can mislead. Instead we describe how you teach, the formats you offer, the test centres you know and real pupil comments shared with consent, which answer what learners actually ask."
              }
            },
            {
              "@type": "Question",
              "name": "Who is allowed to charge for driving lessons?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "GOV.UK says only approved driving instructors on the ADI register, or trainee instructors holding a trainee licence, can charge for lessons in a car. ADIs display a green badge and trainees a pink one (see GOV.UK). We make sure your badge status is described accurately and point learners to the official checks."
              }
            },
            {
              "@type": "Question",
              "name": "How should a trainee driving instructor advertise?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "As a trainee driving instructor, never as a PDI, and through the training establishment named on the licence. GOV.UK trainee licence rules say trainees can only give instruction from that establishment. We keep wording accurate so marketing never suggests a trainee is a fully approved instructor."
              }
            },
            {
              "@type": "Question",
              "name": "Do we need separate pages for each town we cover?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. One clear section listing pick-up postcodes, schools and test centres serves learners better than many thin town pages. We keep that list in step with your Google profile service area, so search engines and AI tools see one consistent answer about where you teach."
              }
            },
            {
              "@type": "Question",
              "name": "Can I mention test centre pass rates in my marketing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can point learners to DVSA's published pass rates by test centre, but do not present a centre's overall rate as your own result. If you quote your own pupils' pass rate, keep the records behind it, say which period it covers and update it, because the CAP Code requires evidence for objective claims."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a driving instructor, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK driving instructor market their lessons?",
        "a": "Driving instructor marketing for ADIs is about helping learners and parents confirm the basics quickly: your pick-up areas, manual or automatic lessons, the test centres you know and your ADI badge. We keep these details consistent across Google, your website and AI tools, so local pupils can find you and see clearly what you offer."
      },
      {
        "q": "Can we publish our pass rate?",
        "a": "We do not publish pass rates or pass-first-time claims on your pages. Results depend on each pupil, and outcome claims can mislead. Instead we describe how you teach, the formats you offer, the test centres you know and real pupil comments shared with consent, which answer what learners actually ask."
      },
      {
        "q": "Who is allowed to charge for driving lessons?",
        "a": "GOV.UK says only approved driving instructors on the ADI register, or trainee instructors holding a trainee licence, can charge for lessons in a car. ADIs display a green badge and trainees a pink one (see GOV.UK). We make sure your badge status is described accurately and point learners to the official checks."
      },
      {
        "q": "How should a trainee driving instructor advertise?",
        "a": "As a trainee driving instructor, never as a PDI, and through the training establishment named on the licence. GOV.UK trainee licence rules say trainees can only give instruction from that establishment. We keep wording accurate so marketing never suggests a trainee is a fully approved instructor."
      },
      {
        "q": "Do we need separate pages for each town we cover?",
        "a": "No. One clear section listing pick-up postcodes, schools and test centres serves learners better than many thin town pages. We keep that list in step with your Google profile service area, so search engines and AI tools see one consistent answer about where you teach."
      },
      {
        "q": "Can I mention test centre pass rates in my marketing?",
        "a": "You can point learners to DVSA's published pass rates by test centre, but do not present a centre's overall rate as your own result. If you quote your own pupils' pass rate, keep the records behind it, say which period it covers and update it, because the CAP Code requires evidence for objective claims."
      },
      {
        "q": "What does the Local Presence Plan cost for a driving instructor, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/dry-cleaner-marketing/": {
    "section": "C-C8",
    "h1": "Dry cleaner marketing for wedding dress care",
    "title": "Dry cleaner marketing for wedding dress care",
    "description": "Dry cleaner marketing for UK specialists in wedding dress cleaning and preservation: Google Business Profile, enquiries, reviews and clear care terms.",
    "answerFirst": "Dry cleaner marketing for wedding dress care should make it easy for brides and families to trust you with a garment they cannot replace. We describe your cleaning, preservation, pressing and collection options accurately across Google, your website and AI tools, with clear care terms and no promises of results no cleaner can guarantee.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Under the Consumer Rights Act 2015, a service must be performed with reasonable care and skill. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2015/15/section/49\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Consumer Rights Act 2015, section 49 (reasonable care and skill)</a>.</li>\n<li>What you tell customers about a service, including on your website, can become a binding term of the contract. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2015/15/section/50\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Consumer Rights Act 2015, section 50</a>.</li>\n<li>Claims such as guaranteed stain removal need evidence under the CAP Code's rules on misleading advertising. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/03.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 3: Misleading advertising</a>.</li>\n<li>Google asks businesses to use their real-world name on their Business Profile, without extra keywords. Source: <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Guidelines for representing your business on Google</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK dry cleaner market wedding dress cleaning?</h3>\n<p>Dry cleaner marketing for wedding dress care should make it easy for brides and families to trust you with a garment they cannot replace. We describe your cleaning, preservation, pressing and collection options accurately across Google, your website and AI tools, with clear care terms and no promises of results no cleaner can guarantee.</p>\n<h3>Can we promise stains will come out?</h3>\n<p>No, and we would not write it. Some marks, dyes and fabrics cannot be treated without risk. Explain that you inspect each garment, discuss what may or may not be possible and agree an approach before cleaning. Honest expectations prevent complaints and give customers more confidence than a promise you might not keep.</p>\n<h3>How do we market wedding dress preservation?</h3>\n<p>Answer the questions brides actually ask: how soon after the wedding to bring the dress in, what the inspection involves, how the dress is boxed and how to store it. A dedicated page with clear steps and your collection options is more useful than a generic dry cleaning list.</p>\n<h3>What should our care terms say?</h3>\n<p>Keep them consistent with the Consumer Rights Act 2015: you carry out the service with reasonable care and skill, and what you tell customers about the service is binding. Explain your inspection at drop-off, how you record existing marks and what happens if something goes wrong. Plain terms prevent disputes.</p>\n<h3>Do care labels matter for our marketing?</h3>\n<p>They matter for setting expectations. Explain what happens when a garment has no care label, fragile trims or unusual materials, and that you may advise against cleaning. Clear guidance like this reduces disputes, shows expertise without overclaiming and gives customers practical information before they hand over something precious.</p>\n<h3>What turnaround should we give customers with an event date?</h3>\n<p>Give a typical range only if you can usually meet it, and say what can make it longer, such as heavy beading or stubborn marks. Customers with an event date need accurate information more than a fast promise. Keep the same wording on your Google profile and website, and update it if your workload changes.</p>\n<h3>What does the Local Presence Plan cost for a dry cleaner, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/dry-cleaner-marketing/#webpage",
          "url": "https://integrated.social/local/dry-cleaner-marketing/",
          "name": "Dry cleaner marketing for wedding dress care",
          "headline": "Dry cleaner marketing for wedding dress care",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/dry-cleaner-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/dry-cleaner-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/dry-cleaner-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Dry cleaner marketing for UK specialists in wedding dress cleaning and preservation: Google Business Profile, enquiries, reviews and clear care terms.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Consumer Rights Act 2015, section 49 (reasonable care and skill)",
              "url": "https://www.legislation.gov.uk/ukpga/2015/15/section/49"
            },
            {
              "@type": "CreativeWork",
              "name": "Consumer Rights Act 2015, section 50",
              "url": "https://www.legislation.gov.uk/ukpga/2015/15/section/50"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 3: Misleading advertising",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/03.html"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Guidelines for representing your business on Google",
              "url": "https://support.google.com/business/answer/3038177"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/dry-cleaner-marketing/#service",
          "name": "Dry cleaner marketing for wedding dress care",
          "description": "Local Presence Plan for Dry Cleaners: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for specialist dry cleaners",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Specialist dry cleaners"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/dry-cleaner-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/dry-cleaner-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/dry-cleaner-marketing/#offer",
          "name": "Local Presence Plan for Dry Cleaners",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/dry-cleaner-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Dry cleaner marketing for wedding dress care",
              "item": "https://integrated.social/local/dry-cleaner-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/dry-cleaner-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK dry cleaner market wedding dress cleaning?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Dry cleaner marketing for wedding dress care should make it easy for brides and families to trust you with a garment they cannot replace. We describe your cleaning, preservation, pressing and collection options accurately across Google, your website and AI tools, with clear care terms and no promises of results no cleaner can guarantee."
              }
            },
            {
              "@type": "Question",
              "name": "Can we promise stains will come out?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No, and we would not write it. Some marks, dyes and fabrics cannot be treated without risk. Explain that you inspect each garment, discuss what may or may not be possible and agree an approach before cleaning. Honest expectations prevent complaints and give customers more confidence than a promise you might not keep."
              }
            },
            {
              "@type": "Question",
              "name": "How do we market wedding dress preservation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Answer the questions brides actually ask: how soon after the wedding to bring the dress in, what the inspection involves, how the dress is boxed and how to store it. A dedicated page with clear steps and your collection options is more useful than a generic dry cleaning list."
              }
            },
            {
              "@type": "Question",
              "name": "What should our care terms say?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Keep them consistent with the Consumer Rights Act 2015: you carry out the service with reasonable care and skill, and what you tell customers about the service is binding. Explain your inspection at drop-off, how you record existing marks and what happens if something goes wrong. Plain terms prevent disputes."
              }
            },
            {
              "@type": "Question",
              "name": "Do care labels matter for our marketing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "They matter for setting expectations. Explain what happens when a garment has no care label, fragile trims or unusual materials, and that you may advise against cleaning. Clear guidance like this reduces disputes, shows expertise without overclaiming and gives customers practical information before they hand over something precious."
              }
            },
            {
              "@type": "Question",
              "name": "What turnaround should we give customers with an event date?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Give a typical range only if you can usually meet it, and say what can make it longer, such as heavy beading or stubborn marks. Customers with an event date need accurate information more than a fast promise. Keep the same wording on your Google profile and website, and update it if your workload changes."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a dry cleaner, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK dry cleaner market wedding dress cleaning?",
        "a": "Dry cleaner marketing for wedding dress care should make it easy for brides and families to trust you with a garment they cannot replace. We describe your cleaning, preservation, pressing and collection options accurately across Google, your website and AI tools, with clear care terms and no promises of results no cleaner can guarantee."
      },
      {
        "q": "Can we promise stains will come out?",
        "a": "No, and we would not write it. Some marks, dyes and fabrics cannot be treated without risk. Explain that you inspect each garment, discuss what may or may not be possible and agree an approach before cleaning. Honest expectations prevent complaints and give customers more confidence than a promise you might not keep."
      },
      {
        "q": "How do we market wedding dress preservation?",
        "a": "Answer the questions brides actually ask: how soon after the wedding to bring the dress in, what the inspection involves, how the dress is boxed and how to store it. A dedicated page with clear steps and your collection options is more useful than a generic dry cleaning list."
      },
      {
        "q": "What should our care terms say?",
        "a": "Keep them consistent with the Consumer Rights Act 2015: you carry out the service with reasonable care and skill, and what you tell customers about the service is binding. Explain your inspection at drop-off, how you record existing marks and what happens if something goes wrong. Plain terms prevent disputes."
      },
      {
        "q": "Do care labels matter for our marketing?",
        "a": "They matter for setting expectations. Explain what happens when a garment has no care label, fragile trims or unusual materials, and that you may advise against cleaning. Clear guidance like this reduces disputes, shows expertise without overclaiming and gives customers practical information before they hand over something precious."
      },
      {
        "q": "What turnaround should we give customers with an event date?",
        "a": "Give a typical range only if you can usually meet it, and say what can make it longer, such as heavy beading or stubborn marks. Customers with an event date need accurate information more than a fast promise. Keep the same wording on your Google profile and website, and update it if your workload changes."
      },
      {
        "q": "What does the Local Presence Plan cost for a dry cleaner, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/florist-marketing/": {
    "section": "C-C9",
    "h1": "Florist marketing for independent flower shops",
    "title": "Florist marketing for independent flower shops",
    "description": "Florist marketing for independent UK flower shops: wedding and funeral enquiries, local delivery pages, Google Business Profile and seasonal peaks.",
    "answerFirst": "Florist marketing for independent flower shops starts with answering what buyers need on the day: can you deliver to that postcode, by when, and for which occasion. We keep your opening hours, delivery cut-offs, wedding and funeral information consistent on Google, your website and AI tools, so local orders reach your shop rather than a faceless national site.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>The 14-day cancellation right does not apply to goods that deteriorate rapidly, such as fresh flowers. Source: <a href=\"https://www.legislation.gov.uk/uksi/2013/3134/regulation/28\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Consumer Contracts Regulations 2013, regulation 28</a>.</li>\n<li>What you tell buyers about delivery can become a binding term of the contract. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2015/15/section/50\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Consumer Rights Act 2015, section 50</a>.</li>\n<li>The CAP Code's rules on misleading advertising apply to delivery promises such as same-day delivery. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/03.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 3: Misleading advertising</a>.</li>\n<li>Google says local results depend mainly on relevance, distance and popularity. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should an independent UK florist market their shop?</h3>\n<p>Florist marketing for independent flower shops starts with answering what buyers need on the day: can you deliver to that postcode, by when, and for which occasion. We keep your opening hours, delivery cut-offs, wedding and funeral information consistent on Google, your website and AI tools, so local orders reach your shop rather than a faceless national site.</p>\n<h3>Should we promise same-day delivery?</h3>\n<p>Only say what you can do every day it is offered. State your cut-off time and delivery areas exactly, and explain what happens on peak days. What you tell customers about a service is binding under consumer law, so a clear, honest cut-off protects both your reputation and your customers.</p>\n<h3>How should a florist write about funeral flowers?</h3>\n<p>Keep the tone calm and practical. Explain how to order, lead times, how you work with funeral directors and how tributes are delivered. Grieving families want clear steps, not sales language, and plain information helps AI tools answer their questions accurately too.</p>\n<h3>How far ahead should we publish Valentine's Day and Mother's Day dates?</h3>\n<p>Earlier than feels natural. People start asking well before the day, so set order-ahead and last-order dates once you know your capacity, then keep them identical on your site, profile and posts. Consistency avoids disappointed customers and phone calls you cannot answer during the rush.</p>\n<h3>Do online flower orders come with a 14-day cancellation right?</h3>\n<p>Usually not for the flowers themselves. Regulation 28 of the Consumer Contracts Regulations 2013 excludes goods that deteriorate rapidly, which covers fresh flowers. Say this plainly on your order and delivery pages, alongside your delivery cut-offs and what happens if a delivery fails, so buyers know where they stand.</p>\n<h3>What does the Local Presence Plan cost for a florist, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/florist-marketing/#webpage",
          "url": "https://integrated.social/local/florist-marketing/",
          "name": "Florist marketing for independent flower shops",
          "headline": "Florist marketing for independent flower shops",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/florist-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/florist-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/florist-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Florist marketing for independent UK flower shops: wedding and funeral enquiries, local delivery pages, Google Business Profile and seasonal peaks.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Consumer Contracts Regulations 2013, regulation 28",
              "url": "https://www.legislation.gov.uk/uksi/2013/3134/regulation/28"
            },
            {
              "@type": "CreativeWork",
              "name": "Consumer Rights Act 2015, section 50",
              "url": "https://www.legislation.gov.uk/ukpga/2015/15/section/50"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 3: Misleading advertising",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/03.html"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/florist-marketing/#service",
          "name": "Florist marketing for independent flower shops",
          "description": "Local Presence Plan for Florists: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for florists",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Florists"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/florist-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/florist-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/florist-marketing/#offer",
          "name": "Local Presence Plan for Florists",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/florist-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Florist marketing for independent flower shops",
              "item": "https://integrated.social/local/florist-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/florist-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should an independent UK florist market their shop?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Florist marketing for independent flower shops starts with answering what buyers need on the day: can you deliver to that postcode, by when, and for which occasion. We keep your opening hours, delivery cut-offs, wedding and funeral information consistent on Google, your website and AI tools, so local orders reach your shop rather than a faceless national site."
              }
            },
            {
              "@type": "Question",
              "name": "Should we promise same-day delivery?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only say what you can do every day it is offered. State your cut-off time and delivery areas exactly, and explain what happens on peak days. What you tell customers about a service is binding under consumer law, so a clear, honest cut-off protects both your reputation and your customers."
              }
            },
            {
              "@type": "Question",
              "name": "How should a florist write about funeral flowers?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Keep the tone calm and practical. Explain how to order, lead times, how you work with funeral directors and how tributes are delivered. Grieving families want clear steps, not sales language, and plain information helps AI tools answer their questions accurately too."
              }
            },
            {
              "@type": "Question",
              "name": "How far ahead should we publish Valentine's Day and Mother's Day dates?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Earlier than feels natural. People start asking well before the day, so set order-ahead and last-order dates once you know your capacity, then keep them identical on your site, profile and posts. Consistency avoids disappointed customers and phone calls you cannot answer during the rush."
              }
            },
            {
              "@type": "Question",
              "name": "Do online flower orders come with a 14-day cancellation right?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Usually not for the flowers themselves. Regulation 28 of the Consumer Contracts Regulations 2013 excludes goods that deteriorate rapidly, which covers fresh flowers. Say this plainly on your order and delivery pages, alongside your delivery cut-offs and what happens if a delivery fails, so buyers know where they stand."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a florist, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should an independent UK florist market their shop?",
        "a": "Florist marketing for independent flower shops starts with answering what buyers need on the day: can you deliver to that postcode, by when, and for which occasion. We keep your opening hours, delivery cut-offs, wedding and funeral information consistent on Google, your website and AI tools, so local orders reach your shop rather than a faceless national site."
      },
      {
        "q": "Should we promise same-day delivery?",
        "a": "Only say what you can do every day it is offered. State your cut-off time and delivery areas exactly, and explain what happens on peak days. What you tell customers about a service is binding under consumer law, so a clear, honest cut-off protects both your reputation and your customers."
      },
      {
        "q": "How should a florist write about funeral flowers?",
        "a": "Keep the tone calm and practical. Explain how to order, lead times, how you work with funeral directors and how tributes are delivered. Grieving families want clear steps, not sales language, and plain information helps AI tools answer their questions accurately too."
      },
      {
        "q": "How far ahead should we publish Valentine's Day and Mother's Day dates?",
        "a": "Earlier than feels natural. People start asking well before the day, so set order-ahead and last-order dates once you know your capacity, then keep them identical on your site, profile and posts. Consistency avoids disappointed customers and phone calls you cannot answer during the rush."
      },
      {
        "q": "Do online flower orders come with a 14-day cancellation right?",
        "a": "Usually not for the flowers themselves. Regulation 28 of the Consumer Contracts Regulations 2013 excludes goods that deteriorate rapidly, which covers fresh flowers. Say this plainly on your order and delivery pages, alongside your delivery cut-offs and what happens if a delivery fails, so buyers know where they stand."
      },
      {
        "q": "What does the Local Presence Plan cost for a florist, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/garage-marketing/": {
    "section": "C-C10",
    "h1": "Garage marketing for MOT, servicing and tyres",
    "title": "Garage marketing for MOT, servicing and tyres",
    "description": "Garage marketing for independent UK garages: MOT and servicing bookings, Google Business Profile categories, reviews and reminders. Written for owners.",
    "answerFirst": "Garage marketing in the UK starts with the facts motorists check before booking: your MOT classes and fee, servicing options, tyre brands, opening hours and reviews. Keep those facts the same on your Google Business Profile and website, make online booking easy, and use reminders customers have agreed to receive.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>The maximum MOT fee for a car is £54.85; garages can charge less, and there is no VAT on the MOT fee. Source: <a href=\"https://www.gov.uk/getting-an-mot/mot-test-fees\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Getting an MOT, MOT costs</a>.</li>\n<li>Motorists can sign up on GOV.UK for free MOT reminders by text or email. Source: <a href=\"https://www.gov.uk/get-stop-view-mot-reminder\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Get, view or stop MOT reminders</a>.</li>\n<li>Anyone can check a vehicle's MOT history, including advisories, on GOV.UK. Source: <a href=\"https://www.gov.uk/check-mot-history\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Check the MOT history of a vehicle</a>.</li>\n<li>The Motor Ombudsman runs codes of practice and a dispute service for garages that sign up. Source: <a href=\"https://www.themotorombudsman.org/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Motor Ombudsman</a>.</li>\n<li>Google says local results depend mainly on relevance, distance and popularity. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>What does garage marketing involve for an independent UK workshop?</h3>\n<p>Garage marketing in the UK starts with the facts motorists check before booking: your MOT classes and fee, servicing options, tyre brands, opening hours and reviews. Keep those facts the same on your Google Business Profile and website, make online booking easy, and use reminders customers have agreed to receive.</p>\n<h3>Should we send our own MOT reminders when GOV.UK offers a free one?</h3>\n<p>The free GOV.UK reminder tells motorists their MOT is due, but not where to book it. Your own reminder, sent to customers who have agreed to hear from you, can offer a slot at your garage and pair it with a service. We set up the wording and timing; you keep control of the customer list.</p>\n<h3>How should we describe our DVSA authorisation?</h3>\n<p>Accurately and plainly: say that you are an authorised MOT test centre and which vehicle classes you test, such as class 4 for cars. Do not imply that the DVSA endorses your other work. We check that your website, listings and booking pages all use the same wording.</p>\n<h3>How do we reassure customers worried about being overcharged?</h3>\n<p>Show how you work before they arrive: how you quote, when you phone before doing extra work and what an interim or full service includes. Reply calmly and specifically to reviews that mention price. Clear information like this gives customers and AI tools something concrete to repeat instead of guessing.</p>\n<h3>Can we compete without naming the fast-fit chains?</h3>\n<p>Yes, and it is the safer route. Comparative advertising that names competitors has to meet strict CAP Code rules. It is usually stronger to describe what you do well: who works on the car, the brands you stock and any warranty you give. We write to those facts and leave rivals out.</p>\n<h3>Should we say which makes, hybrids or electric vehicles we work on?</h3>\n<p>Yes, if it's accurate. Drivers often want to know whether you can handle their make or an electric or hybrid car. List only what your team is trained and equipped for, and update it when that changes. We add it to your Google profile and website in the same words, so drivers get one clear answer.</p>\n<h3>What does the Local Presence Plan cost for a garage, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleHtml": "<section id=\"ops-card-garage\" aria-labelledby=\"ops-card-garage-h\" data-event=\"ops_card_view\" data-card=\"garage\">\n<h2 id=\"ops-card-garage-h\">30-day garage visibility and booking operations card</h2>\n<table>\n<tbody>\n<tr><th scope=\"row\">This month's goal</th><td>Make MOT and service booking straightforward, with reminders customers have agreed to.</td></tr>\n<tr><th scope=\"row\">Who owns it</th><td>You or your service manager.</td></tr>\n<tr><th scope=\"row\">Public fact to verify</th><td>Your MOT classes and MOT price, the vehicle types you work on, what you diagnose and repair, and your hours.</td></tr>\n<tr><th scope=\"row\">Customer question to answer</th><td>Can you test my car this week, and what will it cost?</td></tr>\n<tr><th scope=\"row\">Booking friction to remove</th><td>Phone-only booking during the hours you are busiest, or an MOT price that differs between Google and your website.</td></tr>\n<tr><th scope=\"row\">Official sources to check</th><td><a href=\"https://www.gov.uk/getting-an-mot/mot-test-fees\" target=\"_blank\" rel=\"noopener\">GOV.UK: Getting an MOT, MOT costs</a>; <a href=\"https://www.gov.uk/get-stop-view-mot-reminder\" target=\"_blank\" rel=\"noopener\">GOV.UK: Get, view or stop MOT reminders</a>; <a href=\"https://www.gov.uk/check-mot-history\" target=\"_blank\" rel=\"noopener\">GOV.UK: Check the MOT history of a vehicle</a>; <a href=\"https://www.themotorombudsman.org/\" target=\"_blank\" rel=\"noopener\">The Motor Ombudsman</a></td></tr>\n<tr><th scope=\"row\">What not to claim</th><td>False urgency (\"your car may be illegal\"), a guaranteed pass, or membership of a scheme you have not joined.</td></tr>\n<tr><th scope=\"row\">Review date</th><td>Set a date 30 days from today and run the card again.</td></tr>\n</tbody>\n</table>\n<h3>Five public checks</h3>\n<ul>\n<li>Your MOT price is stated, and it is no higher than the government maximum for each class.</li>\n<li>Your MOT classes and vehicle types match on Google and your website.</li>\n<li>You say what you diagnose and repair, and what you refer elsewhere.</li>\n<li>Your hours on Google match the hours you answer the phone.</li>\n<li>Your website mentions the free GOV.UK MOT reminder service and the MOT history check.</li>\n</ul>\n<h3>Booking-route check</h3>\n<p>A driver can book an MOT online or by phone and receives a written confirmation. Reminders go only to customers who agreed to receive them, by the method they chose.</p>\n<h3>Review and response check</h3>\n<p>Reply to every new review within a time you set. Never ask for reviews only from customers who passed or paid; ask everyone in the same way.</p>\n<h3>What this card cannot promise</h3>\n<p>The card helps your public information stay accurate. It does not promise more bookings, a place in Google Maps or a mention in an AI answer. For the checks every local business should run, see the <a href=\"/local/#uk-local-visibility-evidence-standard\">UK Local Visibility Evidence Standard</a>.</p>\n</section>",
    "moduleBeforeHeading": "Plans, prices and next step",
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/garage-marketing/#webpage",
          "url": "https://integrated.social/local/garage-marketing/",
          "name": "Garage marketing for MOT, servicing and tyres",
          "headline": "Garage marketing for MOT, servicing and tyres",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/garage-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/garage-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/garage-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Garage marketing for independent UK garages: MOT and servicing bookings, Google Business Profile categories, reviews and reminders. Written for owners.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Getting an MOT, MOT costs",
              "url": "https://www.gov.uk/getting-an-mot/mot-test-fees"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Get, view or stop MOT reminders",
              "url": "https://www.gov.uk/get-stop-view-mot-reminder"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Check the MOT history of a vehicle",
              "url": "https://www.gov.uk/check-mot-history"
            },
            {
              "@type": "CreativeWork",
              "name": "The Motor Ombudsman",
              "url": "https://www.themotorombudsman.org/"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/garage-marketing/#service",
          "name": "Garage marketing for MOT, servicing and tyres",
          "description": "Local Presence Plan for Garages: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for independent MOT, servicing and tyre centres",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent garages, MOT test centres and tyre centres"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/garage-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/garage-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/garage-marketing/#offer",
          "name": "Local Presence Plan for Garages",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/garage-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Garage marketing for MOT, servicing and tyres",
              "item": "https://integrated.social/local/garage-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/garage-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What does garage marketing involve for an independent UK workshop?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Garage marketing in the UK starts with the facts motorists check before booking: your MOT classes and fee, servicing options, tyre brands, opening hours and reviews. Keep those facts the same on your Google Business Profile and website, make online booking easy, and use reminders customers have agreed to receive."
              }
            },
            {
              "@type": "Question",
              "name": "Should we send our own MOT reminders when GOV.UK offers a free one?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The free GOV.UK reminder tells motorists their MOT is due, but not where to book it. Your own reminder, sent to customers who have agreed to hear from you, can offer a slot at your garage and pair it with a service. We set up the wording and timing; you keep control of the customer list."
              }
            },
            {
              "@type": "Question",
              "name": "How should we describe our DVSA authorisation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Accurately and plainly: say that you are an authorised MOT test centre and which vehicle classes you test, such as class 4 for cars. Do not imply that the DVSA endorses your other work. We check that your website, listings and booking pages all use the same wording."
              }
            },
            {
              "@type": "Question",
              "name": "How do we reassure customers worried about being overcharged?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Show how you work before they arrive: how you quote, when you phone before doing extra work and what an interim or full service includes. Reply calmly and specifically to reviews that mention price. Clear information like this gives customers and AI tools something concrete to repeat instead of guessing."
              }
            },
            {
              "@type": "Question",
              "name": "Can we compete without naming the fast-fit chains?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, and it is the safer route. Comparative advertising that names competitors has to meet strict CAP Code rules. It is usually stronger to describe what you do well: who works on the car, the brands you stock and any warranty you give. We write to those facts and leave rivals out."
              }
            },
            {
              "@type": "Question",
              "name": "Should we say which makes, hybrids or electric vehicles we work on?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, if it's accurate. Drivers often want to know whether you can handle their make or an electric or hybrid car. List only what your team is trained and equipped for, and update it when that changes. We add it to your Google profile and website in the same words, so drivers get one clear answer."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a garage, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What does garage marketing involve for an independent UK workshop?",
        "a": "Garage marketing in the UK starts with the facts motorists check before booking: your MOT classes and fee, servicing options, tyre brands, opening hours and reviews. Keep those facts the same on your Google Business Profile and website, make online booking easy, and use reminders customers have agreed to receive."
      },
      {
        "q": "Should we send our own MOT reminders when GOV.UK offers a free one?",
        "a": "The free GOV.UK reminder tells motorists their MOT is due, but not where to book it. Your own reminder, sent to customers who have agreed to hear from you, can offer a slot at your garage and pair it with a service. We set up the wording and timing; you keep control of the customer list."
      },
      {
        "q": "How should we describe our DVSA authorisation?",
        "a": "Accurately and plainly: say that you are an authorised MOT test centre and which vehicle classes you test, such as class 4 for cars. Do not imply that the DVSA endorses your other work. We check that your website, listings and booking pages all use the same wording."
      },
      {
        "q": "How do we reassure customers worried about being overcharged?",
        "a": "Show how you work before they arrive: how you quote, when you phone before doing extra work and what an interim or full service includes. Reply calmly and specifically to reviews that mention price. Clear information like this gives customers and AI tools something concrete to repeat instead of guessing."
      },
      {
        "q": "Can we compete without naming the fast-fit chains?",
        "a": "Yes, and it is the safer route. Comparative advertising that names competitors has to meet strict CAP Code rules. It is usually stronger to describe what you do well: who works on the car, the brands you stock and any warranty you give. We write to those facts and leave rivals out."
      },
      {
        "q": "Should we say which makes, hybrids or electric vehicles we work on?",
        "a": "Yes, if it's accurate. Drivers often want to know whether you can handle their make or an electric or hybrid car. List only what your team is trained and equipped for, and update it when that changes. We add it to your Google profile and website in the same words, so drivers get one clear answer."
      },
      {
        "q": "What does the Local Presence Plan cost for a garage, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/gym-marketing/": {
    "section": "C-C11",
    "h1": "Gym marketing for independent gyms and studios",
    "title": "Gym marketing for independent gyms and studios",
    "description": "Gym marketing for independent UK gyms and studios: membership enquiries, free trials, class timetables, Google Business Profile and reviews.",
    "answerFirst": "Gym marketing for an independent gym or studio is about answering what people check before they join: your timetable, membership options, trial or day pass, facilities and who coaches. We keep those details accurate across your Google Business Profile, website and the AI tools people ask, so enquiries reach you while interest is high.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Weight-loss and slimming claims are tightly restricted under section 13 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/13.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 13: Weight control and slimming</a>.</li>\n<li>CIMSPA is the chartered professional body for sport and physical activity; state only the qualifications each coach actually holds. Source: <a href=\"https://cimspa.co.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CIMSPA (Chartered Institute for the Management of Sport and Physical Activity)</a>.</li>\n<li>Paid fitness training in the Royal Parks needs a licence from The Royal Parks. Source: <a href=\"https://www.royalparks.org.uk/commercial-licences/fitness-training-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Royal Parks: Fitness training licences</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should an independent UK gym market its memberships?</h3>\n<p>Gym marketing for an independent gym or studio is about answering what people check before they join: your timetable, membership options, trial or day pass, facilities and who coaches. We keep those details accurate across your Google Business Profile, website and the AI tools people ask, so enquiries reach you while interest is high.</p>\n<h3>Should we offer a free trial?</h3>\n<p>That is your call, based on capacity and how members usually join. If you run one, describe it precisely: its length, what is included and what happens at the end. Vague trial terms frustrate people and invite complaints. We make the trial page, listing and booking form say exactly the same thing.</p>\n<h3>How do we win back lapsed members?</h3>\n<p>Contact former members who agreed to hear from you with something specific: a new class, a returning coach or a changed timetable. Generic messages are easy to ignore. We help you time the contact around your own seasonal pattern, such as before January or after the summer break.</p>\n<h3>Which coaching credentials should our team page show?</h3>\n<p>Show the real ones. People choosing a gym want to know who will coach them, so give each trainer a short profile covering the qualifications your coaches hold and what each one specialises in. A profile that lists an award nobody holds is misleading, so we only publish what you confirm.</p>\n<h3>Can we advertise weight-loss results?</h3>\n<p>Be very careful. Weight-loss and body-transformation claims need solid evidence under the CAP Code, and one member's result is not a typical result. We keep pages to what you can show, such as classes, coaching approach and facilities, and avoid promised outcomes.</p>\n<h3>We're open 24 hours but not always staffed. How should we show that?</h3>\n<p>Show both clearly: your access hours and your staffed hours. People searching late at night want to know whether they can get in, and new members want to know when someone will be there to help. We add both sets of hours to your Google profile, then mirror them exactly on your website and listings.</p>\n<h3>What does the Local Presence Plan cost for a gym, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/gym-marketing/#webpage",
          "url": "https://integrated.social/local/gym-marketing/",
          "name": "Gym marketing for independent gyms and studios",
          "headline": "Gym marketing for independent gyms and studios",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/gym-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/gym-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/gym-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Gym marketing for independent UK gyms and studios: membership enquiries, free trials, class timetables, Google Business Profile and reviews.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 13: Weight control and slimming",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/13.html"
            },
            {
              "@type": "CreativeWork",
              "name": "CIMSPA (Chartered Institute for the Management of Sport and Physical Activity)",
              "url": "https://cimspa.co.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "The Royal Parks: Fitness training licences",
              "url": "https://www.royalparks.org.uk/commercial-licences/fitness-training-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/gym-marketing/#service",
          "name": "Gym marketing for independent gyms and studios",
          "description": "Local Presence Plan for Gyms: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for independent gyms and fitness studios",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent gyms and fitness studios"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/gym-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/gym-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/gym-marketing/#offer",
          "name": "Local Presence Plan for Gyms",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/gym-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Gym marketing for independent gyms and studios",
              "item": "https://integrated.social/local/gym-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/gym-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should an independent UK gym market its memberships?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Gym marketing for an independent gym or studio is about answering what people check before they join: your timetable, membership options, trial or day pass, facilities and who coaches. We keep those details accurate across your Google Business Profile, website and the AI tools people ask, so enquiries reach you while interest is high."
              }
            },
            {
              "@type": "Question",
              "name": "Should we offer a free trial?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "That is your call, based on capacity and how members usually join. If you run one, describe it precisely: its length, what is included and what happens at the end. Vague trial terms frustrate people and invite complaints. We make the trial page, listing and booking form say exactly the same thing."
              }
            },
            {
              "@type": "Question",
              "name": "How do we win back lapsed members?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Contact former members who agreed to hear from you with something specific: a new class, a returning coach or a changed timetable. Generic messages are easy to ignore. We help you time the contact around your own seasonal pattern, such as before January or after the summer break."
              }
            },
            {
              "@type": "Question",
              "name": "Which coaching credentials should our team page show?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Show the real ones. People choosing a gym want to know who will coach them, so give each trainer a short profile covering the qualifications your coaches hold and what each one specialises in. A profile that lists an award nobody holds is misleading, so we only publish what you confirm."
              }
            },
            {
              "@type": "Question",
              "name": "Can we advertise weight-loss results?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Be very careful. Weight-loss and body-transformation claims need solid evidence under the CAP Code, and one member's result is not a typical result. We keep pages to what you can show, such as classes, coaching approach and facilities, and avoid promised outcomes."
              }
            },
            {
              "@type": "Question",
              "name": "We're open 24 hours but not always staffed. How should we show that?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Show both clearly: your access hours and your staffed hours. People searching late at night want to know whether they can get in, and new members want to know when someone will be there to help. We add both sets of hours to your Google profile, then mirror them exactly on your website and listings."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a gym, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should an independent UK gym market its memberships?",
        "a": "Gym marketing for an independent gym or studio is about answering what people check before they join: your timetable, membership options, trial or day pass, facilities and who coaches. We keep those details accurate across your Google Business Profile, website and the AI tools people ask, so enquiries reach you while interest is high."
      },
      {
        "q": "Should we offer a free trial?",
        "a": "That is your call, based on capacity and how members usually join. If you run one, describe it precisely: its length, what is included and what happens at the end. Vague trial terms frustrate people and invite complaints. We make the trial page, listing and booking form say exactly the same thing."
      },
      {
        "q": "How do we win back lapsed members?",
        "a": "Contact former members who agreed to hear from you with something specific: a new class, a returning coach or a changed timetable. Generic messages are easy to ignore. We help you time the contact around your own seasonal pattern, such as before January or after the summer break."
      },
      {
        "q": "Which coaching credentials should our team page show?",
        "a": "Show the real ones. People choosing a gym want to know who will coach them, so give each trainer a short profile covering the qualifications your coaches hold and what each one specialises in. A profile that lists an award nobody holds is misleading, so we only publish what you confirm."
      },
      {
        "q": "Can we advertise weight-loss results?",
        "a": "Be very careful. Weight-loss and body-transformation claims need solid evidence under the CAP Code, and one member's result is not a typical result. We keep pages to what you can show, such as classes, coaching approach and facilities, and avoid promised outcomes."
      },
      {
        "q": "We're open 24 hours but not always staffed. How should we show that?",
        "a": "Show both clearly: your access hours and your staffed hours. People searching late at night want to know whether they can get in, and new members want to know when someone will be there to help. We add both sets of hours to your Google profile, then mirror them exactly on your website and listings."
      },
      {
        "q": "What does the Local Presence Plan cost for a gym, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/pilates-studio-marketing/": {
    "section": "C-C12",
    "h1": "Pilates studio marketing for reformer and mat",
    "title": "Pilates studio marketing for reformer and mat",
    "description": "Pilates studio marketing for UK reformer and mat studios: class bookings, intro offers, Google Business Profile, instructor credentials and reviews.",
    "answerFirst": "Pilates studio marketing works when new clients can see exactly what they are booking: reformer or mat, class level, group size, instructor and intro offer. We keep your class schedule, class packs and booking rules consistent across Google, your website and AI tools, so newcomers can see how to book a second class.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Health claims in ads, such as treating back pain, need evidence under section 12 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n<li>Paid outdoor classes in the Royal Parks need a fitness training licence from The Royal Parks. Source: <a href=\"https://www.royalparks.org.uk/commercial-licences/fitness-training-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Royal Parks: Fitness training licences</a>.</li>\n<li>Google says local results depend mainly on relevance, distance and popularity. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK pilates studio market its classes?</h3>\n<p>Pilates studio marketing works when new clients can see exactly what they are booking: reformer or mat, class level, group size, instructor and intro offer. We keep your class schedule, class packs and booking rules consistent across Google, your website and AI tools, so newcomers can see how to book a second class.</p>\n<h3>Should we lead with reformer or mat classes?</h3>\n<p>Lead with whatever has spare capacity and suits newcomers in your studio. Reformer and mat clients search differently, so each format needs its own page with class size, level and equipment explained. We map the questions people ask about each and make sure your timetable and listings match.</p>\n<h3>How do we turn intro offers into regular clients?</h3>\n<p>Make the step after the intro offer obvious: which class pack or membership suits someone who enjoyed their first classes, and how to book it. Follow up only with clients who agreed to hear from you. We plan the offer page and follow-up wording; you set the prices.</p>\n<h3>Can we say Pilates helps with back pain?</h3>\n<p>Not as a promise. Health claims need robust evidence under the CAP Code, and individual results vary. Describe what happens in class and who it suits, and invite clients with health conditions to talk to their instructor or GP first. We keep pages factual and refer health questions on.</p>\n<h3>Do instructor profiles matter for a Pilates studio?</h3>\n<p>Yes. Many clients choose a studio for a particular instructor, especially for pre- and postnatal or rehabilitation-adjacent classes. Each profile should name real training and the classes that instructor leads. Accurate profiles also give search engines and AI tools concrete facts about who teaches what.</p>\n<h3>We take bookings through an app. Does the Local Presence Plan still help?</h3>\n<p>Yes. Your booking app handles the booking, but many clients first find you through Google or your website. We make sure the booking link on your Google profile and website goes to the right place and works on a phone. We also keep your studio name, address and hours the same across the main directories.</p>\n<h3>What does the Local Presence Plan cost for a Pilates studio, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/pilates-studio-marketing/#webpage",
          "url": "https://integrated.social/local/pilates-studio-marketing/",
          "name": "Pilates studio marketing for reformer and mat",
          "headline": "Pilates studio marketing for reformer and mat",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/pilates-studio-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/pilates-studio-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/pilates-studio-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Pilates studio marketing for UK reformer and mat studios: class bookings, intro offers, Google Business Profile, instructor credentials and reviews.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            },
            {
              "@type": "CreativeWork",
              "name": "The Royal Parks: Fitness training licences",
              "url": "https://www.royalparks.org.uk/commercial-licences/fitness-training-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/pilates-studio-marketing/#service",
          "name": "Pilates studio marketing for reformer and mat",
          "description": "Local Presence Plan for Pilates Studios: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for Pilates studios",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent Pilates studios (reformer and mat)"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/pilates-studio-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/pilates-studio-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/pilates-studio-marketing/#offer",
          "name": "Local Presence Plan for Pilates Studios",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/pilates-studio-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Pilates studio marketing for reformer and mat",
              "item": "https://integrated.social/local/pilates-studio-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/pilates-studio-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK pilates studio market its classes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Pilates studio marketing works when new clients can see exactly what they are booking: reformer or mat, class level, group size, instructor and intro offer. We keep your class schedule, class packs and booking rules consistent across Google, your website and AI tools, so newcomers can see how to book a second class."
              }
            },
            {
              "@type": "Question",
              "name": "Should we lead with reformer or mat classes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Lead with whatever has spare capacity and suits newcomers in your studio. Reformer and mat clients search differently, so each format needs its own page with class size, level and equipment explained. We map the questions people ask about each and make sure your timetable and listings match."
              }
            },
            {
              "@type": "Question",
              "name": "How do we turn intro offers into regular clients?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Make the step after the intro offer obvious: which class pack or membership suits someone who enjoyed their first classes, and how to book it. Follow up only with clients who agreed to hear from you. We plan the offer page and follow-up wording; you set the prices."
              }
            },
            {
              "@type": "Question",
              "name": "Can we say Pilates helps with back pain?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Not as a promise. Health claims need robust evidence under the CAP Code, and individual results vary. Describe what happens in class and who it suits, and invite clients with health conditions to talk to their instructor or GP first. We keep pages factual and refer health questions on."
              }
            },
            {
              "@type": "Question",
              "name": "Do instructor profiles matter for a Pilates studio?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Many clients choose a studio for a particular instructor, especially for pre- and postnatal or rehabilitation-adjacent classes. Each profile should name real training and the classes that instructor leads. Accurate profiles also give search engines and AI tools concrete facts about who teaches what."
              }
            },
            {
              "@type": "Question",
              "name": "We take bookings through an app. Does the Local Presence Plan still help?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Your booking app handles the booking, but many clients first find you through Google or your website. We make sure the booking link on your Google profile and website goes to the right place and works on a phone. We also keep your studio name, address and hours the same across the main directories."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a Pilates studio, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK pilates studio market its classes?",
        "a": "Pilates studio marketing works when new clients can see exactly what they are booking: reformer or mat, class level, group size, instructor and intro offer. We keep your class schedule, class packs and booking rules consistent across Google, your website and AI tools, so newcomers can see how to book a second class."
      },
      {
        "q": "Should we lead with reformer or mat classes?",
        "a": "Lead with whatever has spare capacity and suits newcomers in your studio. Reformer and mat clients search differently, so each format needs its own page with class size, level and equipment explained. We map the questions people ask about each and make sure your timetable and listings match."
      },
      {
        "q": "How do we turn intro offers into regular clients?",
        "a": "Make the step after the intro offer obvious: which class pack or membership suits someone who enjoyed their first classes, and how to book it. Follow up only with clients who agreed to hear from you. We plan the offer page and follow-up wording; you set the prices."
      },
      {
        "q": "Can we say Pilates helps with back pain?",
        "a": "Not as a promise. Health claims need robust evidence under the CAP Code, and individual results vary. Describe what happens in class and who it suits, and invite clients with health conditions to talk to their instructor or GP first. We keep pages factual and refer health questions on."
      },
      {
        "q": "Do instructor profiles matter for a Pilates studio?",
        "a": "Yes. Many clients choose a studio for a particular instructor, especially for pre- and postnatal or rehabilitation-adjacent classes. Each profile should name real training and the classes that instructor leads. Accurate profiles also give search engines and AI tools concrete facts about who teaches what."
      },
      {
        "q": "We take bookings through an app. Does the Local Presence Plan still help?",
        "a": "Yes. Your booking app handles the booking, but many clients first find you through Google or your website. We make sure the booking link on your Google profile and website goes to the right place and works on a phone. We also keep your studio name, address and hours the same across the main directories."
      },
      {
        "q": "What does the Local Presence Plan cost for a Pilates studio, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/restaurant-marketing/": {
    "section": "C-C13",
    "h1": "Restaurant marketing for independent restaurants",
    "title": "Restaurant marketing for independent restaurants",
    "description": "Restaurant marketing for UK independents: direct table bookings, Google Business Profile, menus, allergen information and reviews. Owner-first.",
    "answerFirst": "Restaurant marketing for an independent restaurant is about making the decision easy for diners: accurate opening times, a current menu with allergen guidance, a reservation link that works and reviews you respond to. We make sure Google, Maps, your website and AI tools show the same facts, and that diners can book with you directly.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Food businesses must give customers information about any of the 14 listed allergens used as ingredients. Source: <a href=\"https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Allergen guidance for food businesses</a>.</li>\n<li>Food hygiene ratings are published online for businesses in England, Wales and Northern Ireland. Source: <a href=\"https://ratings.food.gov.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Food hygiene ratings search</a>; <a href=\"https://www.gov.uk/government/publications/food-hygiene-rating-scheme-fhrs-guidance-for-businesses/food-hygiene-rating-scheme-fhrs-guidance-for-businesses\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Food Hygiene Rating Scheme guidance for businesses</a>.</li>\n<li>Selling alcohol needs a premises licence from the council, which sets your licensed hours. Source: <a href=\"https://www.gov.uk/find-licences/premises-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Premises licence (England and Wales)</a>.</li>\n<li>Tables and chairs on the pavement need a pavement licence from the council. Source: <a href=\"https://www.gov.uk/government/publications/pavement-licences-guidance\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Pavement licences guidance</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should an independent UK restaurant market itself?</h3>\n<p>Restaurant marketing for an independent restaurant is about making the decision easy for diners: accurate opening times, a current menu with allergen guidance, a reservation link that works and reviews you respond to. We make sure Google, Maps, your website and AI tools show the same facts, and that diners can book with you directly.</p>\n<h3>How do we move bookings from platforms to our own site?</h3>\n<p>Make your own route the easiest: a reservation button on your Google Business Profile pointing to your system, a clear booking page on your site and confirmation emails inviting diners to book direct next time. Platforms can still bring new diners; the balance between them is your commercial call.</p>\n<h3>What allergen information should be on our website?</h3>\n<p>Explain how allergen information is provided: that staff can advise on the allergens UK law requires you to declare and where diners can see full details. Keep online menus in step with the kitchen. We never describe a dish as allergen-free unless you confirm it in writing, because that claim carries real risk for diners and for you.</p>\n<h3>How can we fill quiet nights?</h3>\n<p>Start with what you already offer on those nights, such as set menus, private dining or supper clubs, and give each its own page and listing post. Then contact past diners who agreed to hear from you. We report which nights the work targets and what changed, without promising covers.</p>\n<h3>How should a restaurant handle negative reviews?</h3>\n<p>Respond promptly and specifically to detailed or critical reviews, say what you have changed, and take the conversation offline if it needs detail. Fake reviews and reviews bought with hidden incentives are banned under UK consumer law. Honest replies show diners you listen and give AI tools fair context.</p>\n<h3>Can you help us promote private dining and group bookings?</h3>\n<p>Yes, within the plan. Your Google profile and website can say whether you take private hire or large groups, how many people you can seat and how to enquire. Your weekly LinkedIn post can mention it to local businesses planning team meals. We keep the details factual, so the enquiries you receive are ones you can actually take.</p>\n<h3>What does the Local Presence Plan cost for a restaurant, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/restaurant-marketing/#webpage",
          "url": "https://integrated.social/local/restaurant-marketing/",
          "name": "Restaurant marketing for independent restaurants",
          "headline": "Restaurant marketing for independent restaurants",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/restaurant-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/restaurant-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/restaurant-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Restaurant marketing for UK independents: direct table bookings, Google Business Profile, menus, allergen information and reviews. Owner-first.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "FSA: Allergen guidance for food businesses",
              "url": "https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Food hygiene ratings search",
              "url": "https://ratings.food.gov.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Food Hygiene Rating Scheme guidance for businesses",
              "url": "https://www.gov.uk/government/publications/food-hygiene-rating-scheme-fhrs-guidance-for-businesses/food-hygiene-rating-scheme-fhrs-guidance-for-businesses"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Premises licence (England and Wales)",
              "url": "https://www.gov.uk/find-licences/premises-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Pavement licences guidance",
              "url": "https://www.gov.uk/government/publications/pavement-licences-guidance"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/restaurant-marketing/#service",
          "name": "Restaurant marketing for independent restaurants",
          "description": "Local Presence Plan for Restaurants: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for independent restaurants",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent restaurants"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/restaurant-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/restaurant-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/restaurant-marketing/#offer",
          "name": "Local Presence Plan for Restaurants",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/restaurant-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Restaurant marketing for independent restaurants",
              "item": "https://integrated.social/local/restaurant-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/restaurant-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should an independent UK restaurant market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Restaurant marketing for an independent restaurant is about making the decision easy for diners: accurate opening times, a current menu with allergen guidance, a reservation link that works and reviews you respond to. We make sure Google, Maps, your website and AI tools show the same facts, and that diners can book with you directly."
              }
            },
            {
              "@type": "Question",
              "name": "How do we move bookings from platforms to our own site?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Make your own route the easiest: a reservation button on your Google Business Profile pointing to your system, a clear booking page on your site and confirmation emails inviting diners to book direct next time. Platforms can still bring new diners; the balance between them is your commercial call."
              }
            },
            {
              "@type": "Question",
              "name": "What allergen information should be on our website?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Explain how allergen information is provided: that staff can advise on the allergens UK law requires you to declare and where diners can see full details. Keep online menus in step with the kitchen. We never describe a dish as allergen-free unless you confirm it in writing, because that claim carries real risk for diners and for you."
              }
            },
            {
              "@type": "Question",
              "name": "How can we fill quiet nights?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Start with what you already offer on those nights, such as set menus, private dining or supper clubs, and give each its own page and listing post. Then contact past diners who agreed to hear from you. We report which nights the work targets and what changed, without promising covers."
              }
            },
            {
              "@type": "Question",
              "name": "How should a restaurant handle negative reviews?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Respond promptly and specifically to detailed or critical reviews, say what you have changed, and take the conversation offline if it needs detail. Fake reviews and reviews bought with hidden incentives are banned under UK consumer law. Honest replies show diners you listen and give AI tools fair context."
              }
            },
            {
              "@type": "Question",
              "name": "Can you help us promote private dining and group bookings?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, within the plan. Your Google profile and website can say whether you take private hire or large groups, how many people you can seat and how to enquire. Your weekly LinkedIn post can mention it to local businesses planning team meals. We keep the details factual, so the enquiries you receive are ones you can actually take."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a restaurant, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should an independent UK restaurant market itself?",
        "a": "Restaurant marketing for an independent restaurant is about making the decision easy for diners: accurate opening times, a current menu with allergen guidance, a reservation link that works and reviews you respond to. We make sure Google, Maps, your website and AI tools show the same facts, and that diners can book with you directly."
      },
      {
        "q": "How do we move bookings from platforms to our own site?",
        "a": "Make your own route the easiest: a reservation button on your Google Business Profile pointing to your system, a clear booking page on your site and confirmation emails inviting diners to book direct next time. Platforms can still bring new diners; the balance between them is your commercial call."
      },
      {
        "q": "What allergen information should be on our website?",
        "a": "Explain how allergen information is provided: that staff can advise on the allergens UK law requires you to declare and where diners can see full details. Keep online menus in step with the kitchen. We never describe a dish as allergen-free unless you confirm it in writing, because that claim carries real risk for diners and for you."
      },
      {
        "q": "How can we fill quiet nights?",
        "a": "Start with what you already offer on those nights, such as set menus, private dining or supper clubs, and give each its own page and listing post. Then contact past diners who agreed to hear from you. We report which nights the work targets and what changed, without promising covers."
      },
      {
        "q": "How should a restaurant handle negative reviews?",
        "a": "Respond promptly and specifically to detailed or critical reviews, say what you have changed, and take the conversation offline if it needs detail. Fake reviews and reviews bought with hidden incentives are banned under UK consumer law. Honest replies show diners you listen and give AI tools fair context."
      },
      {
        "q": "Can you help us promote private dining and group bookings?",
        "a": "Yes, within the plan. Your Google profile and website can say whether you take private hire or large groups, how many people you can seat and how to enquire. Your weekly LinkedIn post can mention it to local businesses planning team meals. We keep the details factual, so the enquiries you receive are ones you can actually take."
      },
      {
        "q": "What does the Local Presence Plan cost for a restaurant, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/solicitor-marketing/": {
    "section": "C-C14",
    "h1": "Solicitor marketing for high-street law firms",
    "title": "Solicitor marketing for high-street law firms",
    "description": "Solicitor marketing for UK high-street firms: SRA Transparency Rules pages, Google Business Profile, reviews and AI search. Your firm signs off.",
    "answerFirst": "Solicitor marketing for a high-street firm means answering clients' first questions clearly with the SRA's rules in mind: what you do, what it costs, who handles the matter and how to start. We keep your practice-area pages, price information and Google Business Profile accurate and consistent, so search engines and AI tools describe your firm correctly.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>The SRA Transparency Rules require firms offering certain services to publish price information on their website, including whether VAT is included, and to display the SRA's clickable logo. Source: <a href=\"https://www.sra.org.uk/solicitors/standards-regulations/transparency-rules/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">SRA: Transparency Rules</a>.</li>\n<li>Clients can check a solicitor or firm on the SRA's Solicitor check register. Source: <a href=\"https://www.sra.org.uk/consumers/solicitor-check/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">SRA: Solicitor check</a>.</li>\n<li>Google asks businesses to use their real-world name on their Business Profile, without extra keywords. Source: <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Guidelines for representing your business on Google</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK high-street solicitor market the firm?</h3>\n<p>Solicitor marketing for a high-street firm means answering clients' first questions clearly with the SRA's rules in mind: what you do, what it costs, who handles the matter and how to start. We keep your practice-area pages, price information and Google Business Profile accurate and consistent, so search engines and AI tools describe your firm correctly.</p>\n<h3>What do the SRA Transparency Rules require on our website?</h3>\n<p>Firms offering certain services, including residential conveyancing, uncontested probate and some employment tribunal claims, must publish costs and service information. Authorised firms must also show complaints information, their SRA number and the SRA digital badge. We structure those pages clearly; your compliance officer confirms the content before it goes live.</p>\n<h3>How should a law firm respond to online reviews?</h3>\n<p>Carefully. A response must not confirm that the reviewer is a client or reveal anything about a matter, because confidentiality still applies. Keep responses general, thank the person and invite them to contact the firm directly. We draft neutral wording for your approval, and your compliance officer has the final say.</p>\n<h3>Can we describe the firm as leading or expert?</h3>\n<p>Only with evidence that supports the claim. Unqualified superlatives can mislead, and the SRA expects publicity to be accurate. Accreditations such as Lexcel or the Conveyancing Quality Scheme are verifiable facts you can state if you hold them. We write to what you can prove.</p>\n<h3>How do AI answers handle legal questions about our firm?</h3>\n<p>They summarise whatever they can find, which may include outdated fees or the wrong solicitor's name. Keeping your price pages, team pages and listings current gives them accurate material. We check what AI tools say about your firm and correct the sources we control, but we cannot edit AI answers themselves.</p>\n<h3>Do you need access to our client files or case management system?</h3>\n<p>No. The work uses public information about your firm: your website, your Google Business Profile and your directory listings. We don't need, and shouldn't see, any client data. Access to your Google profile, and to your website for adding structured data, is all the plan requires. Your free score uses only your public website.</p>\n<h3>What does the Local Presence Plan cost for a law firm, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/solicitor-marketing/#webpage",
          "url": "https://integrated.social/local/solicitor-marketing/",
          "name": "Solicitor marketing for high-street law firms",
          "headline": "Solicitor marketing for high-street law firms",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/solicitor-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/solicitor-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/solicitor-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Solicitor marketing for UK high-street firms: SRA Transparency Rules pages, Google Business Profile, reviews and AI search. Your firm signs off.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "SRA: Transparency Rules",
              "url": "https://www.sra.org.uk/solicitors/standards-regulations/transparency-rules/"
            },
            {
              "@type": "CreativeWork",
              "name": "SRA: Solicitor check",
              "url": "https://www.sra.org.uk/consumers/solicitor-check/"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Guidelines for representing your business on Google",
              "url": "https://support.google.com/business/answer/3038177"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/solicitor-marketing/#service",
          "name": "Solicitor marketing for high-street law firms",
          "description": "Local Presence Plan for Solicitors: available across the UK, including Greater London.",
          "serviceType": "Marketing and AI search visibility for high-street solicitors within SRA rules",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "High-street law firms and solicitors"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/solicitor-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/solicitor-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/solicitor-marketing/#offer",
          "name": "Local Presence Plan for Solicitors",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/solicitor-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Solicitor marketing for high-street law firms",
              "item": "https://integrated.social/local/solicitor-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/solicitor-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK high-street solicitor market the firm?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Solicitor marketing for a high-street firm means answering clients' first questions clearly with the SRA's rules in mind: what you do, what it costs, who handles the matter and how to start. We keep your practice-area pages, price information and Google Business Profile accurate and consistent, so search engines and AI tools describe your firm correctly."
              }
            },
            {
              "@type": "Question",
              "name": "What do the SRA Transparency Rules require on our website?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Firms offering certain services, including residential conveyancing, uncontested probate and some employment tribunal claims, must publish costs and service information. Authorised firms must also show complaints information, their SRA number and the SRA digital badge. We structure those pages clearly; your compliance officer confirms the content before it goes live."
              }
            },
            {
              "@type": "Question",
              "name": "How should a law firm respond to online reviews?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Carefully. A response must not confirm that the reviewer is a client or reveal anything about a matter, because confidentiality still applies. Keep responses general, thank the person and invite them to contact the firm directly. We draft neutral wording for your approval, and your compliance officer has the final say."
              }
            },
            {
              "@type": "Question",
              "name": "Can we describe the firm as leading or expert?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only with evidence that supports the claim. Unqualified superlatives can mislead, and the SRA expects publicity to be accurate. Accreditations such as Lexcel or the Conveyancing Quality Scheme are verifiable facts you can state if you hold them. We write to what you can prove."
              }
            },
            {
              "@type": "Question",
              "name": "How do AI answers handle legal questions about our firm?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "They summarise whatever they can find, which may include outdated fees or the wrong solicitor's name. Keeping your price pages, team pages and listings current gives them accurate material. We check what AI tools say about your firm and correct the sources we control, but we cannot edit AI answers themselves."
              }
            },
            {
              "@type": "Question",
              "name": "Do you need access to our client files or case management system?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. The work uses public information about your firm: your website, your Google Business Profile and your directory listings. We don't need, and shouldn't see, any client data. Access to your Google profile, and to your website for adding structured data, is all the plan requires. Your free score uses only your public website."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a law firm, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK high-street solicitor market the firm?",
        "a": "Solicitor marketing for a high-street firm means answering clients' first questions clearly with the SRA's rules in mind: what you do, what it costs, who handles the matter and how to start. We keep your practice-area pages, price information and Google Business Profile accurate and consistent, so search engines and AI tools describe your firm correctly."
      },
      {
        "q": "What do the SRA Transparency Rules require on our website?",
        "a": "Firms offering certain services, including residential conveyancing, uncontested probate and some employment tribunal claims, must publish costs and service information. Authorised firms must also show complaints information, their SRA number and the SRA digital badge. We structure those pages clearly; your compliance officer confirms the content before it goes live."
      },
      {
        "q": "How should a law firm respond to online reviews?",
        "a": "Carefully. A response must not confirm that the reviewer is a client or reveal anything about a matter, because confidentiality still applies. Keep responses general, thank the person and invite them to contact the firm directly. We draft neutral wording for your approval, and your compliance officer has the final say."
      },
      {
        "q": "Can we describe the firm as leading or expert?",
        "a": "Only with evidence that supports the claim. Unqualified superlatives can mislead, and the SRA expects publicity to be accurate. Accreditations such as Lexcel or the Conveyancing Quality Scheme are verifiable facts you can state if you hold them. We write to what you can prove."
      },
      {
        "q": "How do AI answers handle legal questions about our firm?",
        "a": "They summarise whatever they can find, which may include outdated fees or the wrong solicitor's name. Keeping your price pages, team pages and listings current gives them accurate material. We check what AI tools say about your firm and correct the sources we control, but we cannot edit AI answers themselves."
      },
      {
        "q": "Do you need access to our client files or case management system?",
        "a": "No. The work uses public information about your firm: your website, your Google Business Profile and your directory listings. We don't need, and shouldn't see, any client data. Access to your Google profile, and to your website for adding structured data, is all the plan requires. Your free score uses only your public website."
      },
      {
        "q": "What does the Local Presence Plan cost for a law firm, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/tanning-salon-marketing/": {
    "section": "C-C15",
    "h1": "Tanning salon marketing for sunbeds and spray tans",
    "title": "Tanning salon marketing for sunbeds and spray tans",
    "description": "Tanning salon marketing for UK sunbed and spray tan salons: bookings, Google Business Profile, reviews and ad rules on under-18s and health claims.",
    "answerFirst": "Tanning salon marketing has to sell sessions within some of the strictest rules in beauty: no sunbed use by under-18s and no health claims. We keep your sunbed minutes, spray tan bookings, opening hours and age policy clear across Google, your website and AI tools, so the right adult customers can book.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Under the Sunbeds (Regulation) Act 2010, a sunbed business must not let anyone under 18 use a sunbed on its premises. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2010/20/section/2\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Sunbeds (Regulation) Act 2010, section 2</a>.</li>\n<li>Health-related claims in ads need evidence under section 12 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n<li>Google asks businesses to use their real-world name on their Business Profile, without extra keywords. Source: <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Guidelines for representing your business on Google</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK tanning salon market its sessions?</h3>\n<p>Tanning salon marketing has to sell sessions within some of the strictest rules in beauty: no sunbed use by under-18s and no health claims. We keep your sunbed minutes, spray tan bookings, opening hours and age policy clear across Google, your website and AI tools, so the right adult customers can book.</p>\n<h3>Can you promote our sunbeds?</h3>\n<p>Yes, within the law and the CAP Code. Sunbed businesses must not let under-18s use them or offer them a session, and adverts must not claim health benefits such as vitamin D. We plan sunbed pages that state the age rule plainly, describe sessions factually and keep promotions away from young audiences.</p>\n<h3>Can we say sunbeds are safe if used sensibly?</h3>\n<p>No. Government advice says there is no evidence that any type of sunbed is less harmful than natural sun, and UKHSA discourages cosmetic use. Advertising regulators do not accept safety claims for sunbeds. We describe your equipment, hygiene and supervision factually and link customers to official safety advice instead.</p>\n<h3>How do we stop under-18s booking online?</h3>\n<p>Add a clear age declaration to your booking flow, explain that staff will check identification, and repeat the rule on your website and Google Business Profile. Exclude under-18s from paid social audiences. These steps also support a due-diligence defence, though the legal duty stays with your business.</p>\n<h3>Should spray tans have a separate page from sunbeds?</h3>\n<p>Yes. Spray tan customers ask different questions: how to prepare, how long it lasts and how far ahead of an event to book. A separate page answers those clearly, keeps sunbed rules where they belong and gives search engines and AI tools a distinct, accurate description of each service.</p>\n<h3>What category should our tanning salon use on Google?</h3>\n<p>Choose the tanning category that fits what most customers book with you, then add sunbed sessions and spray tans as separate services so each one is clear. If you also offer mobile spray tans, describe that as a service rather than adding a second address. We add these to your Google Business Profile and keep the same service names on your website.</p>\n<h3>What does the Local Presence Plan cost for a tanning salon, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/tanning-salon-marketing/#webpage",
          "url": "https://integrated.social/local/tanning-salon-marketing/",
          "name": "Tanning salon marketing for sunbeds and spray tans",
          "headline": "Tanning salon marketing for sunbeds and spray tans",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/tanning-salon-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/tanning-salon-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/tanning-salon-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Tanning salon marketing for UK sunbed and spray tan salons: bookings, Google Business Profile, reviews and ad rules on under-18s and health claims.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Sunbeds (Regulation) Act 2010, section 2",
              "url": "https://www.legislation.gov.uk/ukpga/2010/20/section/2"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Guidelines for representing your business on Google",
              "url": "https://support.google.com/business/answer/3038177"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/tanning-salon-marketing/#service",
          "name": "Tanning salon marketing for sunbeds and spray tans",
          "description": "Local Presence Plan for Tanning Salons: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for tanning salons",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Tanning salons offering sunbeds and spray tans"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/tanning-salon-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/tanning-salon-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/tanning-salon-marketing/#offer",
          "name": "Local Presence Plan for Tanning Salons",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/tanning-salon-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Tanning salon marketing for sunbeds and spray tans",
              "item": "https://integrated.social/local/tanning-salon-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/tanning-salon-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK tanning salon market its sessions?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tanning salon marketing has to sell sessions within some of the strictest rules in beauty: no sunbed use by under-18s and no health claims. We keep your sunbed minutes, spray tan bookings, opening hours and age policy clear across Google, your website and AI tools, so the right adult customers can book."
              }
            },
            {
              "@type": "Question",
              "name": "Can you promote our sunbeds?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, within the law and the CAP Code. Sunbed businesses must not let under-18s use them or offer them a session, and adverts must not claim health benefits such as vitamin D. We plan sunbed pages that state the age rule plainly, describe sessions factually and keep promotions away from young audiences."
              }
            },
            {
              "@type": "Question",
              "name": "Can we say sunbeds are safe if used sensibly?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Government advice says there is no evidence that any type of sunbed is less harmful than natural sun, and UKHSA discourages cosmetic use. Advertising regulators do not accept safety claims for sunbeds. We describe your equipment, hygiene and supervision factually and link customers to official safety advice instead."
              }
            },
            {
              "@type": "Question",
              "name": "How do we stop under-18s booking online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Add a clear age declaration to your booking flow, explain that staff will check identification, and repeat the rule on your website and Google Business Profile. Exclude under-18s from paid social audiences. These steps also support a due-diligence defence, though the legal duty stays with your business."
              }
            },
            {
              "@type": "Question",
              "name": "Should spray tans have a separate page from sunbeds?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Spray tan customers ask different questions: how to prepare, how long it lasts and how far ahead of an event to book. A separate page answers those clearly, keeps sunbed rules where they belong and gives search engines and AI tools a distinct, accurate description of each service."
              }
            },
            {
              "@type": "Question",
              "name": "What category should our tanning salon use on Google?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Choose the tanning category that fits what most customers book with you, then add sunbed sessions and spray tans as separate services so each one is clear. If you also offer mobile spray tans, describe that as a service rather than adding a second address. We add these to your Google Business Profile and keep the same service names on your website."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a tanning salon, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK tanning salon market its sessions?",
        "a": "Tanning salon marketing has to sell sessions within some of the strictest rules in beauty: no sunbed use by under-18s and no health claims. We keep your sunbed minutes, spray tan bookings, opening hours and age policy clear across Google, your website and AI tools, so the right adult customers can book."
      },
      {
        "q": "Can you promote our sunbeds?",
        "a": "Yes, within the law and the CAP Code. Sunbed businesses must not let under-18s use them or offer them a session, and adverts must not claim health benefits such as vitamin D. We plan sunbed pages that state the age rule plainly, describe sessions factually and keep promotions away from young audiences."
      },
      {
        "q": "Can we say sunbeds are safe if used sensibly?",
        "a": "No. Government advice says there is no evidence that any type of sunbed is less harmful than natural sun, and UKHSA discourages cosmetic use. Advertising regulators do not accept safety claims for sunbeds. We describe your equipment, hygiene and supervision factually and link customers to official safety advice instead."
      },
      {
        "q": "How do we stop under-18s booking online?",
        "a": "Add a clear age declaration to your booking flow, explain that staff will check identification, and repeat the rule on your website and Google Business Profile. Exclude under-18s from paid social audiences. These steps also support a due-diligence defence, though the legal duty stays with your business."
      },
      {
        "q": "Should spray tans have a separate page from sunbeds?",
        "a": "Yes. Spray tan customers ask different questions: how to prepare, how long it lasts and how far ahead of an event to book. A separate page answers those clearly, keeps sunbed rules where they belong and gives search engines and AI tools a distinct, accurate description of each service."
      },
      {
        "q": "What category should our tanning salon use on Google?",
        "a": "Choose the tanning category that fits what most customers book with you, then add sunbed sessions and spray tans as separate services so each one is clear. If you also offer mobile spray tans, describe that as a service rather than adding a second address. We add these to your Google Business Profile and keep the same service names on your website."
      },
      {
        "q": "What does the Local Presence Plan cost for a tanning salon, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/teeth-whitening-marketing/": {
    "section": "C-C16",
    "h1": "Teeth whitening marketing for dental practices",
    "title": "Teeth whitening marketing for dental practices",
    "description": "Teeth whitening marketing for UK dental practices: treatment pages, consultation booking and before-and-after rules under GDC and ASA guidance.",
    "answerFirst": "Teeth whitening marketing for dental practices starts with the question patients now ask first: who can legally do it? In the UK only registered dental professionals may provide whitening, on a dentist's prescription. We help your practice answer that clearly in search and AI tools and turn interest into booked consultations.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>UK cosmetics law limits tooth whitening products containing more than 0.1% hydrogen peroxide to sale to dental practitioners, with first use by or under a dental practitioner, and bars use on under-18s. Source: <a href=\"https://www.legislation.gov.uk/uksi/2013/1478/contents\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Cosmetic Products Enforcement Regulations 2013</a>.</li>\n<li>The GDC's guidance on advertising applies to whitening advertising by dental professionals. Source: <a href=\"https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">General Dental Council: Guidance on advertising</a>.</li>\n<li>Only dentists on a GDC specialist list may describe themselves as specialists. Source: <a href=\"https://www.gdc-uk.org/registration/your-registration/specialist-lists\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">General Dental Council: Specialist lists</a>.</li>\n<li>Claims about whitening results need evidence under section 12 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK dental practice market teeth whitening?</h3>\n<p>Teeth whitening marketing for dental practices starts with the question patients now ask first: who can legally do it? In the UK only registered dental professionals may provide whitening, on a dentist's prescription. We help your practice answer that clearly in search and AI tools and turn interest into booked consultations.</p>\n<h3>Do you market whitening for beauty salons?</h3>\n<p>No. In the UK tooth whitening is the practice of dentistry, and offering it without GDC registration is a criminal offence. We only support registered dental practices, and we help them explain to patients why a dentist-prescribed treatment with a proper assessment is the right route.</p>\n<h3>How do we stand out without criticising other providers?</h3>\n<p>Answer patients' real questions well: who can legally provide whitening, what the assessment involves, how in-practice and home trays differ, and what whitening cannot change, such as crowns and veneers. Calm, factual answers build trust without naming or attacking anyone, and they keep you within the CAP Code.</p>\n<h3>Who signs off before-and-after images?</h3>\n<p>Your clinician does. Before-and-after images and cosmetic claims fall under the CAP Code and GDC guidance. We flag anything that looks retouched or overstated, and we ask for consent records, but a registered professional at your practice approves what is published under your name.</p>\n<h3>Can we promote a whitening offer on social media?</h3>\n<p>You can, if the offer is genuine and the terms are complete: what is included, any conditions and an end date. Every promotion should still lead to a suitability assessment rather than a straight treatment booking. We draft the wording; your practice confirms the terms before anything is posted.</p>\n<h3>Our hygienist carries out most of our whitening. Can our marketing say that?</h3>\n<p>Yes, if it's accurate. In the UK, a registered dentist must prescribe whitening. A registered dental hygienist or therapist may then carry it out on that prescription. Saying who does what, and that they're GDC-registered, helps patients trust you. Check every name against the GDC register before it goes live, and update it when your team changes.</p>\n<h3>What does the Local Presence Plan cost for a practice offering teeth whitening, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/teeth-whitening-marketing/#webpage",
          "url": "https://integrated.social/local/teeth-whitening-marketing/",
          "name": "Teeth whitening marketing for dental practices",
          "headline": "Teeth whitening marketing for dental practices",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            },
            {
              "@id": "https://integrated.social/local/dentist-marketing/#webpage"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/teeth-whitening-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/teeth-whitening-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/teeth-whitening-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Teeth whitening marketing for UK dental practices: treatment pages, consultation booking and before-and-after rules under GDC and ASA guidance.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "The Cosmetic Products Enforcement Regulations 2013",
              "url": "https://www.legislation.gov.uk/uksi/2013/1478/contents"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Guidance on advertising",
              "url": "https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Specialist lists",
              "url": "https://www.gdc-uk.org/registration/your-registration/specialist-lists"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/teeth-whitening-marketing/#service",
          "name": "Teeth whitening marketing for dental practices",
          "description": "Local Presence Plan for Dental Practices: available across the UK, including Greater London.",
          "serviceType": "Teeth whitening marketing for GDC-registered dental practices",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "GDC-registered dental practices offering tooth whitening"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/teeth-whitening-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/teeth-whitening-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/teeth-whitening-marketing/#offer",
          "name": "Local Presence Plan for Dental Practices",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/teeth-whitening-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Teeth whitening marketing for dental practices",
              "item": "https://integrated.social/local/teeth-whitening-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/teeth-whitening-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK dental practice market teeth whitening?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Teeth whitening marketing for dental practices starts with the question patients now ask first: who can legally do it? In the UK only registered dental professionals may provide whitening, on a dentist's prescription. We help your practice answer that clearly in search and AI tools and turn interest into booked consultations."
              }
            },
            {
              "@type": "Question",
              "name": "Do you market whitening for beauty salons?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. In the UK tooth whitening is the practice of dentistry, and offering it without GDC registration is a criminal offence. We only support registered dental practices, and we help them explain to patients why a dentist-prescribed treatment with a proper assessment is the right route."
              }
            },
            {
              "@type": "Question",
              "name": "How do we stand out without criticising other providers?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Answer patients' real questions well: who can legally provide whitening, what the assessment involves, how in-practice and home trays differ, and what whitening cannot change, such as crowns and veneers. Calm, factual answers build trust without naming or attacking anyone, and they keep you within the CAP Code."
              }
            },
            {
              "@type": "Question",
              "name": "Who signs off before-and-after images?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Your clinician does. Before-and-after images and cosmetic claims fall under the CAP Code and GDC guidance. We flag anything that looks retouched or overstated, and we ask for consent records, but a registered professional at your practice approves what is published under your name."
              }
            },
            {
              "@type": "Question",
              "name": "Can we promote a whitening offer on social media?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can, if the offer is genuine and the terms are complete: what is included, any conditions and an end date. Every promotion should still lead to a suitability assessment rather than a straight treatment booking. We draft the wording; your practice confirms the terms before anything is posted."
              }
            },
            {
              "@type": "Question",
              "name": "Our hygienist carries out most of our whitening. Can our marketing say that?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, if it's accurate. In the UK, a registered dentist must prescribe whitening. A registered dental hygienist or therapist may then carry it out on that prescription. Saying who does what, and that they're GDC-registered, helps patients trust you. Check every name against the GDC register before it goes live, and update it when your team changes."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a practice offering teeth whitening, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK dental practice market teeth whitening?",
        "a": "Teeth whitening marketing for dental practices starts with the question patients now ask first: who can legally do it? In the UK only registered dental professionals may provide whitening, on a dentist's prescription. We help your practice answer that clearly in search and AI tools and turn interest into booked consultations."
      },
      {
        "q": "Do you market whitening for beauty salons?",
        "a": "No. In the UK tooth whitening is the practice of dentistry, and offering it without GDC registration is a criminal offence. We only support registered dental practices, and we help them explain to patients why a dentist-prescribed treatment with a proper assessment is the right route."
      },
      {
        "q": "How do we stand out without criticising other providers?",
        "a": "Answer patients' real questions well: who can legally provide whitening, what the assessment involves, how in-practice and home trays differ, and what whitening cannot change, such as crowns and veneers. Calm, factual answers build trust without naming or attacking anyone, and they keep you within the CAP Code."
      },
      {
        "q": "Who signs off before-and-after images?",
        "a": "Your clinician does. Before-and-after images and cosmetic claims fall under the CAP Code and GDC guidance. We flag anything that looks retouched or overstated, and we ask for consent records, but a registered professional at your practice approves what is published under your name."
      },
      {
        "q": "Can we promote a whitening offer on social media?",
        "a": "You can, if the offer is genuine and the terms are complete: what is included, any conditions and an end date. Every promotion should still lead to a suitability assessment rather than a straight treatment booking. We draft the wording; your practice confirms the terms before anything is posted."
      },
      {
        "q": "Our hygienist carries out most of our whitening. Can our marketing say that?",
        "a": "Yes, if it's accurate. In the UK, a registered dentist must prescribe whitening. A registered dental hygienist or therapist may then carry it out on that prescription. Saying who does what, and that they're GDC-registered, helps patients trust you. Check every name against the GDC register before it goes live, and update it when your team changes."
      },
      {
        "q": "What does the Local Presence Plan cost for a practice offering teeth whitening, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/tutor-marketing/": {
    "section": "C-C17",
    "h1": "Tutor marketing for private and small-group tutors",
    "title": "Tutor marketing for private and small-group tutors",
    "description": "Tutor marketing for UK private and small-group tutors: parent enquiries, subject and exam pages, Google Business Profile, reviews and safeguarding.",
    "answerFirst": "Tutor marketing for private and small-group tutors should help parents check fit fast: which subjects and exams you teach, at what level, where or online, and how a trial lesson works. We keep these facts consistent across Google, your website and AI tools, so the families you can genuinely help find you during the weeks they start looking.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Individuals can request a basic DBS check themselves; standard and enhanced checks must be requested by an organisation for an eligible role. Source: <a href=\"https://www.gov.uk/request-copy-criminal-record\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Request a basic DBS check</a>; <a href=\"https://www.gov.uk/dbs-check-applicant-criminal-record\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: DBS checks, what they are and who can apply</a>.</li>\n<li>Claims about exam results or grade improvements need evidence under the CAP Code's rules on misleading advertising. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/03.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 3: Misleading advertising</a>.</li>\n<li>Service-area businesses can show the areas they serve on Google instead of a street address. Source: <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK private tutor market their lessons?</h3>\n<p>Tutor marketing for private and small-group tutors should help parents check fit fast: which subjects and exams you teach, at what level, where or online, and how a trial lesson works. We keep these facts consistent across Google, your website and AI tools, so the families you can genuinely help find you during the weeks they start looking.</p>\n<h3>Can we promise grade improvements?</h3>\n<p>No. Results depend on each pupil, and promises like a guaranteed grade 9 or passing the 11+ can mislead. We describe how you teach, what you cover and how you track progress with families. Honest descriptions build trust with parents and keep your pages accurate for AI tools.</p>\n<h3>Should we create pages for each exam board?</h3>\n<p>Usually a short section per level, naming the boards you know well, is enough. Separate pages make sense only where you have genuinely distinct content. We avoid thin duplicate pages that repeat the same text, because they confuse parents and search engines alike and add little for a family comparing tutors.</p>\n<h3>How should tutors handle online and in-person lessons?</h3>\n<p>Say clearly which you offer and where. If you travel, list areas on one page; if you teach online, explain the platform and what a session looks like. Clear format information stops mismatched enquiries and helps AI tools describe your service correctly.</p>\n<h3>When do parents usually look for a tutor?</h3>\n<p>Demand often rises around the start of term, mock exams and the run-up to entrance tests, but your own enquiries are the most reliable guide. We plan profile posts and page updates around the periods you see, and keep waiting-list wording current so parents know whether to enquire now.</p>\n<h3>Should I show a DBS check on my tutor profile?</h3>\n<p>Say exactly which check you hold, its level and its issue date, because parents may ask. Anyone can request a basic DBS check for themselves on GOV.UK, while standard and enhanced checks have to be requested through an organisation for an eligible role. Never describe a basic check as an enhanced one.</p>\n<h3>What does the Local Presence Plan cost for a tutor, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/tutor-marketing/#webpage",
          "url": "https://integrated.social/local/tutor-marketing/",
          "name": "Tutor marketing for private and small-group tutors",
          "headline": "Tutor marketing for private and small-group tutors",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/tutor-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/tutor-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/tutor-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Tutor marketing for UK private and small-group tutors: parent enquiries, subject and exam pages, Google Business Profile, reviews and safeguarding.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Request a basic DBS check",
              "url": "https://www.gov.uk/request-copy-criminal-record"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: DBS checks, what they are and who can apply",
              "url": "https://www.gov.uk/dbs-check-applicant-criminal-record"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 3: Misleading advertising",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/03.html"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Manage your service areas for service-area and hybrid businesses",
              "url": "https://support.google.com/business/answer/9157481"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/tutor-marketing/#service",
          "name": "Tutor marketing for private and small-group tutors",
          "description": "Local Presence Plan for Tutors: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for tutors",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Tutors"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/tutor-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/tutor-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/tutor-marketing/#offer",
          "name": "Local Presence Plan for Tutors",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/tutor-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Tutor marketing for private and small-group tutors",
              "item": "https://integrated.social/local/tutor-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/tutor-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK private tutor market their lessons?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tutor marketing for private and small-group tutors should help parents check fit fast: which subjects and exams you teach, at what level, where or online, and how a trial lesson works. We keep these facts consistent across Google, your website and AI tools, so the families you can genuinely help find you during the weeks they start looking."
              }
            },
            {
              "@type": "Question",
              "name": "Can we promise grade improvements?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Results depend on each pupil, and promises like a guaranteed grade 9 or passing the 11+ can mislead. We describe how you teach, what you cover and how you track progress with families. Honest descriptions build trust with parents and keep your pages accurate for AI tools."
              }
            },
            {
              "@type": "Question",
              "name": "Should we create pages for each exam board?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Usually a short section per level, naming the boards you know well, is enough. Separate pages make sense only where you have genuinely distinct content. We avoid thin duplicate pages that repeat the same text, because they confuse parents and search engines alike and add little for a family comparing tutors."
              }
            },
            {
              "@type": "Question",
              "name": "How should tutors handle online and in-person lessons?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Say clearly which you offer and where. If you travel, list areas on one page; if you teach online, explain the platform and what a session looks like. Clear format information stops mismatched enquiries and helps AI tools describe your service correctly."
              }
            },
            {
              "@type": "Question",
              "name": "When do parents usually look for a tutor?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Demand often rises around the start of term, mock exams and the run-up to entrance tests, but your own enquiries are the most reliable guide. We plan profile posts and page updates around the periods you see, and keep waiting-list wording current so parents know whether to enquire now."
              }
            },
            {
              "@type": "Question",
              "name": "Should I show a DBS check on my tutor profile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Say exactly which check you hold, its level and its issue date, because parents may ask. Anyone can request a basic DBS check for themselves on GOV.UK, while standard and enhanced checks have to be requested through an organisation for an eligible role. Never describe a basic check as an enhanced one."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a tutor, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK private tutor market their lessons?",
        "a": "Tutor marketing for private and small-group tutors should help parents check fit fast: which subjects and exams you teach, at what level, where or online, and how a trial lesson works. We keep these facts consistent across Google, your website and AI tools, so the families you can genuinely help find you during the weeks they start looking."
      },
      {
        "q": "Can we promise grade improvements?",
        "a": "No. Results depend on each pupil, and promises like a guaranteed grade 9 or passing the 11+ can mislead. We describe how you teach, what you cover and how you track progress with families. Honest descriptions build trust with parents and keep your pages accurate for AI tools."
      },
      {
        "q": "Should we create pages for each exam board?",
        "a": "Usually a short section per level, naming the boards you know well, is enough. Separate pages make sense only where you have genuinely distinct content. We avoid thin duplicate pages that repeat the same text, because they confuse parents and search engines alike and add little for a family comparing tutors."
      },
      {
        "q": "How should tutors handle online and in-person lessons?",
        "a": "Say clearly which you offer and where. If you travel, list areas on one page; if you teach online, explain the platform and what a session looks like. Clear format information stops mismatched enquiries and helps AI tools describe your service correctly."
      },
      {
        "q": "When do parents usually look for a tutor?",
        "a": "Demand often rises around the start of term, mock exams and the run-up to entrance tests, but your own enquiries are the most reliable guide. We plan profile posts and page updates around the periods you see, and keep waiting-list wording current so parents know whether to enquire now."
      },
      {
        "q": "Should I show a DBS check on my tutor profile?",
        "a": "Say exactly which check you hold, its level and its issue date, because parents may ask. Anyone can request a basic DBS check for themselves on GOV.UK, while standard and enhanced checks have to be requested through an organisation for an eligible role. Never describe a basic check as an enhanced one."
      },
      {
        "q": "What does the Local Presence Plan cost for a tutor, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/window-cleaner-marketing/": {
    "section": "C-C18",
    "h1": "Window cleaner marketing for local rounds",
    "title": "Window cleaner marketing for local rounds",
    "description": "Window cleaner marketing for UK sole traders and small firms: new rounds, quote requests, Google Business Profile service areas and repeat customers.",
    "answerFirst": "Window cleaner marketing works when householders can see, in seconds, whether you cover their street, how often you call and how a quote works. We set up your service-area profile, round frequencies and quote route consistently across Google, your website and AI tools, so the right homes on your round get in touch.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>HSE publishes guidance on window cleaning, including the choice between ladders, water-fed poles and other access. Source: <a href=\"https://www.hse.gov.uk/cleaning/topics/window-cleaning.htm\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">HSE: Window cleaning</a>.</li>\n<li>The Work at Height Regulations 2005 require work at height to be properly planned and carried out safely. Source: <a href=\"https://www.hse.gov.uk/work-at-height/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">HSE: Working at height</a>.</li>\n<li>Service-area businesses can show the areas they serve on Google instead of a street address. Source: <a href=\"https://support.google.com/business/answer/9157481\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Manage your service areas for service-area and hybrid businesses</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK window cleaner market their round?</h3>\n<p>Window cleaner marketing works when householders can see, in seconds, whether you cover their street, how often you call and how a quote works. We set up your service-area profile, round frequencies and quote route consistently across Google, your website and AI tools, so the right homes on your round get in touch.</p>\n<h3>Should we list prices on our website?</h3>\n<p>That is your decision. Many householders want a rough idea before asking, so a quote form by house type and number of windows can help. We never publish figures you have not confirmed, and we keep whatever you share identical on your site, your profile and AI-readable listings.</p>\n<h3>Why do conservatory roofs and gutters need their own sections?</h3>\n<p>People search for those jobs separately, often outside your usual round. A short section for each, saying how you do the work and which areas you cover, helps search engines and AI tools match you to those requests instead of only to general window cleaning.</p>\n<h3>Can we say our methods are safe?</h3>\n<p>Describe what you actually do, such as using water-fed poles from the ground, without claims about safety records. HSE guidance explains the Work at Height Regulations 2005, and we point to it rather than making promises. Honest descriptions build more trust than broad safety slogans.</p>\n<h3>How do we fill quiet streets on our round?</h3>\n<p>Tell us where the gaps are. We focus your areas section, profile updates and posts on those streets and postcodes, and ask nearby customers for an honest review in their own words. Your round grows where travel time is already low, without separate thin pages for each town.</p>\n<h3>Should I mention how I work at height?</h3>\n<p>Yes, briefly. Householders and managing agents may ask whether you use water-fed poles or ladders and whether you hold public liability insurance. HSE publishes guidance on window cleaning and working at height. Describe your own methods accurately, and mention insurance only if you hold a current policy you can show on request.</p>\n<h3>What does the Local Presence Plan cost for a window cleaner, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/window-cleaner-marketing/#webpage",
          "url": "https://integrated.social/local/window-cleaner-marketing/",
          "name": "Window cleaner marketing for local rounds",
          "headline": "Window cleaner marketing for local rounds",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/window-cleaner-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/window-cleaner-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/window-cleaner-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Window cleaner marketing for UK sole traders and small firms: new rounds, quote requests, Google Business Profile service areas and repeat customers.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "HSE: Window cleaning",
              "url": "https://www.hse.gov.uk/cleaning/topics/window-cleaning.htm"
            },
            {
              "@type": "CreativeWork",
              "name": "HSE: Working at height",
              "url": "https://www.hse.gov.uk/work-at-height/"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Manage your service areas for service-area and hybrid businesses",
              "url": "https://support.google.com/business/answer/9157481"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/window-cleaner-marketing/#service",
          "name": "Window cleaner marketing for local rounds",
          "description": "Local Presence Plan for Window Cleaners: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for window cleaners",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Window cleaners"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "url": "https://integrated.social/local/window-cleaner-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/window-cleaner-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/window-cleaner-marketing/#offer",
          "name": "Local Presence Plan for Window Cleaners",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London",
              "sameAs": "https://www.wikidata.org/wiki/Q23306"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/window-cleaner-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Window cleaner marketing for local rounds",
              "item": "https://integrated.social/local/window-cleaner-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/window-cleaner-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK window cleaner market their round?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Window cleaner marketing works when householders can see, in seconds, whether you cover their street, how often you call and how a quote works. We set up your service-area profile, round frequencies and quote route consistently across Google, your website and AI tools, so the right homes on your round get in touch."
              }
            },
            {
              "@type": "Question",
              "name": "Should we list prices on our website?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "That is your decision. Many householders want a rough idea before asking, so a quote form by house type and number of windows can help. We never publish figures you have not confirmed, and we keep whatever you share identical on your site, your profile and AI-readable listings."
              }
            },
            {
              "@type": "Question",
              "name": "Why do conservatory roofs and gutters need their own sections?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "People search for those jobs separately, often outside your usual round. A short section for each, saying how you do the work and which areas you cover, helps search engines and AI tools match you to those requests instead of only to general window cleaning."
              }
            },
            {
              "@type": "Question",
              "name": "Can we say our methods are safe?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Describe what you actually do, such as using water-fed poles from the ground, without claims about safety records. HSE guidance explains the Work at Height Regulations 2005, and we point to it rather than making promises. Honest descriptions build more trust than broad safety slogans."
              }
            },
            {
              "@type": "Question",
              "name": "How do we fill quiet streets on our round?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tell us where the gaps are. We focus your areas section, profile updates and posts on those streets and postcodes, and ask nearby customers for an honest review in their own words. Your round grows where travel time is already low, without separate thin pages for each town."
              }
            },
            {
              "@type": "Question",
              "name": "Should I mention how I work at height?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, briefly. Householders and managing agents may ask whether you use water-fed poles or ladders and whether you hold public liability insurance. HSE publishes guidance on window cleaning and working at height. Describe your own methods accurately, and mention insurance only if you hold a current policy you can show on request."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a window cleaner, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK window cleaner market their round?",
        "a": "Window cleaner marketing works when householders can see, in seconds, whether you cover their street, how often you call and how a quote works. We set up your service-area profile, round frequencies and quote route consistently across Google, your website and AI tools, so the right homes on your round get in touch."
      },
      {
        "q": "Should we list prices on our website?",
        "a": "That is your decision. Many householders want a rough idea before asking, so a quote form by house type and number of windows can help. We never publish figures you have not confirmed, and we keep whatever you share identical on your site, your profile and AI-readable listings."
      },
      {
        "q": "Why do conservatory roofs and gutters need their own sections?",
        "a": "People search for those jobs separately, often outside your usual round. A short section for each, saying how you do the work and which areas you cover, helps search engines and AI tools match you to those requests instead of only to general window cleaning."
      },
      {
        "q": "Can we say our methods are safe?",
        "a": "Describe what you actually do, such as using water-fed poles from the ground, without claims about safety records. HSE guidance explains the Work at Height Regulations 2005, and we point to it rather than making promises. Honest descriptions build more trust than broad safety slogans."
      },
      {
        "q": "How do we fill quiet streets on our round?",
        "a": "Tell us where the gaps are. We focus your areas section, profile updates and posts on those streets and postcodes, and ask nearby customers for an honest review in their own words. Your round grows where travel time is already low, without separate thin pages for each town."
      },
      {
        "q": "Should I mention how I work at height?",
        "a": "Yes, briefly. Householders and managing agents may ask whether you use water-fed poles or ladders and whether you hold public liability insurance. HSE publishes guidance on window cleaning and working at height. Describe your own methods accurately, and mention insurance only if you hold a current policy you can show on request."
      },
      {
        "q": "What does the Local Presence Plan cost for a window cleaner, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/yoga-studio-marketing/": {
    "section": "C-C19",
    "h1": "Yoga studio marketing for classes and courses",
    "title": "Yoga studio marketing for classes and courses",
    "description": "Yoga studio marketing for UK studios and teachers: class bookings, intro offers, Google Business Profile, teacher credentials and reviews, in plain words.",
    "answerFirst": "Yoga studio marketing works when a newcomer can tell which style suits them, whether they can simply drop in, and who teaches. We keep your timetable, styles, beginners' courses, workshops and retreats accurate across Google, your website and AI tools, so the right students find the right class and come back.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How it works locally: the facts, with sources</h2>\n<ul>\n<li>Health claims in ads, such as reducing anxiety or curing pain, need evidence under section 12 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n<li>Paid outdoor classes in the Royal Parks need a fitness training licence from The Royal Parks. Source: <a href=\"https://www.royalparks.org.uk/commercial-licences/fitness-training-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Royal Parks: Fitness training licences</a>.</li>\n<li>Google says local results depend mainly on relevance, distance and popularity. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n<li>Fake reviews and concealed incentivised reviews are banned; the CMA's guidance explains what businesses must check. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a UK yoga studio market its classes?</h3>\n<p>Yoga studio marketing works when a newcomer can tell which style suits them, whether they can simply drop in, and who teaches. We keep your timetable, styles, beginners' courses, workshops and retreats accurate across Google, your website and AI tools, so the right students find the right class and come back.</p>\n<h3>Should we push drop-ins or unlimited passes?</h3>\n<p>Both have a place, and the balance is your call. Drop-ins lower the barrier for curious newcomers; passes reward regulars. We make the drop-in route easy to find, then show the step to a pass clearly, so students choose what suits them rather than leaving confused about price or commitment.</p>\n<h3>How do we describe hot yoga responsibly?</h3>\n<p>State the room temperature, session length and what to bring, and suggest anyone with a medical condition checks with their GP first. Avoid claims about detox, weight loss or curing anything, which need evidence under the CAP Code. Plain, factual information builds more trust than promises.</p>\n<h3>Do workshops and retreats need their own pages?</h3>\n<p>Yes. People search for specific events, such as an inversions workshop or a weekend retreat, and want dates, level, teacher and cancellation terms in one place. Separate pages answer those questions properly and give search engines and AI tools distinct facts about each event.</p>\n<h3>Should we call our teachers registered or certified?</h3>\n<p>Be careful. Customers often ask about a teacher's training and experience, and vague words like registered or certified can confuse them. Say where your teachers trained, and name the actual training and any membership, such as the British Wheel of Yoga or Yoga Alliance Professionals, where your teachers genuinely hold it.</p>\n<h3>We teach in hired halls as well as our own studio. How should that appear online?</h3>\n<p>Your own studio can have a Google Business Profile with its address and hours. Classes in hired halls are usually better listed on your website timetable with clear venue details, rather than as separate Google listings at addresses you don't control. We'll look at your set-up on the call and recommend the clearest arrangement before changing anything.</p>\n<h3>What does the Local Presence Plan cost for a yoga studio, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/yoga-studio-marketing/#webpage",
          "url": "https://integrated.social/local/yoga-studio-marketing/",
          "name": "Yoga studio marketing for classes and courses",
          "headline": "Yoga studio marketing for classes and courses",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/yoga-studio-marketing/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/local/yoga-studio-marketing/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/yoga-studio-marketing/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Yoga studio marketing for UK studios and teachers: class bookings, intro offers, Google Business Profile, teacher credentials and reviews, in plain words.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            },
            {
              "@type": "CreativeWork",
              "name": "The Royal Parks: Fitness training licences",
              "url": "https://www.royalparks.org.uk/commercial-licences/fitness-training-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/yoga-studio-marketing/#service",
          "name": "Yoga studio marketing for classes and courses",
          "description": "Local Presence Plan for Yoga Studios: available across the UK, including Greater London.",
          "serviceType": "Local marketing and AI search visibility for yoga studios",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Independent yoga studios and teachers"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "url": "https://integrated.social/local/yoga-studio-marketing/",
          "offers": {
            "@id": "https://integrated.social/local/yoga-studio-marketing/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/yoga-studio-marketing/#offer",
          "name": "Local Presence Plan for Yoga Studios",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/yoga-studio-marketing/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Yoga studio marketing for classes and courses",
              "item": "https://integrated.social/local/yoga-studio-marketing/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/yoga-studio-marketing/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a UK yoga studio market its classes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yoga studio marketing works when a newcomer can tell which style suits them, whether they can simply drop in, and who teaches. We keep your timetable, styles, beginners' courses, workshops and retreats accurate across Google, your website and AI tools, so the right students find the right class and come back."
              }
            },
            {
              "@type": "Question",
              "name": "Should we push drop-ins or unlimited passes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Both have a place, and the balance is your call. Drop-ins lower the barrier for curious newcomers; passes reward regulars. We make the drop-in route easy to find, then show the step to a pass clearly, so students choose what suits them rather than leaving confused about price or commitment."
              }
            },
            {
              "@type": "Question",
              "name": "How do we describe hot yoga responsibly?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "State the room temperature, session length and what to bring, and suggest anyone with a medical condition checks with their GP first. Avoid claims about detox, weight loss or curing anything, which need evidence under the CAP Code. Plain, factual information builds more trust than promises."
              }
            },
            {
              "@type": "Question",
              "name": "Do workshops and retreats need their own pages?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. People search for specific events, such as an inversions workshop or a weekend retreat, and want dates, level, teacher and cancellation terms in one place. Separate pages answer those questions properly and give search engines and AI tools distinct facts about each event."
              }
            },
            {
              "@type": "Question",
              "name": "Should we call our teachers registered or certified?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Be careful. Customers often ask about a teacher's training and experience, and vague words like registered or certified can confuse them. Say where your teachers trained, and name the actual training and any membership, such as the British Wheel of Yoga or Yoga Alliance Professionals, where your teachers genuinely hold it."
              }
            },
            {
              "@type": "Question",
              "name": "We teach in hired halls as well as our own studio. How should that appear online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Your own studio can have a Google Business Profile with its address and hours. Classes in hired halls are usually better listed on your website timetable with clear venue details, rather than as separate Google listings at addresses you don't control. We'll look at your set-up on the call and recommend the clearest arrangement before changing anything."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a yoga studio, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a UK yoga studio market its classes?",
        "a": "Yoga studio marketing works when a newcomer can tell which style suits them, whether they can simply drop in, and who teaches. We keep your timetable, styles, beginners' courses, workshops and retreats accurate across Google, your website and AI tools, so the right students find the right class and come back."
      },
      {
        "q": "Should we push drop-ins or unlimited passes?",
        "a": "Both have a place, and the balance is your call. Drop-ins lower the barrier for curious newcomers; passes reward regulars. We make the drop-in route easy to find, then show the step to a pass clearly, so students choose what suits them rather than leaving confused about price or commitment."
      },
      {
        "q": "How do we describe hot yoga responsibly?",
        "a": "State the room temperature, session length and what to bring, and suggest anyone with a medical condition checks with their GP first. Avoid claims about detox, weight loss or curing anything, which need evidence under the CAP Code. Plain, factual information builds more trust than promises."
      },
      {
        "q": "Do workshops and retreats need their own pages?",
        "a": "Yes. People search for specific events, such as an inversions workshop or a weekend retreat, and want dates, level, teacher and cancellation terms in one place. Separate pages answer those questions properly and give search engines and AI tools distinct facts about each event."
      },
      {
        "q": "Should we call our teachers registered or certified?",
        "a": "Be careful. Customers often ask about a teacher's training and experience, and vague words like registered or certified can confuse them. Say where your teachers trained, and name the actual training and any membership, such as the British Wheel of Yoga or Yoga Alliance Professionals, where your teachers genuinely hold it."
      },
      {
        "q": "We teach in hired halls as well as our own studio. How should that appear online?",
        "a": "Your own studio can have a Google Business Profile with its address and hours. Classes in hired halls are usually better listed on your website timetable with clear venue details, rather than as separate Google listings at addresses you don't control. We'll look at your set-up on the call and recommend the clearest arrangement before changing anything."
      },
      {
        "q": "What does the Local Presence Plan cost for a yoga studio, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses across the UK, including Greater London. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/cafe-marketing-richmond-putney-wimbledon/": {
    "section": "C-D1",
    "h1": "Café marketing in Richmond, Putney, Wimbledon and Lambeth",
    "title": "Café marketing in Richmond, Putney, Wimbledon and Lambeth",
    "description": "Cafe marketing for independent coffee shops in Richmond, Putney, Wimbledon and Lambeth: Google Maps, reviews, food hygiene ratings and local footfall.",
    "answerFirst": "Café marketing in Richmond, Putney and Wimbledon comes down to being easy to find on a map, open when people expect and clear about what they will get. We keep your hours, menu, hygiene rating link and outdoor seating details consistent across Google, your website and AI tools, so local coffee drinkers choose you on the way past.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>Richmond and Lambeth councils issue pavement licences for tables and chairs outside cafés; fees are capped nationally at £500 for a first application. Source: <a href=\"https://www.richmond.gov.uk/services/business/services_for_business/business_and_street_trading_licences/pavement_licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Richmond Council: Pavement licence</a>; <a href=\"https://www.lambeth.gov.uk/Business-rates-services-and-licensing/licensing-and-permits/Apply-change-or-pay-for-a-licence/Apply-for-a-licence/Apply-for-a-pavement-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Lambeth Council: Apply for a pavement licence</a>; <a href=\"https://www.gov.uk/government/publications/pavement-licences-guidance\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Pavement licences guidance</a>.</li>\n<li>Food hygiene ratings for cafés in these boroughs are published on the FSA ratings site. Source: <a href=\"https://ratings.food.gov.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Food hygiene ratings search</a>; <a href=\"https://www.food.gov.uk/safety-hygiene/food-hygiene-rating-scheme\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Food Standards Agency: Food Hygiene Rating Scheme</a>.</li>\n<li>Food businesses must give customers information about any of the 14 listed allergens used as ingredients. Source: <a href=\"https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Allergen guidance for food businesses</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a café in Richmond, Putney or Wimbledon market itself?</h3>\n<p>Café marketing in Richmond, Putney and Wimbledon comes down to being easy to find on a map, open when people expect and clear about what they will get. We keep your hours, menu, hygiene rating link and outdoor seating details consistent across Google, your website and AI tools, so local coffee drinkers choose you on the way past.</p>\n<h3>Should a café show its food hygiene rating on its own website as well as the door?</h3>\n<p>Yes, as a link. Customers can check it on the FSA ratings search, where Wandsworth and Merton publish the results of their inspections. Display of the window sticker is voluntary in England (see the FSA guidance for businesses), so a link from your website to the official listing is the simplest way to let regulars check it without the figure going stale.</p>\n<h3>What do Richmond's pavement licence conditions mean for our photos?</h3>\n<p>Richmond and Merton apply shared standard conditions, including seated customers and a clear walkway for pedestrians. Photos and copy should show the outdoor area as licensed, not crowded with standing customers or blocking the path. We describe your seating as the licence allows, and we update it if the council varies your conditions.</p>\n<h3>Can our Lambeth café keep tables out after dark?</h3>\n<p>Lambeth's pavement licence covers removable furniture put away by 10pm. If you want tables out later, or fixed benches, Lambeth points cafés to its tables and chairs licence instead. We only mention evening seating on your listings once you hold the licence that covers it.</p>\n<h3>Which council licenses tables outside a Putney café?</h3>\n<p>Wandsworth. The joint policy used by Merton and Richmond says Wandsworth applications are handled separately. A Putney café should follow Wandsworth's process, and we make sure your website and Google profile describe outdoor seating only once that permission is in place.</p>\n<h3>Do we need a pavement licence for tables outside our café?</h3>\n<p>Usually, yes. Tables and chairs on the highway need a pavement licence from your council, such as Richmond or Lambeth, and fees are capped at £500 for a first application and £350 for a renewal. Check your council's conditions before you mention outdoor seating on Google or your website, and update the wording if your licence changes.</p>\n<h3>What does the Local Presence Plan cost for a café, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Richmond, Putney, Wimbledon and Lambeth, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#webpage",
          "url": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/",
          "name": "Café marketing in Richmond, Putney, Wimbledon and Lambeth",
          "headline": "Café marketing in Richmond, Putney, Wimbledon and Lambeth",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Cafe marketing for independent coffee shops in Richmond, Putney, Wimbledon and Lambeth: Google Maps, reviews, food hygiene ratings and local footfall.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Richmond Council: Pavement licence",
              "url": "https://www.richmond.gov.uk/services/business/services_for_business/business_and_street_trading_licences/pavement_licence"
            },
            {
              "@type": "CreativeWork",
              "name": "Lambeth Council: Apply for a pavement licence",
              "url": "https://www.lambeth.gov.uk/Business-rates-services-and-licensing/licensing-and-permits/Apply-change-or-pay-for-a-licence/Apply-for-a-licence/Apply-for-a-pavement-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Pavement licences guidance",
              "url": "https://www.gov.uk/government/publications/pavement-licences-guidance"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Food hygiene ratings search",
              "url": "https://ratings.food.gov.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "Food Standards Agency: Food Hygiene Rating Scheme",
              "url": "https://www.food.gov.uk/safety-hygiene/food-hygiene-rating-scheme"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Allergen guidance for food businesses",
              "url": "https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#service",
          "name": "Café marketing in Richmond, Putney, Wimbledon and Lambeth",
          "serviceType": "Local marketing and AI search visibility for cafés",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Cafés"
          },
          "offers": {
            "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Café marketing in Richmond, Putney, Wimbledon and Lambeth",
              "item": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/cafe-marketing-richmond-putney-wimbledon/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a café in Richmond, Putney or Wimbledon market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Café marketing in Richmond, Putney and Wimbledon comes down to being easy to find on a map, open when people expect and clear about what they will get. We keep your hours, menu, hygiene rating link and outdoor seating details consistent across Google, your website and AI tools, so local coffee drinkers choose you on the way past."
              }
            },
            {
              "@type": "Question",
              "name": "Should a café show its food hygiene rating on its own website as well as the door?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, as a link. Customers can check it on the FSA ratings search, where Wandsworth and Merton publish the results of their inspections. Display of the window sticker is voluntary in England (see the FSA guidance for businesses), so a link from your website to the official listing is the simplest way to let regulars check it without the figure going stale."
              }
            },
            {
              "@type": "Question",
              "name": "What do Richmond's pavement licence conditions mean for our photos?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Richmond and Merton apply shared standard conditions, including seated customers and a clear walkway for pedestrians. Photos and copy should show the outdoor area as licensed, not crowded with standing customers or blocking the path. We describe your seating as the licence allows, and we update it if the council varies your conditions."
              }
            },
            {
              "@type": "Question",
              "name": "Can our Lambeth café keep tables out after dark?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Lambeth's pavement licence covers removable furniture put away by 10pm. If you want tables out later, or fixed benches, Lambeth points cafés to its tables and chairs licence instead. We only mention evening seating on your listings once you hold the licence that covers it."
              }
            },
            {
              "@type": "Question",
              "name": "Which council licenses tables outside a Putney café?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Wandsworth. The joint policy used by Merton and Richmond says Wandsworth applications are handled separately. A Putney café should follow Wandsworth's process, and we make sure your website and Google profile describe outdoor seating only once that permission is in place."
              }
            },
            {
              "@type": "Question",
              "name": "Do we need a pavement licence for tables outside our café?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Usually, yes. Tables and chairs on the highway need a pavement licence from your council, such as Richmond or Lambeth, and fees are capped at £500 for a first application and £350 for a renewal. Check your council's conditions before you mention outdoor seating on Google or your website, and update the wording if your licence changes."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a café, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Richmond, Putney, Wimbledon and Lambeth, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a café in Richmond, Putney or Wimbledon market itself?",
        "a": "Café marketing in Richmond, Putney and Wimbledon comes down to being easy to find on a map, open when people expect and clear about what they will get. We keep your hours, menu, hygiene rating link and outdoor seating details consistent across Google, your website and AI tools, so local coffee drinkers choose you on the way past."
      },
      {
        "q": "Should a café show its food hygiene rating on its own website as well as the door?",
        "a": "Yes, as a link. Customers can check it on the FSA ratings search, where Wandsworth and Merton publish the results of their inspections. Display of the window sticker is voluntary in England (see the FSA guidance for businesses), so a link from your website to the official listing is the simplest way to let regulars check it without the figure going stale."
      },
      {
        "q": "What do Richmond's pavement licence conditions mean for our photos?",
        "a": "Richmond and Merton apply shared standard conditions, including seated customers and a clear walkway for pedestrians. Photos and copy should show the outdoor area as licensed, not crowded with standing customers or blocking the path. We describe your seating as the licence allows, and we update it if the council varies your conditions."
      },
      {
        "q": "Can our Lambeth café keep tables out after dark?",
        "a": "Lambeth's pavement licence covers removable furniture put away by 10pm. If you want tables out later, or fixed benches, Lambeth points cafés to its tables and chairs licence instead. We only mention evening seating on your listings once you hold the licence that covers it."
      },
      {
        "q": "Which council licenses tables outside a Putney café?",
        "a": "Wandsworth. The joint policy used by Merton and Richmond says Wandsworth applications are handled separately. A Putney café should follow Wandsworth's process, and we make sure your website and Google profile describe outdoor seating only once that permission is in place."
      },
      {
        "q": "Do we need a pavement licence for tables outside our café?",
        "a": "Usually, yes. Tables and chairs on the highway need a pavement licence from your council, such as Richmond or Lambeth, and fees are capped at £500 for a first application and £350 for a renewal. Check your council's conditions before you mention outdoor seating on Google or your website, and update the wording if your licence changes."
      },
      {
        "q": "What does the Local Presence Plan cost for a café, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Richmond, Putney, Wimbledon and Lambeth, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/clinic-medical-dental-marketing-sw-london/": {
    "section": "C-D2",
    "h1": "Dental practice marketing in SW London",
    "title": "Dental practice marketing in SW London",
    "description": "Dental marketing in SW London and Surrey: Wimbledon, Sutton, Kingston, Lambeth and Epsom. NHS and private enquiries, with GDC and ASA guidance in mind.",
    "answerFirst": "Dental practice marketing in SW London starts with telling patients plainly whether you take NHS patients, what private care you offer and how to book. We keep that information consistent across Google, your website and AI tools for practices in Wimbledon, Kingston, Sutton and Lambeth, and for practices just over the border in Epsom and Woking.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>NHS South West London ICB covers Kingston, Merton, Richmond, Sutton and Wandsworth, according to its constitution. Source: <a href=\"https://www.southwestlondon.icb.nhs.uk/publications/constitution/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">NHS South West London ICB: Constitution</a>; <a href=\"https://www.southwestlondon.icb.nhs.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">NHS South West London Integrated Care Board</a>.</li>\n<li>Epsom and Ewell and Woking now sit within NHS Surrey and Sussex ICB. Source: <a href=\"https://www.surreysussex.icb.nhs.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">NHS Surrey and Sussex Integrated Care Board</a>.</li>\n<li>Patients use the NHS website's Find a dentist service to see which practices say they are taking on NHS patients. Source: <a href=\"https://www.nhs.uk/service-search/find-a-dentist\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">NHS website: Find a dentist</a>.</li>\n<li>The GDC's guidance on advertising applies to all dental advertising. Source: <a href=\"https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">General Dental Council: Guidance on advertising</a>.</li>\n<li>Dental service providers in England must be registered with the Care Quality Commission. Source: <a href=\"https://www.cqc.org.uk/guidance-regulation/dentists\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Care Quality Commission: Dentists, information for providers</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a dental practice in SW London market itself to patients?</h3>\n<p>Dental practice marketing in SW London starts with telling patients plainly whether you take NHS patients, what private care you offer and how to book. We keep that information consistent across Google, your website and AI tools for practices in Wimbledon, Kingston, Sutton and Lambeth, and for practices just over the border in Epsom and Woking.</p>\n<h3>Who is responsible for NHS dentistry in Wimbledon, Kingston and Sutton?</h3>\n<p>NHS South West London Integrated Care Board says it took over responsibility for dentistry in 2023, and its constitution covers Kingston, Merton, Sutton, Richmond and Wandsworth. We name it accurately on your page where patients ask about NHS access, and we keep the wording current if arrangements change.</p>\n<h3>Which NHS body covers a practice in Epsom or Woking?</h3>\n<p>NHS Surrey and Sussex Integrated Care Board. The ICB Establishment and Abolition Order 2026 lists Epsom and Ewell and Woking in its area from 1 April 2026. We name that board where patients ask about NHS access and update the wording if national arrangements change again.</p>\n<h3>Should we mention the NHS dental hubs?</h3>\n<p>Only if it helps patients you cannot see urgently. The SW London ICB says NHS dental hubs operate for urgent problems. If you do not offer urgent NHS appointments, a short, accurate signpost saves calls to reception and avoids implying a service you do not provide.</p>\n<h3>How do we show patients we are properly registered?</h3>\n<p>Make sure your practice details match your CQC listing, and give each clinician's GDC registration number so patients can confirm it on the GDC's online register (see the GDC online register), which covers dentists and dental care professionals such as hygienists, therapists and dental nurses. We add these details to your team and contact pages so patients and AI tools find consistent, checkable information.</p>\n<h3>What are the NHS dental charges in England?</h3>\n<p>NHS dental charges in England are set nationally, not by the practice. Band 1 is £27.90, Band 2 is £76.60 and Band 3 is £332.10 (NHS website, checked 28 October 2026). If you mention NHS charges, link to the NHS page rather than copying figures that change, and keep private fees clearly separate.</p>\n<h3>What does the Local Presence Plan cost for a dental practice, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Sutton, Kingston, Lambeth and Epsom, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleHtml": null,
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#webpage",
          "url": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/",
          "name": "Dental practice marketing in SW London",
          "headline": "Dental practice marketing in SW London",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Dental marketing in SW London and Surrey: Wimbledon, Sutton, Kingston, Lambeth and Epsom. NHS and private enquiries, with GDC and ASA guidance in mind.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "NHS South West London ICB: Constitution",
              "url": "https://www.southwestlondon.icb.nhs.uk/publications/constitution/"
            },
            {
              "@type": "CreativeWork",
              "name": "NHS South West London Integrated Care Board",
              "url": "https://www.southwestlondon.icb.nhs.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "NHS Surrey and Sussex Integrated Care Board",
              "url": "https://www.surreysussex.icb.nhs.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "NHS website: Find a dentist",
              "url": "https://www.nhs.uk/service-search/find-a-dentist"
            },
            {
              "@type": "CreativeWork",
              "name": "General Dental Council: Guidance on advertising",
              "url": "https://www.gdc-uk.org/standards-guidance/standards-and-guidance/gdc-guidance-for-dental-professionals/guidance-on-advertising"
            },
            {
              "@type": "CreativeWork",
              "name": "Care Quality Commission: Dentists, information for providers",
              "url": "https://www.cqc.org.uk/guidance-regulation/dentists"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#service",
          "name": "Dental practice marketing in SW London",
          "serviceType": "Local marketing and AI search visibility for dental practices",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Wandsworth"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Merton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            },
            {
              "@type": "Place",
              "name": "Woking"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Dental practices"
          },
          "offers": {
            "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Wandsworth"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Merton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            },
            {
              "@type": "Place",
              "name": "Woking"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Dental practice marketing in SW London",
              "item": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/clinic-medical-dental-marketing-sw-london/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a dental practice in SW London market itself to patients?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Dental practice marketing in SW London starts with telling patients plainly whether you take NHS patients, what private care you offer and how to book. We keep that information consistent across Google, your website and AI tools for practices in Wimbledon, Kingston, Sutton and Lambeth, and for practices just over the border in Epsom and Woking."
              }
            },
            {
              "@type": "Question",
              "name": "Who is responsible for NHS dentistry in Wimbledon, Kingston and Sutton?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "NHS South West London Integrated Care Board says it took over responsibility for dentistry in 2023, and its constitution covers Kingston, Merton, Sutton, Richmond and Wandsworth. We name it accurately on your page where patients ask about NHS access, and we keep the wording current if arrangements change."
              }
            },
            {
              "@type": "Question",
              "name": "Which NHS body covers a practice in Epsom or Woking?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "NHS Surrey and Sussex Integrated Care Board. The ICB Establishment and Abolition Order 2026 lists Epsom and Ewell and Woking in its area from 1 April 2026. We name that board where patients ask about NHS access and update the wording if national arrangements change again."
              }
            },
            {
              "@type": "Question",
              "name": "Should we mention the NHS dental hubs?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only if it helps patients you cannot see urgently. The SW London ICB says NHS dental hubs operate for urgent problems. If you do not offer urgent NHS appointments, a short, accurate signpost saves calls to reception and avoids implying a service you do not provide."
              }
            },
            {
              "@type": "Question",
              "name": "How do we show patients we are properly registered?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Make sure your practice details match your CQC listing, and give each clinician's GDC registration number so patients can confirm it on the GDC's online register (see the GDC online register), which covers dentists and dental care professionals such as hygienists, therapists and dental nurses. We add these details to your team and contact pages so patients and AI tools find consistent, checkable information."
              }
            },
            {
              "@type": "Question",
              "name": "What are the NHS dental charges in England?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "NHS dental charges in England are set nationally, not by the practice. Band 1 is £27.90, Band 2 is £76.60 and Band 3 is £332.10 (NHS website, checked 28 October 2026). If you mention NHS charges, link to the NHS page rather than copying figures that change, and keep private fees clearly separate."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a dental practice, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Sutton, Kingston, Lambeth and Epsom, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a dental practice in SW London market itself to patients?",
        "a": "Dental practice marketing in SW London starts with telling patients plainly whether you take NHS patients, what private care you offer and how to book. We keep that information consistent across Google, your website and AI tools for practices in Wimbledon, Kingston, Sutton and Lambeth, and for practices just over the border in Epsom and Woking."
      },
      {
        "q": "Who is responsible for NHS dentistry in Wimbledon, Kingston and Sutton?",
        "a": "NHS South West London Integrated Care Board says it took over responsibility for dentistry in 2023, and its constitution covers Kingston, Merton, Sutton, Richmond and Wandsworth. We name it accurately on your page where patients ask about NHS access, and we keep the wording current if arrangements change."
      },
      {
        "q": "Which NHS body covers a practice in Epsom or Woking?",
        "a": "NHS Surrey and Sussex Integrated Care Board. The ICB Establishment and Abolition Order 2026 lists Epsom and Ewell and Woking in its area from 1 April 2026. We name that board where patients ask about NHS access and update the wording if national arrangements change again."
      },
      {
        "q": "Should we mention the NHS dental hubs?",
        "a": "Only if it helps patients you cannot see urgently. The SW London ICB says NHS dental hubs operate for urgent problems. If you do not offer urgent NHS appointments, a short, accurate signpost saves calls to reception and avoids implying a service you do not provide."
      },
      {
        "q": "How do we show patients we are properly registered?",
        "a": "Make sure your practice details match your CQC listing, and give each clinician's GDC registration number so patients can confirm it on the GDC's online register (see the GDC online register), which covers dentists and dental care professionals such as hygienists, therapists and dental nurses. We add these details to your team and contact pages so patients and AI tools find consistent, checkable information."
      },
      {
        "q": "What are the NHS dental charges in England?",
        "a": "NHS dental charges in England are set nationally, not by the practice. Band 1 is £27.90, Band 2 is £76.60 and Band 3 is £332.10 (NHS website, checked 28 October 2026). If you mention NHS charges, link to the NHS page rather than copying figures that change, and keep private fees clearly separate."
      },
      {
        "q": "What does the Local Presence Plan cost for a dental practice, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Sutton, Kingston, Lambeth and Epsom, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/gym-marketing-putney-wimbledon-kingston/": {
    "section": "C-D3",
    "h1": "Gym marketing in Putney, Wimbledon and Kingston",
    "title": "Gym marketing in Putney, Wimbledon and Kingston",
    "description": "Gym marketing for independent gyms and studios in Putney, Wimbledon and Kingston: membership enquiries, free trials and class bookings.",
    "answerFirst": "Gym marketing in Putney, Wimbledon and Kingston should show people quickly what membership includes, when classes run and whether there is a trial. We keep your timetable, facilities and booking route consistent across Google, your website and AI tools, and we describe saunas, massage and outdoor sessions accurately, including the licences and permits they need.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>Paid fitness training in the Royal Parks, including Richmond Park, needs a licence from The Royal Parks. Source: <a href=\"https://www.royalparks.org.uk/commercial-licences/fitness-training-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Royal Parks: Fitness training licences</a>.</li>\n<li>Richmond Council sets its own rules for paid activities in its parks and open spaces. Source: <a href=\"https://www.richmond.gov.uk/services/parks_and_open_spaces/fitness_activities_in_parks/hold_an_activity_in_a_park_or_open_space\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Richmond Council: Hold an activity in a park or open space</a>.</li>\n<li>Wimbledon and Putney Commons are managed by their own conservators, who set rules for activities on the Commons. Source: <a href=\"https://www.wpcc.org.uk/governance/management-information\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Wimbledon and Putney Commons: Management information</a>.</li>\n<li>Massage and some treatments in London gyms need a special treatment licence from the borough, such as Kingston or Sutton. Source: <a href=\"https://www.kingston.gov.uk/business-and-licensing/licensing/massage-and-special-treatment-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Royal Borough of Kingston: Massage and special treatment licences</a>; <a href=\"https://www.sutton.gov.uk/businesses-and-licensing/licensing/massage-and-special-treatments-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">London Borough of Sutton: Massage and special treatments licence</a>.</li>\n<li>Weight-loss and slimming claims are tightly restricted under section 13 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/13.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 13: Weight control and slimming</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a gym in Putney, Wimbledon or Kingston market itself?</h3>\n<p>Gym marketing in Putney, Wimbledon and Kingston should show people quickly what membership includes, when classes run and whether there is a trial. We keep your timetable, facilities and booking route consistent across Google, your website and AI tools, and we describe saunas, massage and outdoor sessions accurately, including the licences and permits they need.</p>\n<h3>Our Sutton gym has a steam room. Does that change anything?</h3>\n<p>It can. Sutton lists sauna, spa and steam among its medium-risk special treatments under the London Local Authorities Act 1991. We check with you that the licence is in place before the steam room or any massage service features on your website and listings.</p>\n<h3>Can our coaches take members onto Putney's open spaces for conditioning work?</h3>\n<p>Only with a permit. Enable, which runs Wandsworth's parks and open spaces, issues permits for personal and group trainers with limits on group size and use of park furniture. We list each outdoor spot with the permit behind it, never a vague promise of training anywhere nearby.</p>\n<h3>Can we run bootcamps on Wimbledon Common?</h3>\n<p>Ask the Conservators first. Wimbledon and Putney Commons are managed under an Access Framework that weighs whether a use suits exercise and recreation, avoids damage and does not get in the way of other users. We add Commons sessions to your timetable only once that permission is confirmed.</p>\n<h3>Our Kingston gym is opening a second site in Woking with a sauna. What changes?</h3>\n<p>No. GOV.UK says special treatment licences, which cover saunas and massage, apply in Greater London. Woking is in Surrey, so that London regime does not apply. We describe your Woking facilities without borrowing London licensing wording, and check any other local requirements with you.</p>\n<h3>Do outdoor classes in Richmond Park or on Wimbledon Common need permission?</h3>\n<p>Usually, yes. Paid fitness training in the Royal Parks, including Richmond Park, needs a licence from The Royal Parks, and Wimbledon and Putney Commons and Richmond Council set their own rules for paid activities. Check each landowner's terms before you list outdoor sessions on Google or your timetable.</p>\n<h3>What does the Local Presence Plan cost for a gym, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Wimbledon and Kingston, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#webpage",
          "url": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/",
          "name": "Gym marketing in Putney, Wimbledon and Kingston",
          "headline": "Gym marketing in Putney, Wimbledon and Kingston",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Gym marketing for independent gyms and studios in Putney, Wimbledon and Kingston: membership enquiries, free trials and class bookings.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "The Royal Parks: Fitness training licences",
              "url": "https://www.royalparks.org.uk/commercial-licences/fitness-training-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Richmond Council: Hold an activity in a park or open space",
              "url": "https://www.richmond.gov.uk/services/parks_and_open_spaces/fitness_activities_in_parks/hold_an_activity_in_a_park_or_open_space"
            },
            {
              "@type": "CreativeWork",
              "name": "Wimbledon and Putney Commons: Management information",
              "url": "https://www.wpcc.org.uk/governance/management-information"
            },
            {
              "@type": "CreativeWork",
              "name": "Royal Borough of Kingston: Massage and special treatment licences",
              "url": "https://www.kingston.gov.uk/business-and-licensing/licensing/massage-and-special-treatment-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "London Borough of Sutton: Massage and special treatments licence",
              "url": "https://www.sutton.gov.uk/businesses-and-licensing/licensing/massage-and-special-treatments-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 13: Weight control and slimming",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/13.html"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#service",
          "name": "Gym marketing in Putney, Wimbledon and Kingston",
          "serviceType": "Local marketing and AI search visibility for gyms",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "Place",
              "name": "Woking"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Gyms"
          },
          "offers": {
            "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "Place",
              "name": "Woking"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Gym marketing in Putney, Wimbledon and Kingston",
              "item": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/gym-marketing-putney-wimbledon-kingston/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a gym in Putney, Wimbledon or Kingston market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Gym marketing in Putney, Wimbledon and Kingston should show people quickly what membership includes, when classes run and whether there is a trial. We keep your timetable, facilities and booking route consistent across Google, your website and AI tools, and we describe saunas, massage and outdoor sessions accurately, including the licences and permits they need."
              }
            },
            {
              "@type": "Question",
              "name": "Our Sutton gym has a steam room. Does that change anything?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It can. Sutton lists sauna, spa and steam among its medium-risk special treatments under the London Local Authorities Act 1991. We check with you that the licence is in place before the steam room or any massage service features on your website and listings."
              }
            },
            {
              "@type": "Question",
              "name": "Can our coaches take members onto Putney's open spaces for conditioning work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only with a permit. Enable, which runs Wandsworth's parks and open spaces, issues permits for personal and group trainers with limits on group size and use of park furniture. We list each outdoor spot with the permit behind it, never a vague promise of training anywhere nearby."
              }
            },
            {
              "@type": "Question",
              "name": "Can we run bootcamps on Wimbledon Common?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ask the Conservators first. Wimbledon and Putney Commons are managed under an Access Framework that weighs whether a use suits exercise and recreation, avoids damage and does not get in the way of other users. We add Commons sessions to your timetable only once that permission is confirmed."
              }
            },
            {
              "@type": "Question",
              "name": "Our Kingston gym is opening a second site in Woking with a sauna. What changes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. GOV.UK says special treatment licences, which cover saunas and massage, apply in Greater London. Woking is in Surrey, so that London regime does not apply. We describe your Woking facilities without borrowing London licensing wording, and check any other local requirements with you."
              }
            },
            {
              "@type": "Question",
              "name": "Do outdoor classes in Richmond Park or on Wimbledon Common need permission?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Usually, yes. Paid fitness training in the Royal Parks, including Richmond Park, needs a licence from The Royal Parks, and Wimbledon and Putney Commons and Richmond Council set their own rules for paid activities. Check each landowner's terms before you list outdoor sessions on Google or your timetable."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a gym, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Wimbledon and Kingston, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a gym in Putney, Wimbledon or Kingston market itself?",
        "a": "Gym marketing in Putney, Wimbledon and Kingston should show people quickly what membership includes, when classes run and whether there is a trial. We keep your timetable, facilities and booking route consistent across Google, your website and AI tools, and we describe saunas, massage and outdoor sessions accurately, including the licences and permits they need."
      },
      {
        "q": "Our Sutton gym has a steam room. Does that change anything?",
        "a": "It can. Sutton lists sauna, spa and steam among its medium-risk special treatments under the London Local Authorities Act 1991. We check with you that the licence is in place before the steam room or any massage service features on your website and listings."
      },
      {
        "q": "Can our coaches take members onto Putney's open spaces for conditioning work?",
        "a": "Only with a permit. Enable, which runs Wandsworth's parks and open spaces, issues permits for personal and group trainers with limits on group size and use of park furniture. We list each outdoor spot with the permit behind it, never a vague promise of training anywhere nearby."
      },
      {
        "q": "Can we run bootcamps on Wimbledon Common?",
        "a": "Ask the Conservators first. Wimbledon and Putney Commons are managed under an Access Framework that weighs whether a use suits exercise and recreation, avoids damage and does not get in the way of other users. We add Commons sessions to your timetable only once that permission is confirmed."
      },
      {
        "q": "Our Kingston gym is opening a second site in Woking with a sauna. What changes?",
        "a": "No. GOV.UK says special treatment licences, which cover saunas and massage, apply in Greater London. Woking is in Surrey, so that London regime does not apply. We describe your Woking facilities without borrowing London licensing wording, and check any other local requirements with you."
      },
      {
        "q": "Do outdoor classes in Richmond Park or on Wimbledon Common need permission?",
        "a": "Usually, yes. Paid fitness training in the Royal Parks, including Richmond Park, needs a licence from The Royal Parks, and Wimbledon and Putney Commons and Richmond Council set their own rules for paid activities. Check each landowner's terms before you list outdoor sessions on Google or your timetable."
      },
      {
        "q": "What does the Local Presence Plan cost for a gym, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Wimbledon and Kingston, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/law-firm-solicitor-marketing-sw-london-surrey/": {
    "section": "C-D4",
    "h1": "Solicitor marketing in SW London and Surrey",
    "title": "Solicitor marketing in SW London and Surrey",
    "description": "Solicitor marketing for high-street firms in Wimbledon, Kingston, Guildford, Epsom, Esher and Weybridge: SRA Transparency pages, Google Maps and reviews.",
    "answerFirst": "Solicitor marketing in SW London and Surrey means making it easy for local clients to see what you handle, what it may cost and how to reach you, with SRA information in the right place. We keep your services, Transparency Rules pages and office details consistent across Google, your website and AI tools for high-street and market-town firms.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>The SRA Transparency Rules require firms offering certain services to publish price information on their website, including whether VAT is included, and to display the SRA's clickable logo. Source: <a href=\"https://www.sra.org.uk/solicitors/standards-regulations/transparency-rules/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">SRA: Transparency Rules</a>.</li>\n<li>Clients can check a solicitor or firm on the SRA's Solicitor check register. Source: <a href=\"https://www.sra.org.uk/consumers/solicitor-check/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">SRA: Solicitor check</a>.</li>\n<li>Local court details, such as Kingston and Guildford, are published on GOV.UK's Find a court or tribunal service. Source: <a href=\"https://www.find-court-tribunal.service.gov.uk/courts/kingston-upon-thames-crown-court\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">HMCTS: Kingston-upon-Thames Crown Court</a>; <a href=\"https://www.find-court-tribunal.service.gov.uk/courts/kingston-upon-thames-county-court-and-family-court\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">HMCTS: Kingston-upon-Thames County Court and Family Court</a>; <a href=\"https://www.find-court-tribunal.service.gov.uk/courts/guildford-county-court-and-family-court\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">HMCTS: Guildford County Court and Family Court</a>.</li>\n<li>Surrey Law Society represents solicitors practising in Surrey. Source: <a href=\"https://www.surreylawsociety.org.uk/about-us/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Surrey Law Society</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a law firm in SW London or Surrey market itself?</h3>\n<p>Solicitor marketing in SW London and Surrey means making it easy for local clients to see what you handle, what it may cost and how to reach you, with SRA information in the right place. We keep your services, Transparency Rules pages and office details consistent across Google, your website and AI tools for high-street and market-town firms.</p>\n<h3>Should our pages mention Kingston or Guildford county courts?</h3>\n<p>Only where it is true. GOV.UK lists Kingston upon Thames County Court and Family Court and Guildford County Court and Family Court, with the matters each handles. If you regularly act there, naming the court on the relevant practice page gives local clients a concrete, checkable detail.</p>\n<h3>We do criminal defence in Kingston. Should we name the Crown Court?</h3>\n<p>If your team regularly appears there, yes. GOV.UK lists Kingston upon Thames Crown Court as handling crime. Naming it on your crime page helps clients and AI tools understand where you work. We never suggest outcomes or imply any special standing with the court.</p>\n<h3>Is Surrey Law Society membership worth mentioning?</h3>\n<p>If you are a member, yes, briefly. Surrey Law Society describes itself as a membership organisation for Surrey solicitors and firms. We name memberships only when you hold them, and we avoid suggesting any membership is a regulatory approval. Regulation of your firm still sits with the SRA, wherever your office is.</p>\n<h3>What must every office's pages show under the SRA rules?</h3>\n<p>The SRA Transparency Rules cover cost information for certain services, your complaints procedure and your SRA number and badge. We place these where clients of each office will find them, consistent across your site, profiles and the information AI tools read.</p>\n<h3>What do the SRA Transparency Rules ask a firm to publish?</h3>\n<p>For certain services, such as residential conveyancing, uncontested probate and some employment tribunal claims, firms must publish price information on their website, including the total cost or the basis for charging and whether VAT is included, and details of who will do the work. Firms must also display the SRA's clickable logo.</p>\n<h3>What does the Local Presence Plan cost for a law firm, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Kingston, Guildford, Epsom, Esher and Weybridge, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#webpage",
          "url": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/",
          "name": "Solicitor marketing in SW London and Surrey",
          "headline": "Solicitor marketing in SW London and Surrey",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Solicitor marketing for high-street firms in Wimbledon, Kingston, Guildford, Epsom, Esher and Weybridge: SRA Transparency pages, Google Maps and reviews.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "SRA: Transparency Rules",
              "url": "https://www.sra.org.uk/solicitors/standards-regulations/transparency-rules/"
            },
            {
              "@type": "CreativeWork",
              "name": "SRA: Solicitor check",
              "url": "https://www.sra.org.uk/consumers/solicitor-check/"
            },
            {
              "@type": "CreativeWork",
              "name": "HMCTS: Kingston-upon-Thames Crown Court",
              "url": "https://www.find-court-tribunal.service.gov.uk/courts/kingston-upon-thames-crown-court"
            },
            {
              "@type": "CreativeWork",
              "name": "HMCTS: Kingston-upon-Thames County Court and Family Court",
              "url": "https://www.find-court-tribunal.service.gov.uk/courts/kingston-upon-thames-county-court-and-family-court"
            },
            {
              "@type": "CreativeWork",
              "name": "HMCTS: Guildford County Court and Family Court",
              "url": "https://www.find-court-tribunal.service.gov.uk/courts/guildford-county-court-and-family-court"
            },
            {
              "@type": "CreativeWork",
              "name": "Surrey Law Society",
              "url": "https://www.surreylawsociety.org.uk/about-us/"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#service",
          "name": "Solicitor marketing in SW London and Surrey",
          "serviceType": "Local marketing and AI search visibility for solicitors",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            },
            {
              "@type": "Place",
              "name": "Esher"
            },
            {
              "@type": "Place",
              "name": "Weybridge",
              "sameAs": "https://www.wikidata.org/wiki/Q1754096"
            },
            {
              "@type": "Place",
              "name": "Woking"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Solicitors"
          },
          "offers": {
            "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            },
            {
              "@type": "Place",
              "name": "Esher"
            },
            {
              "@type": "Place",
              "name": "Weybridge",
              "sameAs": "https://www.wikidata.org/wiki/Q1754096"
            },
            {
              "@type": "Place",
              "name": "Woking"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Solicitor marketing in SW London and Surrey",
              "item": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/law-firm-solicitor-marketing-sw-london-surrey/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a law firm in SW London or Surrey market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Solicitor marketing in SW London and Surrey means making it easy for local clients to see what you handle, what it may cost and how to reach you, with SRA information in the right place. We keep your services, Transparency Rules pages and office details consistent across Google, your website and AI tools for high-street and market-town firms."
              }
            },
            {
              "@type": "Question",
              "name": "Should our pages mention Kingston or Guildford county courts?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only where it is true. GOV.UK lists Kingston upon Thames County Court and Family Court and Guildford County Court and Family Court, with the matters each handles. If you regularly act there, naming the court on the relevant practice page gives local clients a concrete, checkable detail."
              }
            },
            {
              "@type": "Question",
              "name": "We do criminal defence in Kingston. Should we name the Crown Court?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "If your team regularly appears there, yes. GOV.UK lists Kingston upon Thames Crown Court as handling crime. Naming it on your crime page helps clients and AI tools understand where you work. We never suggest outcomes or imply any special standing with the court."
              }
            },
            {
              "@type": "Question",
              "name": "Is Surrey Law Society membership worth mentioning?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "If you are a member, yes, briefly. Surrey Law Society describes itself as a membership organisation for Surrey solicitors and firms. We name memberships only when you hold them, and we avoid suggesting any membership is a regulatory approval. Regulation of your firm still sits with the SRA, wherever your office is."
              }
            },
            {
              "@type": "Question",
              "name": "What must every office's pages show under the SRA rules?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The SRA Transparency Rules cover cost information for certain services, your complaints procedure and your SRA number and badge. We place these where clients of each office will find them, consistent across your site, profiles and the information AI tools read."
              }
            },
            {
              "@type": "Question",
              "name": "What do the SRA Transparency Rules ask a firm to publish?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For certain services, such as residential conveyancing, uncontested probate and some employment tribunal claims, firms must publish price information on their website, including the total cost or the basis for charging and whether VAT is included, and details of who will do the work. Firms must also display the SRA's clickable logo."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a law firm, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Kingston, Guildford, Epsom, Esher and Weybridge, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a law firm in SW London or Surrey market itself?",
        "a": "Solicitor marketing in SW London and Surrey means making it easy for local clients to see what you handle, what it may cost and how to reach you, with SRA information in the right place. We keep your services, Transparency Rules pages and office details consistent across Google, your website and AI tools for high-street and market-town firms."
      },
      {
        "q": "Should our pages mention Kingston or Guildford county courts?",
        "a": "Only where it is true. GOV.UK lists Kingston upon Thames County Court and Family Court and Guildford County Court and Family Court, with the matters each handles. If you regularly act there, naming the court on the relevant practice page gives local clients a concrete, checkable detail."
      },
      {
        "q": "We do criminal defence in Kingston. Should we name the Crown Court?",
        "a": "If your team regularly appears there, yes. GOV.UK lists Kingston upon Thames Crown Court as handling crime. Naming it on your crime page helps clients and AI tools understand where you work. We never suggest outcomes or imply any special standing with the court."
      },
      {
        "q": "Is Surrey Law Society membership worth mentioning?",
        "a": "If you are a member, yes, briefly. Surrey Law Society describes itself as a membership organisation for Surrey solicitors and firms. We name memberships only when you hold them, and we avoid suggesting any membership is a regulatory approval. Regulation of your firm still sits with the SRA, wherever your office is."
      },
      {
        "q": "What must every office's pages show under the SRA rules?",
        "a": "The SRA Transparency Rules cover cost information for certain services, your complaints procedure and your SRA number and badge. We place these where clients of each office will find them, consistent across your site, profiles and the information AI tools read."
      },
      {
        "q": "What do the SRA Transparency Rules ask a firm to publish?",
        "a": "For certain services, such as residential conveyancing, uncontested probate and some employment tribunal claims, firms must publish price information on their website, including the total cost or the basis for charging and whether VAT is included, and details of who will do the work. Firms must also display the SRA's clickable logo."
      },
      {
        "q": "What does the Local Presence Plan cost for a law firm, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Kingston, Guildford, Epsom, Esher and Weybridge, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/pilates-studio-marketing-sw-london-surrey/": {
    "section": "C-D5",
    "h1": "Pilates studio marketing in Putney, Richmond and Wimbledon",
    "title": "Pilates studio marketing in Putney, Richmond and Wimbledon",
    "description": "Pilates studio marketing for reformer and mat studios in Putney, Richmond and Wimbledon: class bookings, intro offers, park class permits and reviews.",
    "answerFirst": "Pilates studio marketing in SW London depends on clients finding the right class: reformer or mat, beginner or experienced, studio or outdoors. We keep your timetable, intro offer and booking route consistent across Google, your website and AI tools for studios in Putney, Richmond and Wimbledon, including any classes you run outdoors there.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>Paid fitness classes in the Royal Parks, including Richmond Park, need a fitness training licence from The Royal Parks. Source: <a href=\"https://www.royalparks.org.uk/commercial-licences/fitness-training-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">The Royal Parks: Fitness training licences</a>.</li>\n<li>Richmond Council sets its own rules for paid activities in its parks and open spaces. Source: <a href=\"https://www.richmond.gov.uk/services/parks_and_open_spaces/fitness_activities_in_parks/hold_an_activity_in_a_park_or_open_space\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Richmond Council: Hold an activity in a park or open space</a>.</li>\n<li>Wimbledon and Putney Commons are managed by their own conservators, who set rules for activities on the Commons. Source: <a href=\"https://www.wpcc.org.uk/governance/management-information\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Wimbledon and Putney Commons: Management information</a>.</li>\n<li>Health claims in ads, such as treating back pain, need evidence under section 12 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a pilates studio in SW London or Surrey market its classes?</h3>\n<p>Pilates studio marketing in SW London depends on clients finding the right class: reformer or mat, beginner or experienced, studio or outdoors. We keep your timetable, intro offer and booking route consistent across Google, your website and AI tools for studios in Putney, Richmond and Wimbledon, including any classes you run outdoors there.</p>\n<h3>We want a summer mat class on the grass near Richmond. Where do we start?</h3>\n<p>First work out who owns the grass. Richmond Park is a Royal Park with its own fitness licence, while other open spaces belong to Richmond council, which issues its own permits from April. Once the right permission is confirmed, we add the class, the meeting point and what to bring to your timetable.</p>\n<h3>Does a Putney studio need anything to teach in a nearby park?</h3>\n<p>A group permit from Enable, which manages Wandsworth's parks and open spaces and fixes the day, time and place on each permit. We copy those details exactly into your timetable, so a client turning up for mat Pilates finds the class where the permit says it will be.</p>\n<h3>Who decides whether a class can meet on the Commons near Wimbledon village?</h3>\n<p>The Conservators of Wimbledon and Putney Commons, not Merton or Enable. Their published framework puts recreation first and protects the Commons' natural features, so a small, quiet mat class is the kind of request to describe carefully. Once they agree, we publish the class with the exact meeting point.</p>\n<h3>How do we show which studio runs reformer and which runs mat?</h3>\n<p>Give each site its own short timetable block naming the machines, levels and class lengths it offers. A client in Richmond looking for a beginner reformer slot should see straight away whether that studio has one, or whether another of your studios is the better choice that week.</p>\n<h3>Can we say pilates helps with back pain?</h3>\n<p>Only with care. Section 12 of the CAP Code requires evidence for health claims and stops ads discouraging people from seeking medical advice. Describe what a class involves, its level and who teaches it, and avoid saying pilates treats or cures a condition unless you hold evidence the ASA would accept.</p>\n<h3>What does the Local Presence Plan cost for a Pilates studio, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Richmond and Wimbledon, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#webpage",
          "url": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/",
          "name": "Pilates studio marketing in Putney, Richmond and Wimbledon",
          "headline": "Pilates studio marketing in Putney, Richmond and Wimbledon",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Pilates studio marketing for reformer and mat studios in Putney, Richmond and Wimbledon: class bookings, intro offers, park class permits and reviews.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "The Royal Parks: Fitness training licences",
              "url": "https://www.royalparks.org.uk/commercial-licences/fitness-training-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Richmond Council: Hold an activity in a park or open space",
              "url": "https://www.richmond.gov.uk/services/parks_and_open_spaces/fitness_activities_in_parks/hold_an_activity_in_a_park_or_open_space"
            },
            {
              "@type": "CreativeWork",
              "name": "Wimbledon and Putney Commons: Management information",
              "url": "https://www.wpcc.org.uk/governance/management-information"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#service",
          "name": "Pilates studio marketing in Putney, Richmond and Wimbledon",
          "serviceType": "Local marketing and AI search visibility for Pilates studios",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Pilates studios"
          },
          "offers": {
            "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Pilates studio marketing in Putney, Richmond and Wimbledon",
              "item": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/pilates-studio-marketing-sw-london-surrey/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a pilates studio in SW London or Surrey market its classes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Pilates studio marketing in SW London depends on clients finding the right class: reformer or mat, beginner or experienced, studio or outdoors. We keep your timetable, intro offer and booking route consistent across Google, your website and AI tools for studios in Putney, Richmond and Wimbledon, including any classes you run outdoors there."
              }
            },
            {
              "@type": "Question",
              "name": "We want a summer mat class on the grass near Richmond. Where do we start?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "First work out who owns the grass. Richmond Park is a Royal Park with its own fitness licence, while other open spaces belong to Richmond council, which issues its own permits from April. Once the right permission is confirmed, we add the class, the meeting point and what to bring to your timetable."
              }
            },
            {
              "@type": "Question",
              "name": "Does a Putney studio need anything to teach in a nearby park?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A group permit from Enable, which manages Wandsworth's parks and open spaces and fixes the day, time and place on each permit. We copy those details exactly into your timetable, so a client turning up for mat Pilates finds the class where the permit says it will be."
              }
            },
            {
              "@type": "Question",
              "name": "Who decides whether a class can meet on the Commons near Wimbledon village?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Conservators of Wimbledon and Putney Commons, not Merton or Enable. Their published framework puts recreation first and protects the Commons' natural features, so a small, quiet mat class is the kind of request to describe carefully. Once they agree, we publish the class with the exact meeting point."
              }
            },
            {
              "@type": "Question",
              "name": "How do we show which studio runs reformer and which runs mat?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Give each site its own short timetable block naming the machines, levels and class lengths it offers. A client in Richmond looking for a beginner reformer slot should see straight away whether that studio has one, or whether another of your studios is the better choice that week."
              }
            },
            {
              "@type": "Question",
              "name": "Can we say pilates helps with back pain?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Only with care. Section 12 of the CAP Code requires evidence for health claims and stops ads discouraging people from seeking medical advice. Describe what a class involves, its level and who teaches it, and avoid saying pilates treats or cures a condition unless you hold evidence the ASA would accept."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a Pilates studio, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Richmond and Wimbledon, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a pilates studio in SW London or Surrey market its classes?",
        "a": "Pilates studio marketing in SW London depends on clients finding the right class: reformer or mat, beginner or experienced, studio or outdoors. We keep your timetable, intro offer and booking route consistent across Google, your website and AI tools for studios in Putney, Richmond and Wimbledon, including any classes you run outdoors there."
      },
      {
        "q": "We want a summer mat class on the grass near Richmond. Where do we start?",
        "a": "First work out who owns the grass. Richmond Park is a Royal Park with its own fitness licence, while other open spaces belong to Richmond council, which issues its own permits from April. Once the right permission is confirmed, we add the class, the meeting point and what to bring to your timetable."
      },
      {
        "q": "Does a Putney studio need anything to teach in a nearby park?",
        "a": "A group permit from Enable, which manages Wandsworth's parks and open spaces and fixes the day, time and place on each permit. We copy those details exactly into your timetable, so a client turning up for mat Pilates finds the class where the permit says it will be."
      },
      {
        "q": "Who decides whether a class can meet on the Commons near Wimbledon village?",
        "a": "The Conservators of Wimbledon and Putney Commons, not Merton or Enable. Their published framework puts recreation first and protects the Commons' natural features, so a small, quiet mat class is the kind of request to describe carefully. Once they agree, we publish the class with the exact meeting point."
      },
      {
        "q": "How do we show which studio runs reformer and which runs mat?",
        "a": "Give each site its own short timetable block naming the machines, levels and class lengths it offers. A client in Richmond looking for a beginner reformer slot should see straight away whether that studio has one, or whether another of your studios is the better choice that week."
      },
      {
        "q": "Can we say pilates helps with back pain?",
        "a": "Only with care. Section 12 of the CAP Code requires evidence for health claims and stops ads discouraging people from seeking medical advice. Describe what a class involves, its level and who teaches it, and avoid saying pilates treats or cures a condition unless you hold evidence the ASA would accept."
      },
      {
        "q": "What does the Local Presence Plan cost for a Pilates studio, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Richmond and Wimbledon, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/restaurant-marketing-richmond-kingston-guildford/": {
    "section": "C-D6",
    "h1": "Restaurant marketing in Richmond, Kingston and Guildford",
    "title": "Restaurant marketing in Richmond, Kingston and Guildford",
    "description": "Restaurant marketing for independents in Richmond, Kingston, Guildford, Esher and Weybridge: table bookings, Google Maps, hygiene ratings and reviews.",
    "answerFirst": "Restaurant marketing in Richmond and Kingston, and in Guildford, is about turning searches into direct bookings: clear menus, accurate hours, an obvious booking link and honest details about outdoor tables and allergens. We keep these consistent across Google, your website and AI tools, so diners book with you directly rather than through someone else's listing.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>Richmond, Kingston, Guildford and Elmbridge councils issue pavement licences for outdoor tables; fees are capped nationally at £500 for a first application. Source: <a href=\"https://www.richmond.gov.uk/services/business/services_for_business/business_and_street_trading_licences/pavement_licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Richmond Council: Pavement licence</a>; <a href=\"https://www.kingston.gov.uk/business-and-licensing/licensing/street-trading-licences/pavement-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Royal Borough of Kingston: Pavement licence</a>; <a href=\"https://www.guildford.gov.uk/pavementlicence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Guildford Borough Council: Pavement licence</a>; <a href=\"https://www.elmbridge.gov.uk/licensing/pavement-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Elmbridge Borough Council: Pavement licences</a>; <a href=\"https://www.gov.uk/government/publications/pavement-licences-guidance\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Pavement licences guidance</a>.</li>\n<li>Selling alcohol needs a premises licence from the council, which sets your licensed hours. Source: <a href=\"https://www.gov.uk/find-licences/premises-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Premises licence (England and Wales)</a>.</li>\n<li>Food businesses must give customers information about any of the 14 listed allergens used as ingredients. Source: <a href=\"https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Allergen guidance for food businesses</a>.</li>\n<li>Food hygiene ratings are published on the FSA ratings site. Source: <a href=\"https://ratings.food.gov.uk/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">FSA: Food hygiene ratings search</a>; <a href=\"https://www.food.gov.uk/safety-hygiene/food-hygiene-rating-scheme\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Food Standards Agency: Food Hygiene Rating Scheme</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a restaurant in Richmond, Kingston or Guildford market itself?</h3>\n<p>Restaurant marketing in Richmond and Kingston, and in Guildford, is about turning searches into direct bookings: clear menus, accurate hours, an obvious booking link and honest details about outdoor tables and allergens. We keep these consistent across Google, your website and AI tools, so diners book with you directly rather than through someone else's listing.</p>\n<h3>Can we advertise our terrace before the licence comes through?</h3>\n<p>No. Richmond, Kingston and Guildford each run pavement licence schemes for outdoor tables, and Kingston requires furniture to be removed at the end of each day. We describe outdoor seating on your site only once you hold the licence, and in the terms it was granted.</p>\n<h3>Who inspects a restaurant in Esher or Weybridge?</h3>\n<p>Elmbridge Borough Council, which covers both towns. The FSA explains that local authorities carry out the inspections and issue the ratings. We name the right council on your page and link to the official listing rather than repeating a rating diners can check for themselves.</p>\n<h3>Our Weybridge restaurant wants tables outside. What should we check?</h3>\n<p>Whether the pavement is public highway. Elmbridge says its pavement licence only covers highway land and suggests checking with Surrey County Council's highways team, because a licence for other land is invalid. Once confirmed and granted, we add the terrace to your listings.</p>\n<h3>Do our website hours need to match our premises licence?</h3>\n<p>Yes. Your council's licensing team sets what your premises licence permits. Advertising later service than your licence allows misleads diners and can cause problems with the council. We check your hours against what you tell us your licence says before publishing them.</p>\n<h3>Which licences should a restaurant mention online?</h3>\n<p>Mention the ones customers act on: whether you serve alcohol and until when, which your premises licence sets, and whether you have outdoor tables, which need a pavement licence from Richmond, Kingston or Guildford council. Keep the wording matched to the licence conditions and update it whenever they change.</p>\n<h3>What does the Local Presence Plan cost for a restaurant, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Richmond, Kingston and Guildford, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#webpage",
          "url": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/",
          "name": "Restaurant marketing in Richmond, Kingston and Guildford",
          "headline": "Restaurant marketing in Richmond, Kingston and Guildford",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Restaurant marketing for independents in Richmond, Kingston, Guildford, Esher and Weybridge: table bookings, Google Maps, hygiene ratings and reviews.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Richmond Council: Pavement licence",
              "url": "https://www.richmond.gov.uk/services/business/services_for_business/business_and_street_trading_licences/pavement_licence"
            },
            {
              "@type": "CreativeWork",
              "name": "Royal Borough of Kingston: Pavement licence",
              "url": "https://www.kingston.gov.uk/business-and-licensing/licensing/street-trading-licences/pavement-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "Guildford Borough Council: Pavement licence",
              "url": "https://www.guildford.gov.uk/pavementlicence"
            },
            {
              "@type": "CreativeWork",
              "name": "Elmbridge Borough Council: Pavement licences",
              "url": "https://www.elmbridge.gov.uk/licensing/pavement-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Pavement licences guidance",
              "url": "https://www.gov.uk/government/publications/pavement-licences-guidance"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Premises licence (England and Wales)",
              "url": "https://www.gov.uk/find-licences/premises-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Allergen guidance for food businesses",
              "url": "https://www.gov.uk/government/publications/allergen-guidance-for-food-businesses"
            },
            {
              "@type": "CreativeWork",
              "name": "FSA: Food hygiene ratings search",
              "url": "https://ratings.food.gov.uk/"
            },
            {
              "@type": "CreativeWork",
              "name": "Food Standards Agency: Food Hygiene Rating Scheme",
              "url": "https://www.food.gov.uk/safety-hygiene/food-hygiene-rating-scheme"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#service",
          "name": "Restaurant marketing in Richmond, Kingston and Guildford",
          "serviceType": "Local marketing and AI search visibility for restaurants",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Esher"
            },
            {
              "@type": "Place",
              "name": "Weybridge",
              "sameAs": "https://www.wikidata.org/wiki/Q1754096"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Restaurants"
          },
          "offers": {
            "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Esher"
            },
            {
              "@type": "Place",
              "name": "Weybridge",
              "sameAs": "https://www.wikidata.org/wiki/Q1754096"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Restaurant marketing in Richmond, Kingston and Guildford",
              "item": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/restaurant-marketing-richmond-kingston-guildford/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a restaurant in Richmond, Kingston or Guildford market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Restaurant marketing in Richmond and Kingston, and in Guildford, is about turning searches into direct bookings: clear menus, accurate hours, an obvious booking link and honest details about outdoor tables and allergens. We keep these consistent across Google, your website and AI tools, so diners book with you directly rather than through someone else's listing."
              }
            },
            {
              "@type": "Question",
              "name": "Can we advertise our terrace before the licence comes through?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Richmond, Kingston and Guildford each run pavement licence schemes for outdoor tables, and Kingston requires furniture to be removed at the end of each day. We describe outdoor seating on your site only once you hold the licence, and in the terms it was granted."
              }
            },
            {
              "@type": "Question",
              "name": "Who inspects a restaurant in Esher or Weybridge?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Elmbridge Borough Council, which covers both towns. The FSA explains that local authorities carry out the inspections and issue the ratings. We name the right council on your page and link to the official listing rather than repeating a rating diners can check for themselves."
              }
            },
            {
              "@type": "Question",
              "name": "Our Weybridge restaurant wants tables outside. What should we check?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Whether the pavement is public highway. Elmbridge says its pavement licence only covers highway land and suggests checking with Surrey County Council's highways team, because a licence for other land is invalid. Once confirmed and granted, we add the terrace to your listings."
              }
            },
            {
              "@type": "Question",
              "name": "Do our website hours need to match our premises licence?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Your council's licensing team sets what your premises licence permits. Advertising later service than your licence allows misleads diners and can cause problems with the council. We check your hours against what you tell us your licence says before publishing them."
              }
            },
            {
              "@type": "Question",
              "name": "Which licences should a restaurant mention online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Mention the ones customers act on: whether you serve alcohol and until when, which your premises licence sets, and whether you have outdoor tables, which need a pavement licence from Richmond, Kingston or Guildford council. Keep the wording matched to the licence conditions and update it whenever they change."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a restaurant, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Richmond, Kingston and Guildford, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a restaurant in Richmond, Kingston or Guildford market itself?",
        "a": "Restaurant marketing in Richmond and Kingston, and in Guildford, is about turning searches into direct bookings: clear menus, accurate hours, an obvious booking link and honest details about outdoor tables and allergens. We keep these consistent across Google, your website and AI tools, so diners book with you directly rather than through someone else's listing."
      },
      {
        "q": "Can we advertise our terrace before the licence comes through?",
        "a": "No. Richmond, Kingston and Guildford each run pavement licence schemes for outdoor tables, and Kingston requires furniture to be removed at the end of each day. We describe outdoor seating on your site only once you hold the licence, and in the terms it was granted."
      },
      {
        "q": "Who inspects a restaurant in Esher or Weybridge?",
        "a": "Elmbridge Borough Council, which covers both towns. The FSA explains that local authorities carry out the inspections and issue the ratings. We name the right council on your page and link to the official listing rather than repeating a rating diners can check for themselves."
      },
      {
        "q": "Our Weybridge restaurant wants tables outside. What should we check?",
        "a": "Whether the pavement is public highway. Elmbridge says its pavement licence only covers highway land and suggests checking with Surrey County Council's highways team, because a licence for other land is invalid. Once confirmed and granted, we add the terrace to your listings."
      },
      {
        "q": "Do our website hours need to match our premises licence?",
        "a": "Yes. Your council's licensing team sets what your premises licence permits. Advertising later service than your licence allows misleads diners and can cause problems with the council. We check your hours against what you tell us your licence says before publishing them."
      },
      {
        "q": "Which licences should a restaurant mention online?",
        "a": "Mention the ones customers act on: whether you serve alcohol and until when, which your premises licence sets, and whether you have outdoor tables, which need a pavement licence from Richmond, Kingston or Guildford council. Keep the wording matched to the licence conditions and update it whenever they change."
      },
      {
        "q": "What does the Local Presence Plan cost for a restaurant, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Richmond, Kingston and Guildford, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/salon-beauty-marketing-sw-london-surrey/": {
    "section": "C-D7",
    "h1": "Salon marketing in SW London and Surrey",
    "title": "Salon marketing in SW London and Surrey",
    "description": "Salon marketing for hair, nail and beauty salons in Wimbledon, Richmond, Sutton, Lambeth and Guildford: bookings, reviews and special treatment licences.",
    "answerFirst": "Salon marketing in SW London and Surrey works when clients can see what you offer, when you are free and that your premises are properly licensed or registered for the treatments you advertise. We keep services, booking links and licence details consistent across Google, your website and AI tools for salons in London boroughs and in Surrey towns.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>London boroughs such as Wandsworth, Richmond, Kingston, Sutton and Lambeth license massage and special treatments. Source: <a href=\"https://www.wandsworth.gov.uk/business-and-licensing/licensing/special-treatment-licensing/special-treatment-establishment-licences/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Wandsworth Council: Special treatment establishment licences</a>; <a href=\"https://www.richmond.gov.uk/massage_and_special_treatments_licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Richmond Council: Massage and special treatments licences</a>; <a href=\"https://www.kingston.gov.uk/business-and-licensing/licensing/massage-and-special-treatment-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Royal Borough of Kingston: Massage and special treatment licences</a>; <a href=\"https://www.sutton.gov.uk/businesses-and-licensing/licensing/massage-and-special-treatments-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">London Borough of Sutton: Massage and special treatments licence</a>; <a href=\"https://www.lambeth.gov.uk/Business-rates-services-and-licensing/licensing-and-permits/Apply-change-or-pay-for-a-licence/Apply-for-a-treatment-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Lambeth Council: Apply for a treatment licence</a>.</li>\n<li>Outside London, skin piercing, tattooing, electrolysis and semi-permanent make-up need registration with the council, such as Elmbridge or Guildford. Source: <a href=\"https://www.gov.uk/find-licences/skin-piercing-and-tattooing\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Skin piercing and tattooing licence</a>; <a href=\"https://www.elmbridge.gov.uk/business/health-and-safety/business/health-and-safety/skin-or-body-piercing\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Elmbridge Borough Council: Skin or body piercing registration</a>; <a href=\"https://www.guildford.gov.uk/article/26813/What-you-need-to-know\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Guildford Borough Council: Skin piercing registration, what you need to know</a>.</li>\n<li>It is illegal in England to give Botox or dermal fillers for cosmetic purposes to anyone under 18. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2021/19/contents\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Botulinum Toxin and Cosmetic Fillers (Children) Act 2021</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a salon in SW London or Surrey market its treatments?</h3>\n<p>Salon marketing in SW London and Surrey works when clients can see what you offer, when you are free and that your premises are properly licensed or registered for the treatments you advertise. We keep services, booking links and licence details consistent across Google, your website and AI tools for salons in London boroughs and in Surrey towns.</p>\n<h3>Do salons in Putney, Sutton and Kingston need a special treatment licence?</h3>\n<p>For many treatments, yes. Wandsworth, Sutton and Kingston each say premises offering treatments such as manicures, massage or laser need a licence under the London Local Authorities Act 1991. We only advertise treatments your licence covers, and we suggest checking your council's list before adding a new one.</p>\n<h3>Is the rule the same for a salon in Guildford or Esher?</h3>\n<p>No. Both are in Surrey, so the London licensing Act does not apply. GOV.UK says that outside Greater London, work such as cosmetic piercing, semi-permanent make-up and electrolysis is registered with the council instead, and Guildford and Elmbridge publish their own pages. We match your page to the regime for your address.</p>\n<h3>Do we need a licence for treatments in clients' homes?</h3>\n<p>In Sutton, fully mobile services and treatments in clients' own homes are listed as exempt, and Wandsworth says the same about home treatments. If you also run a salon, the salon still needs its licence. We describe home visits and salon services as separate options so clients see the difference.</p>\n<h3>Can we list a new treatment before the council approves it?</h3>\n<p>No. Sutton says adding treatments means applying to vary your special treatment licence. Listing a treatment before that change is approved can mislead clients, so we keep your service list, booking system and Google profile in step with what your licence actually covers.</p>\n<h3>Does a salon in Surrey need the same licence as one in London?</h3>\n<p>No. Special treatment licensing is a Greater London regime, so boroughs such as Wandsworth, Richmond, Kingston, Sutton and Lambeth license massage and some beauty treatments. In Surrey towns there is usually no equivalent licence, but skin piercing, tattooing, electrolysis and semi-permanent make-up still need registration with the council.</p>\n<h3>What does the Local Presence Plan cost for a hair or beauty salon, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Richmond, Sutton, Lambeth and Guildford, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#webpage",
          "url": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/",
          "name": "Salon marketing in SW London and Surrey",
          "headline": "Salon marketing in SW London and Surrey",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Salon marketing for hair, nail and beauty salons in Wimbledon, Richmond, Sutton, Lambeth and Guildford: bookings, reviews and special treatment licences.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Wandsworth Council: Special treatment establishment licences",
              "url": "https://www.wandsworth.gov.uk/business-and-licensing/licensing/special-treatment-licensing/special-treatment-establishment-licences/"
            },
            {
              "@type": "CreativeWork",
              "name": "Richmond Council: Massage and special treatments licences",
              "url": "https://www.richmond.gov.uk/massage_and_special_treatments_licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Royal Borough of Kingston: Massage and special treatment licences",
              "url": "https://www.kingston.gov.uk/business-and-licensing/licensing/massage-and-special-treatment-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "London Borough of Sutton: Massage and special treatments licence",
              "url": "https://www.sutton.gov.uk/businesses-and-licensing/licensing/massage-and-special-treatments-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "Lambeth Council: Apply for a treatment licence",
              "url": "https://www.lambeth.gov.uk/Business-rates-services-and-licensing/licensing-and-permits/Apply-change-or-pay-for-a-licence/Apply-for-a-treatment-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Skin piercing and tattooing licence",
              "url": "https://www.gov.uk/find-licences/skin-piercing-and-tattooing"
            },
            {
              "@type": "CreativeWork",
              "name": "Elmbridge Borough Council: Skin or body piercing registration",
              "url": "https://www.elmbridge.gov.uk/business/health-and-safety/business/health-and-safety/skin-or-body-piercing"
            },
            {
              "@type": "CreativeWork",
              "name": "Guildford Borough Council: Skin piercing registration, what you need to know",
              "url": "https://www.guildford.gov.uk/article/26813/What-you-need-to-know"
            },
            {
              "@type": "CreativeWork",
              "name": "Botulinum Toxin and Cosmetic Fillers (Children) Act 2021",
              "url": "https://www.legislation.gov.uk/ukpga/2021/19/contents"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#service",
          "name": "Salon marketing in SW London and Surrey",
          "serviceType": "Local marketing and AI search visibility for salons",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Esher"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Salons"
          },
          "offers": {
            "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Esher"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Salon marketing in SW London and Surrey",
              "item": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/salon-beauty-marketing-sw-london-surrey/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a salon in SW London or Surrey market its treatments?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Salon marketing in SW London and Surrey works when clients can see what you offer, when you are free and that your premises are properly licensed or registered for the treatments you advertise. We keep services, booking links and licence details consistent across Google, your website and AI tools for salons in London boroughs and in Surrey towns."
              }
            },
            {
              "@type": "Question",
              "name": "Do salons in Putney, Sutton and Kingston need a special treatment licence?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For many treatments, yes. Wandsworth, Sutton and Kingston each say premises offering treatments such as manicures, massage or laser need a licence under the London Local Authorities Act 1991. We only advertise treatments your licence covers, and we suggest checking your council's list before adding a new one."
              }
            },
            {
              "@type": "Question",
              "name": "Is the rule the same for a salon in Guildford or Esher?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Both are in Surrey, so the London licensing Act does not apply. GOV.UK says that outside Greater London, work such as cosmetic piercing, semi-permanent make-up and electrolysis is registered with the council instead, and Guildford and Elmbridge publish their own pages. We match your page to the regime for your address."
              }
            },
            {
              "@type": "Question",
              "name": "Do we need a licence for treatments in clients' homes?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "In Sutton, fully mobile services and treatments in clients' own homes are listed as exempt, and Wandsworth says the same about home treatments. If you also run a salon, the salon still needs its licence. We describe home visits and salon services as separate options so clients see the difference."
              }
            },
            {
              "@type": "Question",
              "name": "Can we list a new treatment before the council approves it?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Sutton says adding treatments means applying to vary your special treatment licence. Listing a treatment before that change is approved can mislead clients, so we keep your service list, booking system and Google profile in step with what your licence actually covers."
              }
            },
            {
              "@type": "Question",
              "name": "Does a salon in Surrey need the same licence as one in London?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Special treatment licensing is a Greater London regime, so boroughs such as Wandsworth, Richmond, Kingston, Sutton and Lambeth license massage and some beauty treatments. In Surrey towns there is usually no equivalent licence, but skin piercing, tattooing, electrolysis and semi-permanent make-up still need registration with the council."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a hair or beauty salon, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Richmond, Sutton, Lambeth and Guildford, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a salon in SW London or Surrey market its treatments?",
        "a": "Salon marketing in SW London and Surrey works when clients can see what you offer, when you are free and that your premises are properly licensed or registered for the treatments you advertise. We keep services, booking links and licence details consistent across Google, your website and AI tools for salons in London boroughs and in Surrey towns."
      },
      {
        "q": "Do salons in Putney, Sutton and Kingston need a special treatment licence?",
        "a": "For many treatments, yes. Wandsworth, Sutton and Kingston each say premises offering treatments such as manicures, massage or laser need a licence under the London Local Authorities Act 1991. We only advertise treatments your licence covers, and we suggest checking your council's list before adding a new one."
      },
      {
        "q": "Is the rule the same for a salon in Guildford or Esher?",
        "a": "No. Both are in Surrey, so the London licensing Act does not apply. GOV.UK says that outside Greater London, work such as cosmetic piercing, semi-permanent make-up and electrolysis is registered with the council instead, and Guildford and Elmbridge publish their own pages. We match your page to the regime for your address."
      },
      {
        "q": "Do we need a licence for treatments in clients' homes?",
        "a": "In Sutton, fully mobile services and treatments in clients' own homes are listed as exempt, and Wandsworth says the same about home treatments. If you also run a salon, the salon still needs its licence. We describe home visits and salon services as separate options so clients see the difference."
      },
      {
        "q": "Can we list a new treatment before the council approves it?",
        "a": "No. Sutton says adding treatments means applying to vary your special treatment licence. Listing a treatment before that change is approved can mislead clients, so we keep your service list, booking system and Google profile in step with what your licence actually covers."
      },
      {
        "q": "Does a salon in Surrey need the same licence as one in London?",
        "a": "No. Special treatment licensing is a Greater London regime, so boroughs such as Wandsworth, Richmond, Kingston, Sutton and Lambeth license massage and some beauty treatments. In Surrey towns there is usually no equivalent licence, but skin piercing, tattooing, electrolysis and semi-permanent make-up still need registration with the council."
      },
      {
        "q": "What does the Local Presence Plan cost for a hair or beauty salon, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Wimbledon, Richmond, Sutton, Lambeth and Guildford, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/tanning-salon-marketing-sw-london-surrey/": {
    "section": "C-D8",
    "h1": "Tanning salon marketing in SW London and Surrey",
    "title": "Tanning salon marketing in SW London and Surrey",
    "description": "Tanning salon marketing in Sutton, Lambeth, Kingston, Richmond and Woking: bookings, reviews, borough sunbed licences and the under-18 rules.",
    "answerFirst": "Tanning salon marketing in SW London and Surrey has to be clear about two things: which services you offer, from spray tans to sunbeds, and the rules that apply to sunbed use. We keep your services, booking and licence details consistent across Google, your website and AI tools for salons in Sutton, Lambeth, Kingston, Richmond, Woking and Epsom.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>Under the Sunbeds (Regulation) Act 2010, a sunbed business must not let anyone under 18 use a sunbed on its premises. Source: <a href=\"https://www.legislation.gov.uk/ukpga/2010/20/section/2\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Sunbeds (Regulation) Act 2010, section 2</a>.</li>\n<li>Sutton, Kingston, Richmond and Lambeth license special treatments, which can include ultraviolet sunbeds. Source: <a href=\"https://www.sutton.gov.uk/businesses-and-licensing/licensing/massage-and-special-treatments-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">London Borough of Sutton: Massage and special treatments licence</a>; <a href=\"https://www.kingston.gov.uk/business-and-licensing/licensing/massage-and-special-treatment-licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Royal Borough of Kingston: Massage and special treatment licences</a>; <a href=\"https://www.richmond.gov.uk/massage_and_special_treatments_licences\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Richmond Council: Massage and special treatments licences</a>; <a href=\"https://www.lambeth.gov.uk/Business-rates-services-and-licensing/licensing-and-permits/Apply-change-or-pay-for-a-licence/Apply-for-a-treatment-licence\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Lambeth Council: Apply for a treatment licence</a>.</li>\n<li>Health-related claims in ads need evidence under section 12 of the CAP Code. Source: <a href=\"https://www.asa.org.uk/type/non_broadcast/code_section/12.html\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CAP Code section 12: Medicines, medical devices, health-related and beauty products</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should a tanning salon in SW London or Surrey market its services?</h3>\n<p>Tanning salon marketing in SW London and Surrey has to be clear about two things: which services you offer, from spray tans to sunbeds, and the rules that apply to sunbed use. We keep your services, booking and licence details consistent across Google, your website and AI tools for salons in Sutton, Lambeth, Kingston, Richmond, Woking and Epsom.</p>\n<h3>Which council licenses our sunbeds in Sutton, Lambeth or Kingston?</h3>\n<p>Your own borough. Sutton and Kingston list ultraviolet sunbeds as medium-risk special treatments, and Lambeth includes ultraviolet tanning in its special treatment bands, all under the London Local Authorities Act 1991. We only advertise sunbeds at a branch once its licence covers them.</p>\n<h3>What does the Sunbeds Act mean for our booking pages?</h3>\n<p>Say it before they book. The Sunbeds (Regulation) Act 2010 requires a sunbed business to stop anyone under 18 using a sunbed or being in the restricted zone. We add that wording to booking pages and FAQs so younger clients know before they arrive, and we avoid any health claims.</p>\n<h3>Does a salon in Woking or Epsom need a sunbed licence?</h3>\n<p>Not a London-style one. GOV.UK says special treatment licences are a Greater London regime, and the registration list outside London does not include sunbeds. The Sunbeds Act still applies everywhere in England. We suggest checking with your borough council and never describe your salon as licensed for sunbeds.</p>\n<h3>Should our Richmond salon explain record cards and goggles online?</h3>\n<p>Yes, briefly. Richmond's standard special treatment conditions include signed record cards, free eye protection and HSE guidance in each cubicle. Describing those steps on your site sets clear expectations for new clients. We present them as what happens at your salon, not as a promise about results.</p>\n<h3>What must a tanning salon say about age rules?</h3>\n<p>State clearly that sunbeds are for adults only. The Sunbeds (Regulation) Act 2010 bars businesses from letting anyone under 18 use a sunbed on their premises, so put the age policy on your Google Business Profile, booking page and price list, and say how you check age at the desk.</p>\n<h3>What does the Local Presence Plan cost for a tanning salon, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Sutton, Lambeth, Kingston, Richmond and Woking, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#webpage",
          "url": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/",
          "name": "Tanning salon marketing in SW London and Surrey",
          "headline": "Tanning salon marketing in SW London and Surrey",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Tanning salon marketing in Sutton, Lambeth, Kingston, Richmond and Woking: bookings, reviews, borough sunbed licences and the under-18 rules.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Sunbeds (Regulation) Act 2010, section 2",
              "url": "https://www.legislation.gov.uk/ukpga/2010/20/section/2"
            },
            {
              "@type": "CreativeWork",
              "name": "London Borough of Sutton: Massage and special treatments licence",
              "url": "https://www.sutton.gov.uk/businesses-and-licensing/licensing/massage-and-special-treatments-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "Royal Borough of Kingston: Massage and special treatment licences",
              "url": "https://www.kingston.gov.uk/business-and-licensing/licensing/massage-and-special-treatment-licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Richmond Council: Massage and special treatments licences",
              "url": "https://www.richmond.gov.uk/massage_and_special_treatments_licences"
            },
            {
              "@type": "CreativeWork",
              "name": "Lambeth Council: Apply for a treatment licence",
              "url": "https://www.lambeth.gov.uk/Business-rates-services-and-licensing/licensing-and-permits/Apply-change-or-pay-for-a-licence/Apply-for-a-treatment-licence"
            },
            {
              "@type": "CreativeWork",
              "name": "CAP Code section 12: Medicines, medical devices, health-related and beauty products",
              "url": "https://www.asa.org.uk/type/non_broadcast/code_section/12.html"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#service",
          "name": "Tanning salon marketing in SW London and Surrey",
          "serviceType": "Local marketing and AI search visibility for tanning salons",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Woking"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Tanning salons"
          },
          "offers": {
            "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Richmond upon Thames"
            },
            {
              "@type": "Place",
              "name": "Woking"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Tanning salon marketing in SW London and Surrey",
              "item": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/tanning-salon-marketing-sw-london-surrey/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should a tanning salon in SW London or Surrey market its services?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tanning salon marketing in SW London and Surrey has to be clear about two things: which services you offer, from spray tans to sunbeds, and the rules that apply to sunbed use. We keep your services, booking and licence details consistent across Google, your website and AI tools for salons in Sutton, Lambeth, Kingston, Richmond, Woking and Epsom."
              }
            },
            {
              "@type": "Question",
              "name": "Which council licenses our sunbeds in Sutton, Lambeth or Kingston?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Your own borough. Sutton and Kingston list ultraviolet sunbeds as medium-risk special treatments, and Lambeth includes ultraviolet tanning in its special treatment bands, all under the London Local Authorities Act 1991. We only advertise sunbeds at a branch once its licence covers them."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Sunbeds Act mean for our booking pages?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Say it before they book. The Sunbeds (Regulation) Act 2010 requires a sunbed business to stop anyone under 18 using a sunbed or being in the restricted zone. We add that wording to booking pages and FAQs so younger clients know before they arrive, and we avoid any health claims."
              }
            },
            {
              "@type": "Question",
              "name": "Does a salon in Woking or Epsom need a sunbed licence?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Not a London-style one. GOV.UK says special treatment licences are a Greater London regime, and the registration list outside London does not include sunbeds. The Sunbeds Act still applies everywhere in England. We suggest checking with your borough council and never describe your salon as licensed for sunbeds."
              }
            },
            {
              "@type": "Question",
              "name": "Should our Richmond salon explain record cards and goggles online?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, briefly. Richmond's standard special treatment conditions include signed record cards, free eye protection and HSE guidance in each cubicle. Describing those steps on your site sets clear expectations for new clients. We present them as what happens at your salon, not as a promise about results."
              }
            },
            {
              "@type": "Question",
              "name": "What must a tanning salon say about age rules?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "State clearly that sunbeds are for adults only. The Sunbeds (Regulation) Act 2010 bars businesses from letting anyone under 18 use a sunbed on their premises, so put the age policy on your Google Business Profile, booking page and price list, and say how you check age at the desk."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a tanning salon, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Sutton, Lambeth, Kingston, Richmond and Woking, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should a tanning salon in SW London or Surrey market its services?",
        "a": "Tanning salon marketing in SW London and Surrey has to be clear about two things: which services you offer, from spray tans to sunbeds, and the rules that apply to sunbed use. We keep your services, booking and licence details consistent across Google, your website and AI tools for salons in Sutton, Lambeth, Kingston, Richmond, Woking and Epsom."
      },
      {
        "q": "Which council licenses our sunbeds in Sutton, Lambeth or Kingston?",
        "a": "Your own borough. Sutton and Kingston list ultraviolet sunbeds as medium-risk special treatments, and Lambeth includes ultraviolet tanning in its special treatment bands, all under the London Local Authorities Act 1991. We only advertise sunbeds at a branch once its licence covers them."
      },
      {
        "q": "What does the Sunbeds Act mean for our booking pages?",
        "a": "Say it before they book. The Sunbeds (Regulation) Act 2010 requires a sunbed business to stop anyone under 18 using a sunbed or being in the restricted zone. We add that wording to booking pages and FAQs so younger clients know before they arrive, and we avoid any health claims."
      },
      {
        "q": "Does a salon in Woking or Epsom need a sunbed licence?",
        "a": "Not a London-style one. GOV.UK says special treatment licences are a Greater London regime, and the registration list outside London does not include sunbeds. The Sunbeds Act still applies everywhere in England. We suggest checking with your borough council and never describe your salon as licensed for sunbeds."
      },
      {
        "q": "Should our Richmond salon explain record cards and goggles online?",
        "a": "Yes, briefly. Richmond's standard special treatment conditions include signed record cards, free eye protection and HSE guidance in each cubicle. Describing those steps on your site sets clear expectations for new clients. We present them as what happens at your salon, not as a promise about results."
      },
      {
        "q": "What must a tanning salon say about age rules?",
        "a": "State clearly that sunbeds are for adults only. The Sunbeds (Regulation) Act 2010 bars businesses from letting anyone under 18 use a sunbed on their premises, so put the age policy on your Google Business Profile, booking page and price list, and say how you check age at the desk."
      },
      {
        "q": "What does the Local Presence Plan cost for a tanning salon, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Sutton, Lambeth, Kingston, Richmond and Woking, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/local/tyre-mot-garage-marketing-sw-london-surrey/": {
    "section": "C-D9",
    "h1": "MOT and garage marketing in SW London and Surrey",
    "title": "MOT and garage marketing in SW London and Surrey",
    "description": "Garage marketing for MOT, tyre and servicing centres in Putney, Wimbledon, Sutton, Lambeth, Woking and Epsom: ULEZ questions, Google Maps and reviews.",
    "answerFirst": "MOT and garage marketing in SW London and Surrey means answering what drivers really ask: can you test my car this week, do you fit tyres, and will my vehicle be charged to drive here. We keep your services, hours and ULEZ answers consistent across Google, your website and AI tools, whichever side of the London boundary your garage sits.",
    "factsHtml": "<h3 id=\"facts-with-sources\">The facts, with sources</h3>\n<ul>\n<li>Transport for London sets where and when the ULEZ applies, and which vehicles must pay the daily charge. Source: <a href=\"https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Transport for London: ULEZ, where and when</a>.</li>\n<li>The maximum MOT fee for a car is £54.85; garages can charge less, and there is no VAT on the MOT fee. Source: <a href=\"https://www.gov.uk/getting-an-mot/mot-test-fees\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Getting an MOT, MOT costs</a>.</li>\n<li>Anyone can check a vehicle's MOT history, including advisories, on GOV.UK. Source: <a href=\"https://www.gov.uk/check-mot-history\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Check the MOT history of a vehicle</a>.</li>\n<li>Motorists can sign up on GOV.UK for free MOT reminders by text or email. Source: <a href=\"https://www.gov.uk/get-stop-view-mot-reminder\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">GOV.UK: Get, view or stop MOT reminders</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>How should an MOT and tyre garage in SW London or Surrey market itself?</h3>\n<p>MOT and garage marketing in SW London and Surrey means answering what drivers really ask: can you test my car this week, do you fit tyres, and will my vehicle be charged to drive here. We keep your services, hours and ULEZ answers consistent across Google, your website and AI tools, whichever side of the London boundary your garage sits.</p>\n<h3>Do customers in Putney and Wimbledon need ULEZ answers on our site?</h3>\n<p>Yes. TfL says the ULEZ covers all London boroughs, which includes Wandsworth and Merton. Drivers often ask a garage whether their car meets the standard. We add a short, factual answer that links to the TfL vehicle checker, and we never state a vehicle's status for you.</p>\n<h3>Are Woking and Epsom inside the ULEZ?</h3>\n<p>No. Both are in Surrey, outside London, and TfL describes the zone as covering London's boroughs. Customers from Woking or Epsom who drive into Kingston or Sutton still need a compliant vehicle or must pay the daily charge, so we write a separate answer for Surrey drivers.</p>\n<h3>Can we put tyres on display outside our Kingston garage?</h3>\n<p>Check first. Kingston council says displaying or selling goods on or near the public highway needs a street trading licence. If your display sits on your own forecourt, it may not apply, but we recommend confirming with the licensing team before you feature it in photos.</p>\n<h3>Which branch should drivers in Sutton and Lambeth contact?</h3>\n<p>Tell them directly. If one site handles MOTs and another does tyres or vans, say so in the areas section and on each Google profile. A driver in Sutton who needs a van serviced should not have to ring two numbers to find out which site can take it this week.</p>\n<h3>What can a garage say about MOT prices?</h3>\n<p>The maximum fee for a car MOT is £54.85 and there is no VAT on the test, but garages can charge less. If you advertise a lower MOT price, show it clearly with any conditions, and list prices for repairs separately, because the MOT fee covers only the test.</p>\n<h3>What does the Local Presence Plan cost for a garage, and what is included?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum.</p>\n<h3>How does the Free AI Visibility Score work, where do you work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Wimbledon, Sutton, Lambeth, Woking and Epsom, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#webpage",
          "url": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/",
          "name": "MOT and garage marketing in SW London and Surrey",
          "headline": "MOT and garage marketing in SW London and Surrey",
          "inLanguage": "en-GB",
          "isPartOf": [
            {
              "@id": "https://integrated.social/local/#webpage"
            },
            {
              "@id": "https://integrated.social/#website"
            }
          ],
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "about": {
            "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#service"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#faq"
          },
          "dateModified": "2026-10-28",
          "description": "Garage marketing for MOT, tyre and servicing centres in Putney, Wimbledon, Sutton, Lambeth, Woking and Epsom: ULEZ questions, Google Maps and reviews.",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Transport for London: ULEZ, where and when",
              "url": "https://tfl.gov.uk/modes/driving/ultra-low-emission-zone/ulez-where-and-when"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Getting an MOT, MOT costs",
              "url": "https://www.gov.uk/getting-an-mot/mot-test-fees"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Check the MOT history of a vehicle",
              "url": "https://www.gov.uk/check-mot-history"
            },
            {
              "@type": "CreativeWork",
              "name": "GOV.UK: Get, view or stop MOT reminders",
              "url": "https://www.gov.uk/get-stop-view-mot-reminder"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#service",
          "name": "MOT and garage marketing in SW London and Surrey",
          "serviceType": "Local marketing and AI search visibility for garages",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Woking"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            }
          ],
          "audience": {
            "@type": "BusinessAudience",
            "name": "Garages"
          },
          "offers": {
            "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#offer"
          }
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Putney"
            },
            {
              "@type": "Place",
              "name": "Wimbledon"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Royal Borough of Kingston upon Thames"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Sutton"
            },
            {
              "@type": "AdministrativeArea",
              "name": "London Borough of Lambeth"
            },
            {
              "@type": "Place",
              "name": "Guildford"
            },
            {
              "@type": "Place",
              "name": "Woking"
            },
            {
              "@type": "Place",
              "name": "Epsom"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local business marketing",
              "item": "https://integrated.social/local/"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "MOT and garage marketing in SW London and Surrey",
              "item": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/local/tyre-mot-garage-marketing-sw-london-surrey/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How should an MOT and tyre garage in SW London or Surrey market itself?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "MOT and garage marketing in SW London and Surrey means answering what drivers really ask: can you test my car this week, do you fit tyres, and will my vehicle be charged to drive here. We keep your services, hours and ULEZ answers consistent across Google, your website and AI tools, whichever side of the London boundary your garage sits."
              }
            },
            {
              "@type": "Question",
              "name": "Do customers in Putney and Wimbledon need ULEZ answers on our site?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. TfL says the ULEZ covers all London boroughs, which includes Wandsworth and Merton. Drivers often ask a garage whether their car meets the standard. We add a short, factual answer that links to the TfL vehicle checker, and we never state a vehicle's status for you."
              }
            },
            {
              "@type": "Question",
              "name": "Are Woking and Epsom inside the ULEZ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. Both are in Surrey, outside London, and TfL describes the zone as covering London's boroughs. Customers from Woking or Epsom who drive into Kingston or Sutton still need a compliant vehicle or must pay the daily charge, so we write a separate answer for Surrey drivers."
              }
            },
            {
              "@type": "Question",
              "name": "Can we put tyres on display outside our Kingston garage?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Check first. Kingston council says displaying or selling goods on or near the public highway needs a street trading licence. If your display sits on your own forecourt, it may not apply, but we recommend confirming with the licensing team before you feature it in photos."
              }
            },
            {
              "@type": "Question",
              "name": "Which branch should drivers in Sutton and Lambeth contact?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tell them directly. If one site handles MOTs and another does tyres or vans, say so in the areas section and on each Google profile. A driver in Sutton who needs a van serviced should not have to ring two numbers to find out which site can take it this week."
              }
            },
            {
              "@type": "Question",
              "name": "What can a garage say about MOT prices?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The maximum fee for a car MOT is £54.85 and there is no VAT on the test, but garages can charge less. If you advertise a lower MOT price, show it clearly with any conditions, and list prices for repairs separately, because the MOT fee covers only the test."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost for a garage, and what is included?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Wimbledon, Sutton, Lambeth, Woking and Epsom, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "How should an MOT and tyre garage in SW London or Surrey market itself?",
        "a": "MOT and garage marketing in SW London and Surrey means answering what drivers really ask: can you test my car this week, do you fit tyres, and will my vehicle be charged to drive here. We keep your services, hours and ULEZ answers consistent across Google, your website and AI tools, whichever side of the London boundary your garage sits."
      },
      {
        "q": "Do customers in Putney and Wimbledon need ULEZ answers on our site?",
        "a": "Yes. TfL says the ULEZ covers all London boroughs, which includes Wandsworth and Merton. Drivers often ask a garage whether their car meets the standard. We add a short, factual answer that links to the TfL vehicle checker, and we never state a vehicle's status for you."
      },
      {
        "q": "Are Woking and Epsom inside the ULEZ?",
        "a": "No. Both are in Surrey, outside London, and TfL describes the zone as covering London's boroughs. Customers from Woking or Epsom who drive into Kingston or Sutton still need a compliant vehicle or must pay the daily charge, so we write a separate answer for Surrey drivers."
      },
      {
        "q": "Can we put tyres on display outside our Kingston garage?",
        "a": "Check first. Kingston council says displaying or selling goods on or near the public highway needs a street trading licence. If your display sits on your own forecourt, it may not apply, but we recommend confirming with the licensing team before you feature it in photos."
      },
      {
        "q": "Which branch should drivers in Sutton and Lambeth contact?",
        "a": "Tell them directly. If one site handles MOTs and another does tyres or vans, say so in the areas section and on each Google profile. A driver in Sutton who needs a van serviced should not have to ring two numbers to find out which site can take it this week."
      },
      {
        "q": "What can a garage say about MOT prices?",
        "a": "The maximum fee for a car MOT is £54.85 and there is no VAT on the test, but garages can charge less. If you advertise a lower MOT price, show it clearly with any conditions, and list prices for repairs separately, because the MOT fee covers only the test."
      },
      {
        "q": "What does the Local Presence Plan cost for a garage, and what is included?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice. It covers your Google Business Profile, structured data on your website, matching business details across the main directories, review templates, a short weekly report and one local LinkedIn post a week. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which has a 3-month minimum."
      },
      {
        "q": "How does the Free AI Visibility Score work, where do you work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score shows on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. We work with independent businesses in Putney, Wimbledon, Sutton, Lambeth, Woking and Epsom, and across the UK. We cannot promise a timescale or any ranking, citation or sales result. Agents draft; a named senior person signs."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/services/local-presence-plan/": {
    "section": "C-E1",
    "h1": "Get Found in \"Near Me\" Searches Across Richmond, Wimbledon, Putney, Kingston, and Surrey",
    "title": "Get Found in \"Near Me\" Searches Across Richmond, Wimbledon, Putney, Kingston, and Surrey",
    "description": "Weekly local visibility reports, Google Business Profile set-up, schema and review templates. £299 a month inc. VAT, plus £99 setup inc. VAT.",
    "answerFirst": "The Local Presence Plan is a monthly retainer for independent businesses in SW London and Surrey that keeps their Google Business Profile, website data and local listings clear and consistent. It costs £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly, you cancel with 30 days' notice, and paid ads are not included.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">The facts behind this plan, with sources</h2>\n<ul>\n<li>Google says local results are mainly based on relevance, distance and popularity. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n<li>Google's guidelines set what a Business Profile may show, including the real-world business name. Source: <a href=\"https://support.google.com/business/answer/3038177\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Guidelines for representing your business on Google</a>.</li>\n<li>Bing Places and Apple Business let a business claim its listing separately from Google. Source: <a href=\"https://www.bing.com/forbusiness/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Bing Places for Business</a>; <a href=\"https://business.apple.com/\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Apple Business (formerly Apple Business Connect)</a>.</li>\n<li>The CMA's fake reviews guidance applies to how review requests are worded and incentivised. Source: <a href=\"https://www.gov.uk/government/publications/fake-reviews\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">CMA: Fake reviews guidance (CMA208)</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>What is the Local Presence Plan and what does it cost?</h3>\n<p>The Local Presence Plan helps independent businesses in SW London and Surrey keep their Google Business Profile, website data and local listings clear and consistent. It costs £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly, you cancel with 30 days' notice, and paid ads are not included.</p>\n<h3>How do I improve how my café or gym shows up in 'near me' searches in Richmond, Wimbledon or Putney?</h3>\n<p>Three things work together: a complete Google Business Profile with accurate categories, LocalBusiness schema on your website, and consistent name, address and phone details across local directories. The Local Presence Plan sets up all three and keeps them in step. We cannot promise any position in Google or in AI answers.</p>\n<h3>How does the Free AI Visibility Score work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score appears on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. Agents draft; a named senior person signs. We cannot promise a timescale or any ranking, citation or sales result.</p>\n<h3>Will my restaurant appear in Google AI Overviews and ChatGPT when someone searches near Wimbledon?</h3>\n<p>We cannot promise that. No one controls what Google AI Overviews or ChatGPT show. What we do is make your details clear: complete LocalBusiness schema covering cuisine, price range and opening hours, and a Google Business Profile that is kept accurate. That gives these tools clearer facts to work from.</p>\n<h3>Do you serve businesses in Surrey towns like Esher, Cobham and Woking?</h3>\n<p>Yes. We work with independent businesses across SW London and Surrey, including Richmond, Putney, Wimbledon, Kingston, Twickenham, Kew, Barnes, Sheen, Mortlake, Guildford, Epsom, Esher, Cobham and Woking. The Local Presence Plan is also available across the UK, including Greater London.</p>\n<h3>How long does it take for a Pilates studio in SW London to see a change in local search?</h3>\n<p>We cannot promise a timescale. Search and AI results depend on many things we do not control, such as competition, reviews and how Google handles your listing. Your weekly report shows what we have changed that week and what to look at next, so you can see the work as it happens.</p>\n<h3>What is included in the weekly local visibility report?</h3>\n<p>Each week you receive a short PDF covering your Google Business Profile score, local keyword positions for your main queries, a review summary, a schema health check and three prioritised actions. The Local Presence Plan also includes review templates and one LinkedIn post a week. Paid ads are not included.</p>\n<h3>What does the Local Presence Plan cost, and what are the terms?</h3>\n<p>The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice, with no minimum term. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which is £1,299 a month inc. VAT with a 3-month minimum.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/services/local-presence-plan/#webpage",
          "url": "https://integrated.social/services/local-presence-plan/",
          "name": "Get Found in \"Near Me\" Searches Across Richmond, Wimbledon, Putney, Kingston, and Surrey",
          "headline": "Get Found in \"Near Me\" Searches Across Richmond, Wimbledon, Putney, Kingston, and Surrey",
          "description": "Weekly local visibility reports, Google Business Profile set-up, schema and review templates. £299 a month inc. VAT, plus £99 setup inc. VAT.",
          "inLanguage": "en-GB",
          "isPartOf": {
            "@id": "https://integrated.social/#website"
          },
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/services/local-presence-plan/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/services/local-presence-plan/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/services/local-presence-plan/#faq"
          },
          "dateModified": "2026-10-28",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Guidelines for representing your business on Google",
              "url": "https://support.google.com/business/answer/3038177"
            },
            {
              "@type": "CreativeWork",
              "name": "Bing Places for Business",
              "url": "https://www.bing.com/forbusiness/"
            },
            {
              "@type": "CreativeWork",
              "name": "Apple Business (formerly Apple Business Connect)",
              "url": "https://business.apple.com/"
            },
            {
              "@type": "CreativeWork",
              "name": "CMA: Fake reviews guidance (CMA208)",
              "url": "https://www.gov.uk/government/publications/fake-reviews"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/services/local-presence-plan/#service",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London"
            }
          ],
          "offers": {
            "@id": "https://integrated.social/services/local-presence-plan/#offer"
          },
          "serviceType": "Local search and AI visibility",
          "description": "A monthly retainer that keeps your Google Business Profile, website data and local listings clear and consistent: weekly local visibility report, schema, review templates and one LinkedIn post a week. Paid ads are not included."
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/services/local-presence-plan/#offer",
          "name": "Local Presence Plan",
          "url": "https://integrated.social/services/local-presence-plan/",
          "price": "299",
          "priceCurrency": "GBP",
          "description": "Monthly retainer billing. Cancel with 30 days' notice. Prices include VAT. Paid ads are not included.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "99",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/local-presence-plan/#service",
            "name": "Local Presence Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/services/local-presence-plan/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Local Presence Plan",
              "item": "https://integrated.social/services/local-presence-plan/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/services/local-presence-plan/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the Local Presence Plan and what does it cost?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan helps independent businesses in SW London and Surrey keep their Google Business Profile, website data and local listings clear and consistent. It costs £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly, you cancel with 30 days' notice, and paid ads are not included."
              }
            },
            {
              "@type": "Question",
              "name": "How do I improve how my café or gym shows up in 'near me' searches in Richmond, Wimbledon or Putney?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Three things work together: a complete Google Business Profile with accurate categories, LocalBusiness schema on your website, and consistent name, address and phone details across local directories. The Local Presence Plan sets up all three and keeps them in step. We cannot promise any position in Google or in AI answers."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score appears on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. Agents draft; a named senior person signs. We cannot promise a timescale or any ranking, citation or sales result."
              }
            },
            {
              "@type": "Question",
              "name": "Will my restaurant appear in Google AI Overviews and ChatGPT when someone searches near Wimbledon?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We cannot promise that. No one controls what Google AI Overviews or ChatGPT show. What we do is make your details clear: complete LocalBusiness schema covering cuisine, price range and opening hours, and a Google Business Profile that is kept accurate. That gives these tools clearer facts to work from."
              }
            },
            {
              "@type": "Question",
              "name": "Do you serve businesses in Surrey towns like Esher, Cobham and Woking?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. We work with independent businesses across SW London and Surrey, including Richmond, Putney, Wimbledon, Kingston, Twickenham, Kew, Barnes, Sheen, Mortlake, Guildford, Epsom, Esher, Cobham and Woking. The Local Presence Plan is also available across the UK, including Greater London."
              }
            },
            {
              "@type": "Question",
              "name": "How long does it take for a Pilates studio in SW London to see a change in local search?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We cannot promise a timescale. Search and AI results depend on many things we do not control, such as competition, reviews and how Google handles your listing. Your weekly report shows what we have changed that week and what to look at next, so you can see the work as it happens."
              }
            },
            {
              "@type": "Question",
              "name": "What is included in the weekly local visibility report?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Each week you receive a short PDF covering your Google Business Profile score, local keyword positions for your main queries, a review summary, a schema health check and three prioritised actions. The Local Presence Plan also includes review templates and one LinkedIn post a week. Paid ads are not included."
              }
            },
            {
              "@type": "Question",
              "name": "What does the Local Presence Plan cost, and what are the terms?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice, with no minimum term. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which is £1,299 a month inc. VAT with a 3-month minimum."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What is the Local Presence Plan and what does it cost?",
        "a": "The Local Presence Plan helps independent businesses in SW London and Surrey keep their Google Business Profile, website data and local listings clear and consistent. It costs £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly, you cancel with 30 days' notice, and paid ads are not included."
      },
      {
        "q": "How do I improve how my café or gym shows up in 'near me' searches in Richmond, Wimbledon or Putney?",
        "a": "Three things work together: a complete Google Business Profile with accurate categories, LocalBusiness schema on your website, and consistent name, address and phone details across local directories. The Local Presence Plan sets up all three and keeps them in step. We cannot promise any position in Google or in AI answers."
      },
      {
        "q": "How does the Free AI Visibility Score work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score appears on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. Agents draft; a named senior person signs. We cannot promise a timescale or any ranking, citation or sales result."
      },
      {
        "q": "Will my restaurant appear in Google AI Overviews and ChatGPT when someone searches near Wimbledon?",
        "a": "We cannot promise that. No one controls what Google AI Overviews or ChatGPT show. What we do is make your details clear: complete LocalBusiness schema covering cuisine, price range and opening hours, and a Google Business Profile that is kept accurate. That gives these tools clearer facts to work from."
      },
      {
        "q": "Do you serve businesses in Surrey towns like Esher, Cobham and Woking?",
        "a": "Yes. We work with independent businesses across SW London and Surrey, including Richmond, Putney, Wimbledon, Kingston, Twickenham, Kew, Barnes, Sheen, Mortlake, Guildford, Epsom, Esher, Cobham and Woking. The Local Presence Plan is also available across the UK, including Greater London."
      },
      {
        "q": "How long does it take for a Pilates studio in SW London to see a change in local search?",
        "a": "We cannot promise a timescale. Search and AI results depend on many things we do not control, such as competition, reviews and how Google handles your listing. Your weekly report shows what we have changed that week and what to look at next, so you can see the work as it happens."
      },
      {
        "q": "What is included in the weekly local visibility report?",
        "a": "Each week you receive a short PDF covering your Google Business Profile score, local keyword positions for your main queries, a review summary, a schema health check and three prioritised actions. The Local Presence Plan also includes review templates and one LinkedIn post a week. Paid ads are not included."
      },
      {
        "q": "What does the Local Presence Plan cost, and what are the terms?",
        "a": "The Local Presence Plan is £299 a month inc. VAT, plus £99 setup inc. VAT. Billing is monthly and you cancel with 30 days' notice, with no minimum term. Paid ads are not included. Ad spend is included only in the Full AI Growth Engine, which is £1,299 a month inc. VAT with a 3-month minimum."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/services/ai-visibility-plan/": {
    "section": "C-E2",
    "h1": "An AI Website and Google Ads Built for Local Businesses in SW London and Surrey",
    "title": "An AI Website and Google Ads Built for Local Businesses in SW London and Surrey",
    "description": "A 5-page AI website, Google Ads management and GA4 for local businesses. £599 a month inc. VAT, plus £149 setup inc. VAT. Ad spend not included.",
    "answerFirst": "The AI Visibility Plan is a monthly retainer at £599 a month inc. VAT, plus £149 setup inc. VAT. It adds a 5-page AI website, Google Ads management, GA4 and attribution setup, weekly content recommendations and a monthly strategy call to everything in the Local Presence Plan. Ad spend is not included. Billing is monthly, with 30 days' notice.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">The facts behind this plan, with sources</h2>\n<ul>\n<li>Google says there are no additional requirements or special optimisations to appear in AI Overviews or AI Mode; a page must be indexed and eligible to show a snippet. Source: <a href=\"https://developers.google.com/search/docs/appearance/ai-features\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Search Central: AI features and your website</a>.</li>\n<li>OpenAI says sites that allow OAI-SearchBot can appear in ChatGPT search answers, separately from GPTBot. Source: <a href=\"https://developers.openai.com/api/docs/bots\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">OpenAI: Overview of OpenAI crawlers</a>.</li>\n<li>Bing's Webmaster Guidelines set out how Bing finds, indexes and ranks pages. Source: <a href=\"https://www.bing.com/webmasters/help/webmaster-guidelines-30fba23a\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Bing Webmaster Guidelines</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not legal, clinical or compliance advice.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": "<section id=\"local-faq\" aria-labelledby=\"local-faq-h\">\n<h2 id=\"local-faq-h\">Frequently asked questions</h2>\n<h3>What is the AI Visibility Plan and what does it cost?</h3>\n<p>The AI Visibility Plan is £599 a month inc. VAT, plus £149 setup inc. VAT. It adds a 5-page AI website, Google Ads management, GA4 and attribution setup, weekly content recommendations and a monthly strategy call to everything in the Local Presence Plan. Ad spend is not included. Billing is monthly, with 30 days' notice.</p>\n<h3>How does an AI website differ from a standard website for a local business?</h3>\n<p>An AI website is built so AI systems have clear, structured facts about your business for queries like 'gym in Wimbledon' or 'pilates classes near Kingston'. It uses LocalBusiness schema, FAQ schema and clear page structure. We cannot promise that any AI system will cite or recommend it.</p>\n<h3>Can Google Ads be targeted to Richmond, Putney, Wimbledon and Kingston?</h3>\n<p>Yes. Campaigns can use radius and location targeting around your postcode, with ad copy for your area. Google Ads management is part of the AI Visibility Plan. Ad spend is not included, so you pay Google directly. We cannot promise any number of clicks, enquiries or sales.</p>\n<h3>How does the Free AI Visibility Score work, and who signs off the work?</h3>\n<p>Enter your website and work email. Your headline score appears on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. Agents draft; a named senior person signs. We cannot promise a timescale or any ranking, citation or sales result.</p>\n<h3>What does the AI Visibility Plan do for Google AI Overviews?</h3>\n<p>Your website gets LocalBusiness schema, FAQ schema and clear page structure, and your Google Business Profile and local citations are kept consistent. Combined with an optimised Google Business Profile and consistent local citations, this gives AI Overviews clear facts to work from. We cannot promise your business will appear.</p>\n<h3>What does the AI Visibility Plan cost, and what are the terms?</h3>\n<p>It is £599 a month inc. VAT, plus a one-time setup fee of £149 inc. VAT. The setup fee covers the initial build and onboarding. Billing is monthly and you cancel with 30 days' notice. There is no minimum term. Ad spend is not included. The 3-month minimum applies only to the Full AI Growth Engine.</p>\n<h3>Can I upgrade from the Local Presence Plan to the AI Visibility Plan?</h3>\n<p>Yes. The upgrade fee is £99 inc. VAT (reduced from £149 inc. VAT), because the Local Presence setup work is already done. Your monthly subscription changes from £299 to £599, both inc. VAT. You can upgrade at any time, and billing stays monthly with 30 days' notice.</p>\n<h3>Do you serve businesses in Guildford and other Surrey towns on this plan?</h3>\n<p>Yes. We work with independent businesses across SW London and Surrey, including Richmond, Putney, Wimbledon, Kingston, Guildford, Epsom, Esher, Cobham and Woking. The plan is also available across the UK, including Greater London.</p>\n</section>",
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/services/ai-visibility-plan/#webpage",
          "url": "https://integrated.social/services/ai-visibility-plan/",
          "name": "An AI Website and Google Ads Built for Local Businesses in SW London and Surrey",
          "headline": "An AI Website and Google Ads Built for Local Businesses in SW London and Surrey",
          "description": "A 5-page AI website, Google Ads management and GA4 for local businesses. £599 a month inc. VAT, plus £149 setup inc. VAT. Ad spend not included.",
          "inLanguage": "en-GB",
          "isPartOf": {
            "@id": "https://integrated.social/#website"
          },
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/services/ai-visibility-plan/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/services/ai-visibility-plan/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/services/ai-visibility-plan/#faq"
          },
          "dateModified": "2026-10-28",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Google Search Central: AI features and your website",
              "url": "https://developers.google.com/search/docs/appearance/ai-features"
            },
            {
              "@type": "CreativeWork",
              "name": "OpenAI: Overview of OpenAI crawlers",
              "url": "https://developers.openai.com/api/docs/bots"
            },
            {
              "@type": "CreativeWork",
              "name": "Bing Webmaster Guidelines",
              "url": "https://www.bing.com/webmasters/help/webmaster-guidelines-30fba23a"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/services/ai-visibility-plan/#service",
          "name": "AI Visibility Plan",
          "url": "https://integrated.social/services/ai-visibility-plan/",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London"
            }
          ],
          "offers": {
            "@id": "https://integrated.social/services/ai-visibility-plan/#offer"
          },
          "serviceType": "AI website and Google Ads for local businesses",
          "description": "A monthly retainer including everything in the Local Presence Plan plus a 5-page AI website, Google Ads management, GA4 and attribution setup, weekly content recommendations and a monthly strategy call. Ad spend is not included."
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/services/ai-visibility-plan/#offer",
          "name": "AI Visibility Plan",
          "url": "https://integrated.social/services/ai-visibility-plan/",
          "price": "599",
          "priceCurrency": "GBP",
          "description": "Monthly retainer billing. Cancel with 30 days' notice. Prices include VAT. Google Ads management is included; ad spend is not.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "599",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "149",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/ai-visibility-plan/#service",
            "name": "AI Visibility Plan"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/services/ai-visibility-plan/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "AI Visibility Plan",
              "item": "https://integrated.social/services/ai-visibility-plan/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/services/ai-visibility-plan/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the AI Visibility Plan and what does it cost?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The AI Visibility Plan is £599 a month inc. VAT, plus £149 setup inc. VAT. It adds a 5-page AI website, Google Ads management, GA4 and attribution setup, weekly content recommendations and a monthly strategy call to everything in the Local Presence Plan. Ad spend is not included. Billing is monthly, with 30 days' notice."
              }
            },
            {
              "@type": "Question",
              "name": "How does an AI website differ from a standard website for a local business?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "An AI website is built so AI systems have clear, structured facts about your business for queries like 'gym in Wimbledon' or 'pilates classes near Kingston'. It uses LocalBusiness schema, FAQ schema and clear page structure. We cannot promise that any AI system will cite or recommend it."
              }
            },
            {
              "@type": "Question",
              "name": "Can Google Ads be targeted to Richmond, Putney, Wimbledon and Kingston?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Campaigns can use radius and location targeting around your postcode, with ad copy for your area. Google Ads management is part of the AI Visibility Plan. Ad spend is not included, so you pay Google directly. We cannot promise any number of clicks, enquiries or sales."
              }
            },
            {
              "@type": "Question",
              "name": "How does the Free AI Visibility Score work, and who signs off the work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Enter your website and work email. Your headline score appears on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. Agents draft; a named senior person signs. We cannot promise a timescale or any ranking, citation or sales result."
              }
            },
            {
              "@type": "Question",
              "name": "What does the AI Visibility Plan do for Google AI Overviews?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Your website gets LocalBusiness schema, FAQ schema and clear page structure, and your Google Business Profile and local citations are kept consistent. Combined with an optimised Google Business Profile and consistent local citations, this gives AI Overviews clear facts to work from. We cannot promise your business will appear."
              }
            },
            {
              "@type": "Question",
              "name": "What does the AI Visibility Plan cost, and what are the terms?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It is £599 a month inc. VAT, plus a one-time setup fee of £149 inc. VAT. The setup fee covers the initial build and onboarding. Billing is monthly and you cancel with 30 days' notice. There is no minimum term. Ad spend is not included. The 3-month minimum applies only to the Full AI Growth Engine."
              }
            },
            {
              "@type": "Question",
              "name": "Can I upgrade from the Local Presence Plan to the AI Visibility Plan?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. The upgrade fee is £99 inc. VAT (reduced from £149 inc. VAT), because the Local Presence setup work is already done. Your monthly subscription changes from £299 to £599, both inc. VAT. You can upgrade at any time, and billing stays monthly with 30 days' notice."
              }
            },
            {
              "@type": "Question",
              "name": "Do you serve businesses in Guildford and other Surrey towns on this plan?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. We work with independent businesses across SW London and Surrey, including Richmond, Putney, Wimbledon, Kingston, Guildford, Epsom, Esher, Cobham and Woking. The plan is also available across the UK, including Greater London."
              }
            }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What is the AI Visibility Plan and what does it cost?",
        "a": "The AI Visibility Plan is £599 a month inc. VAT, plus £149 setup inc. VAT. It adds a 5-page AI website, Google Ads management, GA4 and attribution setup, weekly content recommendations and a monthly strategy call to everything in the Local Presence Plan. Ad spend is not included. Billing is monthly, with 30 days' notice."
      },
      {
        "q": "How does an AI website differ from a standard website for a local business?",
        "a": "An AI website is built so AI systems have clear, structured facts about your business for queries like 'gym in Wimbledon' or 'pilates classes near Kingston'. It uses LocalBusiness schema, FAQ schema and clear page structure. We cannot promise that any AI system will cite or recommend it."
      },
      {
        "q": "Can Google Ads be targeted to Richmond, Putney, Wimbledon and Kingston?",
        "a": "Yes. Campaigns can use radius and location targeting around your postcode, with ad copy for your area. Google Ads management is part of the AI Visibility Plan. Ad spend is not included, so you pay Google directly. We cannot promise any number of clicks, enquiries or sales."
      },
      {
        "q": "How does the Free AI Visibility Score work, and who signs off the work?",
        "a": "Enter your website and work email. Your headline score appears on screen straight away, and the full scorecard and PDF unlock after you enter the code we email you. It is free, with no obligation. Agents draft; a named senior person signs. We cannot promise a timescale or any ranking, citation or sales result."
      },
      {
        "q": "What does the AI Visibility Plan do for Google AI Overviews?",
        "a": "Your website gets LocalBusiness schema, FAQ schema and clear page structure, and your Google Business Profile and local citations are kept consistent. Combined with an optimised Google Business Profile and consistent local citations, this gives AI Overviews clear facts to work from. We cannot promise your business will appear."
      },
      {
        "q": "What does the AI Visibility Plan cost, and what are the terms?",
        "a": "It is £599 a month inc. VAT, plus a one-time setup fee of £149 inc. VAT. The setup fee covers the initial build and onboarding. Billing is monthly and you cancel with 30 days' notice. There is no minimum term. Ad spend is not included. The 3-month minimum applies only to the Full AI Growth Engine."
      },
      {
        "q": "Can I upgrade from the Local Presence Plan to the AI Visibility Plan?",
        "a": "Yes. The upgrade fee is £99 inc. VAT (reduced from £149 inc. VAT), because the Local Presence setup work is already done. Your monthly subscription changes from £299 to £599, both inc. VAT. You can upgrade at any time, and billing stays monthly with 30 days' notice."
      },
      {
        "q": "Do you serve businesses in Guildford and other Surrey towns on this plan?",
        "a": "Yes. We work with independent businesses across SW London and Surrey, including Richmond, Putney, Wimbledon, Kingston, Guildford, Epsom, Esher, Cobham and Woking. The plan is also available across the UK, including Greater London."
      }
    ],
    "dateModified": "2026-10-28"
  },
  "/services/full-ai-growth-engine/": {
    "section": "C-E3",
    "h1": "AI Growth Retainer for London & UK: SEO, AEO and GEO",
    "title": "AI Growth Retainer for London & UK: SEO, AEO and GEO",
    "description": "A monthly SEO, AEO, GEO, LLMO, paid-media and AI-growth retainer for South West London, Greater London and UK businesses. £1,299 a month inc. VAT, plus £299 setup inc. VAT.",
    "answerFirst": "The Full AI Growth Engine is a monthly retainer for businesses in South West London, Greater London and the UK that need technical SEO, AEO, GEO, LLMO, paid media and practical growth delivery to work together. At £1,299 a month inc. VAT plus £299 setup, it includes a 10+ page website, human-approved AI-assisted lead generation, LinkedIn, Performance Max, GA4 reporting and weekly strategy calls. It does not guarantee rankings, AI citations, leads or sales.",
    "factsHtml": "<section id=\"how-it-works-locally\" aria-labelledby=\"how-it-works-locally-h\">\n<h2 id=\"how-it-works-locally-h\">How SEO, AI search and local relevance work together</h2>\n<ul>\n<li>Google says the same SEO best practices remain relevant for AI Overviews and AI Mode; there are no special requirements or special AI markup. A page must be indexed and eligible to show a snippet. Source: <a href=\"https://developers.google.com/search/docs/appearance/ai-features\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Search Central: AI features and your website</a>.</li>\n<li>Google lists crawlability, internal links, textual content, page experience and structured data that matches visible text among the foundations for pages appearing in its AI search features. Source: <a href=\"https://developers.google.com/search/docs/appearance/ai-features\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Search Central: AI features and your website</a>.</li>\n<li>OpenAI says sites that allow OAI-SearchBot can appear in ChatGPT search answers, separately from GPTBot. Source: <a href=\"https://developers.openai.com/api/docs/bots\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">OpenAI: Overview of OpenAI crawlers</a>.</li>\n<li>For local searches, Google says results are mainly based on relevance, distance and popularity. Geographic scope must therefore match the business, the offer and the page rather than a generic local claim. Source: <a href=\"https://support.google.com/business/answer/7091\" target=\"_blank\" rel=\"noopener\" data-event=\"official_source_click\">Google Business Profile Help: Tips to improve your local ranking on Google</a>.</li>\n</ul>\n<p>Checked: 28 October 2026. These are public facts with their official sources, not a promise of rankings, AI citations, enquiries or sales.</p>\n</section>",
    "faqHtml": "",
    "moduleHtml": null,
    "moduleBeforeHeading": null,
    "schemaGraph": {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://integrated.social/services/full-ai-growth-engine/#webpage",
          "url": "https://integrated.social/services/full-ai-growth-engine/",
          "name": "AI Growth Retainer for London & UK: SEO, AEO and GEO",
          "headline": "AI Growth Retainer for London & UK: SEO, AEO and GEO",
          "description": "A monthly SEO, AEO, GEO, LLMO, paid-media and AI-growth retainer for South West London, Greater London and UK businesses. £1,299 a month inc. VAT, plus £299 setup inc. VAT.",
          "inLanguage": "en-GB",
          "isPartOf": {
            "@id": "https://integrated.social/#website"
          },
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "breadcrumb": {
            "@id": "https://integrated.social/services/full-ai-growth-engine/#breadcrumb"
          },
          "about": {
            "@id": "https://integrated.social/services/full-ai-growth-engine/#service"
          },
          "mainEntity": {
            "@id": "https://integrated.social/services/full-ai-growth-engine/#faq"
          },
          "dateModified": "2026-10-28",
          "citation": [
            {
              "@type": "CreativeWork",
              "name": "Google Search Central: AI features and your website",
              "url": "https://developers.google.com/search/docs/appearance/ai-features"
            },
            {
              "@type": "CreativeWork",
              "name": "OpenAI: Overview of OpenAI crawlers",
              "url": "https://developers.openai.com/api/docs/bots"
            },
            {
              "@type": "CreativeWork",
              "name": "Google Business Profile Help: Tips to improve your local ranking on Google",
              "url": "https://support.google.com/business/answer/7091"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://integrated.social/services/full-ai-growth-engine/#service",
          "name": "Full AI Growth Engine",
          "url": "https://integrated.social/services/full-ai-growth-engine/",
          "provider": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London"
            },
            {
              "@type": "AdministrativeArea",
              "name": "South West London"
            }
          ],
          "offers": {
            "@id": "https://integrated.social/services/full-ai-growth-engine/#offer"
          },
          "serviceType": "SEO, AEO, GEO, LLMO, paid media and AI growth retainer",
          "description": "A monthly retainer with a 3-month minimum that combines technical SEO, AEO, GEO, LLMO, human-approved AI-assisted lead generation, LinkedIn management, Performance Max, Google Ads management, GA4 reporting and weekly strategy calls for South West London, Greater London and UK businesses."
        },
        {
          "@type": "Offer",
          "@id": "https://integrated.social/services/full-ai-growth-engine/#offer",
          "name": "Full AI Growth Engine",
          "url": "https://integrated.social/services/full-ai-growth-engine/",
          "price": "1299",
          "priceCurrency": "GBP",
          "description": "Monthly retainer billing with a 3-month minimum, then 30 days' notice. Prices include VAT. Includes Google Ads management and a monthly ad budget, confirmed on the call.",
          "priceSpecification": [
            {
              "@type": "UnitPriceSpecification",
              "price": "1299",
              "priceCurrency": "GBP",
              "unitText": "MONTH",
              "referenceQuantity": {
                "@type": "QuantitativeValue",
                "value": 1,
                "unitCode": "MON"
              },
              "valueAddedTaxIncluded": true
            },
            {
              "@type": "PriceSpecification",
              "name": "One-time setup fee",
              "price": "299",
              "priceCurrency": "GBP",
              "valueAddedTaxIncluded": true
            }
          ],
          "seller": {
            "@id": "https://integrated.social/#organization"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom",
              "sameAs": "https://www.wikidata.org/wiki/Q145"
            },
            {
              "@type": "AdministrativeArea",
              "name": "Greater London"
            },
            {
              "@type": "AdministrativeArea",
              "name": "South West London"
            }
          ],
          "itemOffered": {
            "@type": "Service",
            "@id": "https://integrated.social/services/full-ai-growth-engine/#service",
            "name": "Full AI Growth Engine"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://integrated.social/services/full-ai-growth-engine/#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://integrated.social/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://integrated.social/services/"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Full AI Growth Engine",
              "item": "https://integrated.social/services/full-ai-growth-engine/"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://integrated.social/services/full-ai-growth-engine/#faq",
          "mainEntity": [
              {
                "@type": "Question",
                "name": "What is included in the Full AI Growth Engine retainer, and what does it cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Full AI Growth Engine is £1,299 a month inc. VAT, plus £299 setup inc. VAT, with a three-month minimum and then 30 days’ notice. The retainer combines a 10+ page website, technical SEO, AEO, GEO, LLMO, human-approved AI-assisted lead generation, LinkedIn, Performance Max, Google Ads management, GA4 reporting and weekly strategy calls. The monthly ad budget is confirmed on the call."
                }
              },
              {
                "@type": "Question",
                "name": "How do SEO, AEO, GEO, AI Answers and LLMO work together in this service?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SEO keeps important pages crawlable, indexable and useful in search. AEO and AI Answers work focus on clear, evidence-backed answers to buyer questions. GEO and LLMO are practical ways to make the same information easier for generative and language-model surfaces to understand. The work is one coordinated programme, not a promise that any platform will rank, cite or recommend a page."
                }
              },
              {
                "@type": "Question",
                "name": "Why is technical SEO still important for AI search and AI Answers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Technical SEO remains the foundation because a page needs to be discoverable, indexed and eligible to show a snippet before it can appear as a supporting link in Google AI features. Google says its normal SEO best practices still apply and that there are no special AI-markup requirements. We therefore address technical access, clear content, internal links and visible-schema alignment before treating AI visibility as a result."
                }
              },
              {
                "@type": "Question",
                "name": "Do you work only with businesses in Putney or South West London?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. The retainer is available to businesses in South West London, across Greater London and elsewhere in the UK. Local context is used only where it matches the business and the buyer journey. We do not claim a local office, a Google Business Profile location or a local outcome where those facts do not exist. Geographic targeting and content scope are agreed around your actual market."
                }
              },
              {
                "@type": "Question",
                "name": "How does human approval work in the AI-assisted growth workflow?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "AI-assisted systems can research, draft and surface recommendations, but a named senior person reviews material claims, publishing decisions and consequential campaign changes. The approval boundary is documented around the work rather than presented as fully autonomous execution. That helps teams understand what the system may analyse, recommend or prepare, and what still needs accountable human judgment before anything is released or a budget decision is made."
                }
              },
              {
                "@type": "Question",
                "name": "Are Performance Max and Google Ads included in the Full AI Growth Engine?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. The retainer includes Performance Max and Google Ads management alongside the site, SEO and AI-search work. Performance Max can run across multiple Google inventory surfaces, while the campaign structure, targeting, assets, measurement and change controls still need review. The monthly ad budget is confirmed on the call. We do not promise a particular cost per lead, return on ad spend, number of enquiries or sales outcome."
                }
              },
              {
                "@type": "Question",
                "name": "What is the contract length, and does the retainer guarantee rankings or AI citations?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Full AI Growth Engine has a three-month minimum and then runs monthly with 30 days’ notice. It is a delivery retainer, not a guarantee of rankings, AI citations, leads or sales. Search and answer platforms decide what they crawl, index, surface and cite. Our role is to make the technical foundation, buyer answers, evidence, review process and measurement more commercially useful and easier to validate."
                }
              }
          ]
        },
        {
          "@type": [
            "Organization",
            "ProfessionalService"
          ],
          "@id": "https://integrated.social/#organization",
          "name": "Integrated.Social",
          "legalName": "Integrated Social Ltd",
          "url": "https://integrated.social/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://integrated.social/media/integrated-social-logo_c362b0f0.webp",
            "width": 457,
            "height": 457
          },
          "description": "UK AI marketing agency for serious B2B teams and selected B2C, B2B2C and DTC programmes, specialising in AI search visibility, agentic lead generation and performance marketing.",
          "foundingDate": "2014",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "postalCode": "SW15 4LE",
            "addressCountry": "GB"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "United Kingdom"
            },
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Country",
              "name": "United Arab Emirates"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "email": "info@integrated.social",
            "telephone": "+447717804077",
            "areaServed": [
              "GB",
              "US",
              "AE"
            ],
            "availableLanguage": [
              "en-GB",
              "ar"
            ]
          },
          "knowsAbout": [
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "SEO",
            "PPC and Performance Max",
            "AI marketing strategy",
            "B2B lead generation",
            "B2B AI Marketing",
            "Agentic AI Lead Generation",
            "LLM Mention Optimisation (LLMO)",
            "AI Citation Readiness",
            "Google AI Mode Optimisation",
            "Account-Based Marketing",
            "AI Governance for Marketing",
            "Conversion Rate Optimisation",
            "B2C and DTC Marketing",
            "Local AI search marketing",
            "Google Business Profile optimisation",
            "Local SEO for independent businesses"
          ],
          "founder": {
            "@id": "https://integrated.social/modi-elnadi/#person"
          },
          "sameAs": [
            "https://www.linkedin.com/company/integrated-social-ltd"
          ]
        },
        {
          "@type": "Person",
          "@id": "https://integrated.social/modi-elnadi/#person",
          "name": "Modi Elnadi",
          "jobTitle": "Founder",
          "description": "Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed.",
          "url": "https://integrated.social/modi-elnadi/",
          "image": "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg",
          "worksFor": {
            "@id": "https://integrated.social/#organization"
          },
          "sameAs": [
            "https://www.linkedin.com/in/modielnadi/",
            "https://x.com/MoElnadi"
          ],
          "knowsAbout": [
            "Paid and earned marketing",
            "Personas",
            "SEO",
            "Answer Engine Optimisation",
            "Generative Engine Optimisation",
            "LLM optimisation",
            "Agentic AI with human review",
            "Google Ads",
            "Performance Max",
            "Account-based marketing",
            "CRM",
            "Conversion rate optimisation",
            "EMEA marketing",
            "Subscription growth",
            "Fintech marketing",
            "eCommerce"
          ],
          "skills": [
            "Paid and earned, planned together",
            "Personas and buying committees",
            "SEO, AEO, GEO and LLMO",
            "Agentic AI with human review",
            "Google Ads and Performance Max",
            "Paid social and LinkedIn",
            "CRM and lifecycle",
            "CRO and landing pages",
            "Account-based marketing",
            "Multi-market EMEA"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Professional Experience",
              "name": "20+ years in digital marketing",
              "description": "Senior marketing roles across fintech, SaaS, professional services, and technology sectors in the UK, US, and MENA."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Agentic AI for B2B Growth",
              "description": "Early adopter and practitioner of agentic AI systems for lead generation, GTM automation, and multi-agent orchestration since 2023."
            },
            {
              "@type": "EducationalOccupationalCredential",
              "credentialCategory": "Specialisation",
              "name": "Answer Engine Optimisation (AEO) and GEO",
              "description": "Specialist in optimising B2B brands for citation in ChatGPT, Gemini, Perplexity, and Google AI Mode."
            }
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://integrated.social/#website",
          "url": "https://integrated.social/",
          "name": "Integrated.Social",
          "publisher": {
            "@id": "https://integrated.social/#organization"
          },
          "inLanguage": "en-GB"
        }
      ]
    },
    "faqs": [
      {
        "q": "What is included in the Full AI Growth Engine retainer, and what does it cost?",
        "a": "The Full AI Growth Engine is £1,299 a month inc. VAT, plus £299 setup inc. VAT, with a three-month minimum and then 30 days’ notice. The retainer combines a 10+ page website, technical SEO, AEO, GEO, LLMO, human-approved AI-assisted lead generation, LinkedIn, Performance Max, Google Ads management, GA4 reporting and weekly strategy calls. The monthly ad budget is confirmed on the call."
      },
      {
        "q": "How do SEO, AEO, GEO, AI Answers and LLMO work together in this service?",
        "a": "SEO keeps important pages crawlable, indexable and useful in search. AEO and AI Answers work focus on clear, evidence-backed answers to buyer questions. GEO and LLMO are practical ways to make the same information easier for generative and language-model surfaces to understand. The work is one coordinated programme, not a promise that any platform will rank, cite or recommend a page."
      },
      {
        "q": "Why is technical SEO still important for AI search and AI Answers?",
        "a": "Technical SEO remains the foundation because a page needs to be discoverable, indexed and eligible to show a snippet before it can appear as a supporting link in Google AI features. Google says its normal SEO best practices still apply and that there are no special AI-markup requirements. We therefore address technical access, clear content, internal links and visible-schema alignment before treating AI visibility as a result."
      },
      {
        "q": "Do you work only with businesses in Putney or South West London?",
        "a": "No. The retainer is available to businesses in South West London, across Greater London and elsewhere in the UK. Local context is used only where it matches the business and the buyer journey. We do not claim a local office, a Google Business Profile location or a local outcome where those facts do not exist. Geographic targeting and content scope are agreed around your actual market."
      },
      {
        "q": "How does human approval work in the AI-assisted growth workflow?",
        "a": "AI-assisted systems can research, draft and surface recommendations, but a named senior person reviews material claims, publishing decisions and consequential campaign changes. The approval boundary is documented around the work rather than presented as fully autonomous execution. That helps teams understand what the system may analyse, recommend or prepare, and what still needs accountable human judgment before anything is released or a budget decision is made."
      },
      {
        "q": "Are Performance Max and Google Ads included in the Full AI Growth Engine?",
        "a": "Yes. The retainer includes Performance Max and Google Ads management alongside the site, SEO and AI-search work. Performance Max can run across multiple Google inventory surfaces, while the campaign structure, targeting, assets, measurement and change controls still need review. The monthly ad budget is confirmed on the call. We do not promise a particular cost per lead, return on ad spend, number of enquiries or sales outcome."
      },
      {
        "q": "What is the contract length, and does the retainer guarantee rankings or AI citations?",
        "a": "The Full AI Growth Engine has a three-month minimum and then runs monthly with 30 days’ notice. It is a delivery retainer, not a guarantee of rankings, AI citations, leads or sales. Search and answer platforms decide what they crawl, index, surface and cite. Our role is to make the technical foundation, buyer answers, evidence, review process and measurement more commercially useful and easier to validate."
      }
    ],
    "dateModified": "2026-10-28"
  }
});
